import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import OrderCalendar from "../components/OrderCalendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AreaChart, Area
} from 'recharts';
import { TrendingDown, Activity, AlertTriangle, PackageCheck, UserCheck, ShoppingCart } from 'lucide-react';
// At the top of your file with other imports (around line 1-40)
import {
  Users,
  Package,
  DollarSign,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Tag,
  Clock,
  Truck,
  Repeat,
  MapPin,
  CheckCircle,
  Lock,
  X,
  Ban,
  CheckCircle2,
  Search,
  Filter,
  ArrowUpDown,
  Calendar,
  Unlock,
  TrendingUp,
  FileText,
  Mail,
  Eye,
  EyeOff,
  Save,
  CreditCard, // Add this for credit notes
  ChevronDown, // For sorting dropdown
  Download,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { generateAndDisplayInvoice, downloadInvoicePDF as downloadPDF, generateInvoiceNumber, calculateGST } from "../utils/invoiceGenerator";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from 'leaflet';

import html2pdf from "html2pdf.js";

export async function htmlToPdfBytes(htmlString) {
  const container = document.createElement("div");

  container.innerHTML = htmlString;

  // 🔴 CRITICAL FIXES
  container.style.position = "relative";   // ❌ not fixed
  container.style.width = "794px";         // A4 width @ 96dpi
  container.style.background = "white";
  container.style.padding = "0px";

  document.body.appendChild(container);

  // 🔴 WAIT for DOM + styles
  await new Promise((resolve) => setTimeout(resolve, 500));

  const options = {
    margin: 0,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 1,
      useCORS: true,
      backgroundColor: "#ffffff"
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait"
    }
  };

  const pdfBlob = await html2pdf()
    .set(options)
    .from(container)
    .outputPdf("blob");

  document.body.removeChild(container);

  return pdfBlob;
}

function OwnerDashboard() {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Add this helper to your OwnerDashboard component
  const [showCreditNoteList, setShowCreditNoteList] = useState(false); // Toggle between Invoices and Credit Notes
  const [allCreditNotes, setAllCreditNotes] = useState([]);
  const [showCreditNoteFilters, setShowCreditNoteFilters] = useState(false);
  const [creditNoteFilters, setCreditNoteFilters] = useState({
    customer: [],
    status: [],
    dateFrom: "",
    dateTo: "",
    amountFrom: "",
    amountTo: ""
  });
  const [editingCreditNote, setEditingCreditNote] = useState(null); // For the Edit Dialog
  const [availableCredits, setAvailableCredits] = useState([]); // Credits for selected customer
  const [selectedCreditIds, setSelectedCreditIds] = useState([]); // Multi-select state
  const [showApplyCreditDialog, setShowApplyCreditDialog] = useState(false);
  const [availableCreditsPool, setAvailableCreditsPool] = useState([]);
  const [selectedPoolCreditIds, setSelectedPoolCreditIds] = useState([]);
  const [showApplyCreditsDialog, setShowApplyCreditsDialog] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [overviewDateFrom, setOverviewDateFrom] = useState("");
  const [overviewDateTo, setOverviewDateTo] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleOpenApplyCredits = async (invoice) => {
    try {
      const res = await axios.get(`${API}/credit-notes?customer_id=${invoice.customer_id}&status=available`);
      setAvailableCreditsPool(res.data);
      setSelectedInvoice(invoice);
      setSelectedPoolCreditIds([]); // Reset selection
      setShowApplyCreditsDialog(true);
    } catch (error) {
      toast.error("Failed to load available credits");
    }
  };
  const fetchAllCreditNotes = async () => {
    try {
      const params = new URLSearchParams();
      if (creditNoteFilters.customer.length > 0) {
        params.append("customer_id", creditNoteFilters.customer[0]);
      }
      if (creditNoteFilters.status.length > 0 && creditNoteFilters.status[0] !== "all") {
        params.append("status", creditNoteFilters.status[0]);
      }
      if (creditNoteFilters.dateFrom) params.append("start_date", creditNoteFilters.dateFrom);
      if (creditNoteFilters.dateTo) params.append("end_date", creditNoteFilters.dateTo);
      if (creditNoteFilters.amountFrom) params.append("min_amount", creditNoteFilters.amountFrom);
      if (creditNoteFilters.amountTo) params.append("max_amount", creditNoteFilters.amountTo);

      const res = await axios.get(`${API}/credit-notes?${params.toString()}`);
      setAllCreditNotes(res.data);
    } catch (error) {
      console.error("Failed to fetch all credit notes", error);
    }
  };
  const fetchAvailableCredits = async (customerId) => {
    try {
      const res = await axios.get(`${API}/credit-notes?customer_id=${customerId}&status=available`);
      setAvailableCredits(res.data);
    } catch (error) {
      toast.error("Failed to fetch available credits");
    }
  };
  // Fetch when the toggle is turned on
  useEffect(() => {
    if (showCreditNoteList) {
      fetchAllCreditNotes();
    }
  }, [showCreditNoteList, creditNoteFilters]);
  // Sync URL with tab state
  useEffect(() => {
    // Check if URL pathname includes /invoices
    if (location.pathname.includes("/invoices")) {
      setActiveTab("invoices");
    }
  }, [location.pathname]);

  const [liveDrivers, setliveDrivers] = useState([]);

  const fetchLiveLocations = async () => {
    try {
      const res = await axios.get(`${API}/users/location/live-tracking`);
      setliveDrivers(res.data);
    } catch (error) {
      console.error("Failed to fetch live locations", error);
    }
  };

  // Poll for live locations every 10 seconds when the tab is active
  useEffect(() => {
    let interval;
    if (activeTab === "live-tracking") {
      fetchLiveLocations();
      interval = setInterval(fetchLiveLocations, 10000);
    }
    return () => clearInterval(interval);
  }, [activeTab]);

  // Update URL when tab changes to invoices
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "invoices") {
      navigate("/dashboard/owner/invoices", { replace: true });
    } else if (location.pathname.includes("/invoices")) {
      navigate("/dashboard/owner", { replace: true });
    }
  };

  // User form
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "customer",
    phone: "",
    secondary_email: "",
    address: "",
  });
  const [showUserDialog, setShowUserDialog] = useState(false);

  // SKU form
  const [skuForm, setSkuForm] = useState({
    name: "",
    category: "",
    price: "",
    unit: "",
    description: "",
  });
  const [showSkuDialog, setShowSkuDialog] = useState(false);
  const [editingSku, setEditingSku] = useState(null);

  // Customer Pricing
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPricing, setCustomerPricing] = useState([]);
  const [skusWithPricing, setSkusWithPricing] = useState([]);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [pricingForm, setPricingForm] = useState({
    sku_id: "",
    custom_price: "",
  });

  // Frequency Templates
  const [frequencyTemplates, setFrequencyTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    frequency_type: "daily",
    frequency_value: "1",
    description: "",
  });

  // Case Management
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showCaseDialog, setShowCaseDialog] = useState(false);
  const [caseUpdate, setCaseUpdate] = useState({
    status: "",
    resolution: "",
    priority: "",
  });
  const [updatingCaseId, setUpdatingCaseId] = useState(null);

  // Cases Filter & Sort
  const [caseSearchQuery, setCaseSearchQuery] = useState("");
  const [caseSortBy, setCaseSortBy] = useState("created_at");
  const [caseSortOrder, setCaseSortOrder] = useState("desc");
  const [caseStatusFilter, setCaseStatusFilter] = useState([]);
  const [casePriorityFilter, setCasePriorityFilter] = useState([]);
  const [caseDateFrom, setCaseDateFrom] = useState("");
  const [caseDateTo, setCaseDateTo] = useState("");

  // Reject Linen
  const [showRejectLinenDialog, setShowRejectLinenDialog] = useState(false);
  const [rejectLinenForm, setRejectLinenForm] = useState({
    customer_id: "", customer_name: "", customer_email: "",
    reject_linen_qty: "", description: "", priority: "medium",
    order_id: "",
  });
  const [submittingRejectLinen, setSubmittingRejectLinen] = useState(false);
  const [rejectLinenPeriod, setRejectLinenPeriod] = useState("monthly");
  const [rejectLinenStats, setRejectLinenStats] = useState(null);
  const [rejectLinenCustom, setRejectLinenCustom] = useState({ start_date: "", end_date: "" });
  const [rejectLinenLoading, setRejectLinenLoading] = useState(false);
  const [rejectLinenCustomerId, setRejectLinenCustomerId] = useState("all");

  // Orders and Drivers
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState({});
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const PERMANENT_PICKUP_ADDRESS = "3/76 Mica Street, Carole Park, QLD, 4300";
  const [orderForm, setOrderForm] = useState({
    customer_id: "",
    customer_name: "",
    customer_email: "",
    items: [],
    delivery_date: "",
    pickup_address: PERMANENT_PICKUP_ADDRESS,
    delivery_address: "",
    special_instructions: "",
    is_recurring: false,
    frequency_template_id: "",
  });
  const [orderItems, setOrderItems] = useState([{ sku_id: "", quantity: 1 }]);
  const [selectedOrderForTracking, setSelectedOrderForTracking] =
    useState(null);
  const [customerSkus, setCustomerSkus] = useState([]);
  const [filteredSkus, setFilteredSkus] = useState([]);

  // Addresses
  const [businessPickupAddress, setBusinessPickupAddress] = useState("");

  // Orders Filter & Sort
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [orderSortBy, setOrderSortBy] = useState("created_at");
  const [orderSortOrder, setOrderSortOrder] = useState("desc");
  const [orderStatusFilter, setOrderStatusFilter] = useState([]);
  const [orderDeliveryStatusFilter, setOrderDeliveryStatusFilter] = useState(
    []
  );
  const [orderDateFilter, setOrderDateFilter] = useState("all");
  const [orderDateFrom, setOrderDateFrom] = useState("");
  const [orderDateTo, setOrderDateTo] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");

  // Users Filter & Sort
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSortBy, setUserSortBy] = useState("created_at");
  const [userSortOrder, setUserSortOrder] = useState("desc");
  const [userRoleFilter, setUserRoleFilter] = useState([]);

  // Pagination for users
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userItemsPerPage] = useState(20);

  // SKUs Filter & Sort
  const [skuSearchQuery, setSkuSearchQuery] = useState("");
  const [skuSortBy, setSkuSortBy] = useState("name");
  const [skuSortOrder, setSkuSortOrder] = useState("asc");
  const [skuCategoryFilter, setSkuCategoryFilter] = useState([]);

  // Delivery Tracking Filter
  const [deliveryTrackingStatusFilter, setDeliveryTrackingStatusFilter] =
    useState([]);
  const [deliveryTrackingDriverFilter, setDeliveryTrackingDriverFilter] =
    useState("");
  const [deliveryTrackingDateFrom, setDeliveryTrackingDateFrom] = useState("");
  const [deliveryTrackingDateTo, setDeliveryTrackingDateTo] = useState("");

  // Pagination for orders
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Password reset
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState(null);
  const [togglingUserId, setTogglingUserId] = useState(null);
  // User edit
  const [showUserEditDialog, setShowUserEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    role: "",
    secondary_email: "",
    billing_terms: "",
  });
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  // Order status update
  const [updatingStatusOrderId, setUpdatingStatusOrderId] = useState(null);

  // Edit Request Approval
  const [pendingEditRequests, setPendingEditRequests] = useState([]);
  const [showEditRequestDialog, setShowEditRequestDialog] = useState(false);
  const [selectedEditRequest, setSelectedEditRequest] = useState(null);
  const [editRequestAction, setEditRequestAction] = useState("approve");
  const [editRequestForm, setEditRequestForm] = useState({});
  const [rejectionReason, setRejectionReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [processingEditRequest, setProcessingEditRequest] = useState(false);

  // Invoicing
  // (around line 150-250)
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);  // KEEP THIS ONE
  const [showInvoiceEmailDialog, setShowInvoiceEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({
    subject: "",
    message: "",
    includePaymentInfo: true,
    emailType: "primary", // 'primary', 'operations', 'finance'
    html_body: ""
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [markingAsPaid, setMarkingAsPaid] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);  // Add this if missing  
  // Add these lines here:
  const [currentUser, setCurrentUser] = useState({ role: '', id: '' });

  // Add these state variables near other state declarations (around line 287-302)
  const [showClearedInvoices, setShowClearedInvoices] = useState(false);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [selectedDisputeInvoice, setSelectedDisputeInvoice] = useState(null);
  const [disputeReason, setDisputeReason] = useState("");
  const [showEditInvoiceDialog, setShowEditInvoiceDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceEditForm, setInvoiceEditForm] = useState({
    customer_name: "",
    customer_email: "",
    items: [],
    subtotal: 0,
    gst_amount: 0,
    total_amount: 0,
    notes: ""
  });
  const [selectedInvoices, setSelectedInvoices] = useState([]); // For bulk actions
  const [showBulkActionDialog, setShowBulkActionDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState("");

  // Add these state variables near other state declarations
  const [invoiceSortBy, setInvoiceSortBy] = useState("created_at");
  const [invoiceSortOrder, setInvoiceSortOrder] = useState("desc");
  const [invoiceFilters, setInvoiceFilters] = useState({
    status: [],
    customer: [],
    dateFrom: "",
    dateTo: "",
    amountFrom: "",
    amountTo: "",
    isCreditNote: false // For credit notes filter
  });
  const [showInvoiceFilters, setShowInvoiceFilters] = useState(false);
  const [showCreditNoteDialog, setShowCreditNoteDialog] = useState(false);
  const [selectedInvoiceForCredit, setSelectedInvoiceForCredit] = useState(null);
  const [creditNoteForm, setCreditNoteForm] = useState({
    customer_id: "",
    reason: "",
    amount: 0,
    notes: "",
    items: [],
    subtotal: 0,
    gst: 0,
    total_with_gst: 0
  });

  // Add these state variables near other state declarations
  const [showEmailHistoryDialog, setShowEmailHistoryDialog] = useState(false);
  const [selectedInvoiceForHistory, setSelectedInvoiceForHistory] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);
  const [autoSendOnApprove, setAutoSendOnApprove] = useState(true);
  const [showAutoSendSettings, setShowAutoSendSettings] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([
    {
      id: 'invoice_approved',
      name: 'Invoice Approved',
      subject: 'Invoice {{invoice_number}} Approved - Infinite Laundry Solutions',
      message: 'Dear {{customer_name}},\n\nYour invoice {{invoice_number}} has been approved and is now pending payment.\n\nTotal Amount: ${{total_amount}}\nDue Date: {{due_date}}\n\nPlease find the attached PDF for payment details.\n\nThank you for your business!',
      default: true
    },
    {
      id: 'payment_reminder',
      name: 'Payment Reminder',
      subject: 'Payment Reminder - Invoice {{invoice_number}}',
      message: 'Dear {{customer_name}},\n\nThis is a friendly reminder that invoice {{invoice_number}} is pending payment.\n\nTotal Amount: ${{total_amount}}\nDue Date: {{due_date}}\n\nPlease arrange payment at your earliest convenience.\n\nThank you for your business!',
      default: true
    },
    {
      id: 'invoice_disputed',
      name: 'Invoice Disputed',
      subject: 'Invoice {{invoice_number}} Dispute - Action Required',
      message: 'Dear {{customer_name}},\n\nYour invoice {{invoice_number}} has been marked as disputed.\n\nReason: {{dispute_reason}}\n\nOur team will review this and get back to you shortly.\n\nThank you for your patience.',
      default: true
    }
  ]);



  // Add this near other constants (around line 335)
  const INVOICE_STATUS = {
    PENDING_REVIEW: 'pending_review',
    APPROVED: 'approved',
    PENDING_PAYMENT: 'pending_payment',
    CLEARED: 'cleared',
    DISPUTE: 'dispute'
  };

  const DEFAULT_STATUS = INVOICE_STATUS.PENDING_REVIEW;


  // Add this useEffect to get current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setCurrentUser(res.data);
      } catch (error) {
        console.error('Failed to fetch current user', error);
      }
    };

    fetchCurrentUser();
  }, []);



  useEffect(() => {
    fetchData();
    fetchAddresses();
  }, []);

  useEffect(() => {
    if (activeTab === "users") {
      fetchData(); // Refresh users list
    } else if (activeTab === "customer-pricing") {
      fetchCustomers();
    } else if (activeTab === "frequency-templates") {
      fetchFrequencyTemplates();
    } else if (activeTab === "cases") {
      fetchCases();
      fetchOrders();     // needed for the Reject Linen dialog order picker
      fetchCustomers();  // needed for the Reject Linen dialog customer picker
    } else if (activeTab === "orders") {
      fetchOrders();
      fetchDrivers();
      fetchCustomers();
      fetchFrequencyTemplates();
    } else if (activeTab === "delivery-tracking") {
      fetchOrders();
    } else if (activeTab === "calendar") {
      fetchOrders();
    } else if (activeTab === "invoices") {
      fetchInvoices();
      fetchOrders();
      fetchCustomers()
    }
    else if (activeTab === "overview") {
      fetchData();
      fetchCustomers();  // needed for the Reject Linen rate customer filter
    }
  }, [activeTab]);

  // Fetch customer-specific SKU pricing when customer is selected in order form
  useEffect(() => {
    if (orderForm.customer_id) {
      fetchCustomerSkus(orderForm.customer_id);
    } else {
      setCustomerSkus([]);
    }
  }, [orderForm.customer_id]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerPricing(selectedCustomer);
    }
  }, [selectedCustomer]);

  // Helper function to get display name for order status
  const getStatusDisplayName = (status) => {
    const statusMap = {
      pending: "Pending",
      scheduled: "Scheduled",
      processing: "Processing",
      ready_for_pickup: "Order Ready for Pickup",
      out_for_delivery: "Out for Delivery",
      delivered: "Delivered",
      cancelled: "Cancelled",
    };
    return statusMap[status] || status;
  };

  const fetchData = async () => {
    setIsRefreshing(true);
    try {
      // Build Query String
      let queryParams = "";
      if (overviewDateFrom) queryParams += `?start_date=${overviewDateFrom}`;
      if (overviewDateTo) {
        queryParams += queryParams ? `&end_date=${overviewDateTo}` : `?end_date=${overviewDateTo}`;
      }

      const [statsRes, usersRes, skusRes, editRequestsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard${queryParams}`), // Pass params here
        axios.get(`${API}/users`),
        axios.get(`${API}/skus`),
        axios.get(`${API}/orders/pending-edit-requests`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSkus(skusRes.data);
      setPendingEditRequests(editRequestsRes.data);
      fetchRejectLinen();
    } catch (error) {
      console.error("Failed to fetch data", error);
      toast.error("Failed to update dashboard");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchRejectLinen = async (params = {}) => {
    try {
      setRejectLinenLoading(true);
      // Always include the current customer filter unless the caller overrides it.
      const merged = { customer_id: rejectLinenCustomerId, ...params };
      const res = await axios.get(`${API}/analytics/reject-linen`, { params: merged });
      setRejectLinenStats(res.data);
    } catch (error) {
      console.error("Failed to fetch reject linen analytics", error);
      setRejectLinenStats(null);
    } finally {
      setRejectLinenLoading(false);
    }
  };

  const selectRejectLinenPeriod = (p) => {
    setRejectLinenPeriod(p);
    fetchRejectLinen({ period: p });
  };

  const changeRejectLinenCustomer = (custId) => {
    setRejectLinenCustomerId(custId);
    // Re-run the currently selected view for the newly chosen customer.
    if (rejectLinenPeriod === "custom" && rejectLinenCustom.start_date && rejectLinenCustom.end_date) {
      fetchRejectLinen({
        customer_id: custId,
        period: "custom",
        start_date: rejectLinenCustom.start_date,
        end_date: rejectLinenCustom.end_date,
      });
    } else {
      fetchRejectLinen({ customer_id: custId, period: rejectLinenPeriod });
    }
  };

  const applyRejectLinenCustom = () => {
    if (!rejectLinenCustom.start_date || !rejectLinenCustom.end_date) {
      toast.error("Please select both a start and end date");
      return;
    }
    if (rejectLinenCustom.start_date > rejectLinenCustom.end_date) {
      toast.error("Start date must be before end date");
      return;
    }
    setRejectLinenPeriod("custom");
    fetchRejectLinen({
      period: "custom",
      start_date: rejectLinenCustom.start_date,
      end_date: rejectLinenCustom.end_date,
    });
  };

  const applyOverviewFilters = () => {
    fetchData();
  };
  const clearOverviewFilters = () => {
    setOverviewDateFrom("");
    setOverviewDateTo("");
    // We need to trigger fetch after state updates, use timeout or useEffect dependency
    // For simplicity, we can pass nulls directly or rely on the next render if using useEffect on [overviewDateFrom]
    // Here we will just reload page default
    window.location.reload(); // Or separate fetch logic
  };
  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setCustomers(res.data.filter((u) => u.role === "customer"));
    } catch (error) {
      console.error("Failed to fetch customers", error);
    }
  };

  const fetchCustomerPricing = async (customerId) => {
    try {
      const [skusRes, pricingRes] = await Promise.all([
        axios.get(`${API}/skus-with-pricing/${customerId}`),
        axios.get(`${API}/customer-pricing/${customerId}`),
      ]);
      setSkusWithPricing(skusRes.data);
      setCustomerPricing(pricingRes.data);
    } catch (error) {
      console.error("Failed to fetch customer pricing", error);
    }
  };

  const fetchFrequencyTemplates = async () => {
    try {
      const res = await axios.get(`${API}/frequency-templates`);
      setFrequencyTemplates(res.data);
    } catch (error) {
      console.error("Failed to fetch frequency templates", error);
    }
  };

  const fetchCases = async () => {
    try {
      const res = await axios.get(`${API}/cases`);
      setCases(res.data);
    } catch (error) {
      console.error("Failed to fetch cases", error);
      toast.error("Failed to fetch cases");
    }
  };

  const fetchCustomerSkus = async (customerId) => {
    try {
      const res = await axios.get(`${API}/skus-with-pricing/${customerId}`);
      setCustomerSkus(res.data);
    } catch (error) {
      console.error("Failed to fetch customer SKUs", error);
      setCustomerSkus([]);
    }
  };

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${API}/config/addresses`);
      setBusinessPickupAddress(res.data.business_pickup_address);
    } catch (error) {
      console.error("Failed to fetch addresses", error);
    }
  };

  // 457-493
  const fetchInvoices = async () => {
    try {
      console.log('Fetching invoices from:', `${API}/invoices`);
      const token = localStorage.getItem('token');

      const res = await axios.get(`${API}/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Invoices response:', res.data);
      console.log('DEBUG - API URL:', `${API}/invoices`);
      console.log('DEBUG - Full backend URL:', API);


      // Ensure all invoices have proper status
      const processedInvoices = res.data.map(invoice => ({
        ...invoice,
        // Set default status if missing
        status: invoice.status || DEFAULT_STATUS,
        // Ensure invoice_number exists
        invoice_number: invoice.invoice_number || `INV-${invoice.order_number}`
      })

      );


      setInvoices(processedInvoices);
    } catch (error) {
      console.error('Failed to fetch invoices', error);
      console.error('Error response:', error.response);

      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || "Failed to fetch invoices");
      }
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, userForm);
      setShowUserDialog(false);
      setUserForm({
        email: "",
        password: "",
        full_name: "",
        role: "customer",
        phone: "",
        address: "",
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleToggleUserStatus = async (user) => {
    const action = user.is_active ? "disable" : "enable";
    if (
      !window.confirm(
        `Are you sure you want to ${action} ${user.full_name}'s account?`
      )
    )
      return;

    setTogglingUserId(user.id);
    try {
      await axios.put(`${API}/admin/users/${user.id}/toggle-status`);
      toast.success(`User account ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    } finally {
      setTogglingUserId(null);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (
      !window.confirm(
        `Are you sure you want to reset the password for ${selectedUser?.full_name}?`
      )
    ) {
      return;
    }

    setResettingPasswordUserId(selectedUser.id);
    try {
      await axios.put(`${API}/admin/reset-password/${selectedUser.id}`, {
        new_password: newPassword,
      });
      toast.success("Password reset successfully");
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword("");
    } catch (error) {
      console.error("Failed to reset password:", error);
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPasswordUserId(null);
    }
  };

  const openPasswordResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowPasswordDialog(true);
  };

  const openUserEditDialog = (u) => {
    setEditingUser(u);
    setUserEditForm({
      full_name: u.full_name || "",
      email: u.email || "",
      phone: u.phone || "",
      address: u.address || "",
      role: u.role || "customer",
      secondary_email: u.secondary_email || "",
      billing_terms: u.billing_terms || "",
    });
    setShowUserEditDialog(true);
  };

  const handleSaveUserEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      setSavingUserEdit(true);
      const payload = {};
      if (userEditForm.full_name !== editingUser.full_name)
        payload.full_name = userEditForm.full_name;
      if (userEditForm.email !== editingUser.email)
        payload.email = userEditForm.email;
      if ((userEditForm.phone || "") !== (editingUser.phone || ""))
        payload.phone = userEditForm.phone;
      if ((userEditForm.address || "") !== (editingUser.address || ""))
        payload.address = userEditForm.address;
      if (userEditForm.role !== editingUser.role)
        payload.role = userEditForm.role;
      if ((userEditForm.secondary_email || "") !== (editingUser.secondary_email || ""))
        payload.secondary_email = userEditForm.secondary_email;
      if (userEditForm.billing_terms !== editingUser.billing_terms)
        payload.billing_terms = userEditForm.billing_terms;

      await axios.put(`${API}/users/${editingUser.id}`, payload);
      toast.success("User updated successfully");
      setShowUserEditDialog(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update user", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSavingUserEdit(false);
    }
  };

  const handleUpdateCase = async (e) => {
    e.preventDefault();
    if (updatingCaseId) return;

    try {
      setUpdatingCaseId(selectedCase.id);
      await axios.put(`${API}/cases/${selectedCase.id}`, caseUpdate);
      toast.success("Case updated successfully");
      setShowCaseDialog(false);
      setSelectedCase(null);
      setCaseUpdate({ status: "", resolution: "", priority: "" });
      fetchCases();
    } catch (error) {
      console.error("Failed to update case", error);
      toast.error("Failed to update case");
    } finally {
      setUpdatingCaseId(null);
    }
  };

  const handleSubmitRejectLinenCase = async (e) => {
    e.preventDefault();
    if (!rejectLinenForm.customer_id || !rejectLinenForm.reject_linen_qty) {
      toast.error("Please select a customer and enter rejected quantity");
      return;
    }
    try {
      setSubmittingRejectLinen(true);
      await axios.post(`${API}/cases`, {
        customer_id: rejectLinenForm.customer_id,
        customer_name: rejectLinenForm.customer_name,
        customer_email: rejectLinenForm.customer_email,
        type: "quality",
        subject: "Reject Linen",
        description: rejectLinenForm.description || `Reject linen case: ${rejectLinenForm.reject_linen_qty} items rejected.`,
        priority: rejectLinenForm.priority,
        order_id: rejectLinenForm.order_id || null,
        reject_linen_qty: parseInt(rejectLinenForm.reject_linen_qty, 10),
      });
      toast.success("Reject linen case raised successfully");
      setShowRejectLinenDialog(false);
      setRejectLinenForm({ customer_id: "", customer_name: "", customer_email: "", reject_linen_qty: "", description: "", priority: "medium", order_id: "" });
      fetchCases();
      fetchData(); // refresh dashboard stats
    } catch (error) {
      console.error("Failed to create reject linen case", error);
      toast.error(error.response?.data?.detail || "Failed to raise reject linen case");
    } finally {
      setSubmittingRejectLinen(false);
    }
  };

  const handleCreateOrUpdateSku = async (e) => {
    e.preventDefault();
    try {
      const skuData = { ...skuForm, price: parseFloat(skuForm.price) };
      if (editingSku) {
        await axios.put(`${API}/skus/${editingSku.id}`, skuData);
      } else {
        await axios.post(`${API}/skus`, skuData);
      }
      setShowSkuDialog(false);
      setSkuForm({
        name: "",
        category: "",
        price: "",
        unit: "",
        description: "",
      });
      setEditingSku(null);
      fetchData();
    } catch (error) {
      alert("Failed to save SKU");
    }
  };

  const handleDeleteSku = async (skuId) => {
    if (!window.confirm("Are you sure you want to delete this SKU?")) return;
    try {
      await axios.delete(`${API}/skus/${skuId}`);
      fetchData();
    } catch (error) {
      alert("Failed to delete SKU");
    }
  };

  const handleEditSku = (sku) => {
    setEditingSku(sku);
    setSkuForm({
      name: sku.name,
      category: sku.category,
      price: sku.price.toString(),
      unit: sku.unit,
      description: sku.description || "",
    });
    setShowSkuDialog(true);
  };

  const handleCreateOrUpdatePricing = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) {
      alert("Please select a customer first");
      return;
    }
    try {
      await axios.post(`${API}/customer-pricing`, {
        customer_id: selectedCustomer,
        sku_id: pricingForm.sku_id,
        custom_price: parseFloat(pricingForm.custom_price),
      });
      setShowPricingDialog(false);
      setPricingForm({ sku_id: "", custom_price: "" });
      fetchCustomerPricing(selectedCustomer);
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to save pricing");
    }
  };

  const handleDeletePricing = async (pricingId) => {
    if (!window.confirm("Are you sure you want to delete this custom pricing?"))
      return;
    try {
      await axios.delete(`${API}/customer-pricing/${pricingId}`);
      fetchCustomerPricing(selectedCustomer);
    } catch (error) {
      alert("Failed to delete pricing");
    }
  };

  const handleCreateOrUpdateTemplate = async (e) => {
    e.preventDefault();
    try {
      const templateData = {
        ...templateForm,
        frequency_value: parseInt(templateForm.frequency_value),
      };
      if (editingTemplate) {
        await axios.put(
          `${API}/frequency-templates/${editingTemplate.id}`,
          templateData
        );
      } else {
        await axios.post(`${API}/frequency-templates`, templateData);
      }
      setShowTemplateDialog(false);
      setTemplateForm({
        name: "",
        frequency_type: "daily",
        frequency_value: "1",
        description: "",
      });
      setEditingTemplate(null);
      fetchFrequencyTemplates();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to save template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?"))
      return;
    try {
      await axios.delete(`${API}/frequency-templates/${templateId}`);
      fetchFrequencyTemplates();
    } catch (error) {
      alert("Failed to delete template");
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      frequency_type: template.frequency_type,
      frequency_value: template.frequency_value.toString(),
      description: template.description || "",
    });
    setShowTemplateDialog(true);
  };

  // Edit Request Handlers
  const handleViewEditRequest = (order) => {
    setSelectedEditRequest(order);
    setEditRequestAction("approve");
    setEditRequestForm({
      delivery_date:
        order.pending_modifications?.delivery_date || order.delivery_date,
      delivery_address:
        order.pending_modifications?.delivery_address || order.delivery_address,
      special_instructions:
        order.pending_modifications?.special_instructions ||
        order.special_instructions,
      items: order.pending_modifications?.items || order.items,
    });
    setRejectionReason("");
    setAdminNotes("");
    setShowEditRequestDialog(true);
  };

  const handleProcessEditRequest = async () => {
    if (editRequestAction === "reject" && !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    setProcessingEditRequest(true);
    try {
      if (editRequestAction === "approve") {
        await axios.put(
          `${API}/orders/${selectedEditRequest.id}/approve-modification`
        );
        toast.success("Edit request approved successfully");
      } else if (editRequestAction === "reject") {
        await axios.put(
          `${API}/orders/${selectedEditRequest.id}/reject-modification`,
          { rejection_reason: rejectionReason }
        );
        toast.success("Edit request rejected");
      }

      setShowEditRequestDialog(false);
      setSelectedEditRequest(null);
      fetchData();
      if (activeTab === "orders") {
        fetchOrders();
      }
    } catch (error) {
      console.error("Failed to process edit request", error);
      toast.error(
        error.response?.data?.detail || "Failed to process edit request"
      );
    } finally {
      setProcessingEditRequest(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders`);
      setOrders(res.data);
    } catch (error) {
      console.error("Failed to fetch orders", error);
    }
  };

  const getFilteredAndSortedOrders = () => {
    let filtered = [...orders];

    // Status filter logic
    if (orderStatusFilter.length > 0) {
      filtered = filtered.filter((o) => orderStatusFilter.includes(o.status));
    } else {
      filtered = filtered.filter(
        (o) => o.status !== "delivered" && o.status !== "cancelled"
      );
    }

    // Search filter
    if (orderSearchQuery.trim()) {
      const query = orderSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.order_number?.toLowerCase().includes(query) ||
          order.customer_name?.toLowerCase().includes(query) ||
          order.customer_email?.toLowerCase().includes(query)
      );
    }

    // Delivery Status filter
    if (orderDeliveryStatusFilter.length > 0) {
      filtered = filtered.filter(
        (order) =>
          order.delivery_status &&
          orderDeliveryStatusFilter.includes(order.delivery_status)
      );
    }

    // Type filter
    if (orderTypeFilter !== "all") {
      filtered = filtered.filter((order) =>
        orderTypeFilter === "recurring"
          ? order.is_recurring
          : !order.is_recurring
      );
    }

    // Date filter
    if (orderDateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter((order) => {
        const deliveryDate = new Date(order.delivery_date);

        switch (orderDateFilter) {
          case "today":
            return deliveryDate.toDateString() === today.toDateString();
          case "week":
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return deliveryDate >= weekAgo;
          case "month":
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return deliveryDate >= monthAgo;
          case "custom":
            if (orderDateFrom && orderDateTo) {
              const fromDate = new Date(orderDateFrom);
              const toDate = new Date(orderDateTo);
              toDate.setHours(23, 59, 59, 999);
              return deliveryDate >= fromDate && deliveryDate <= toDate;
            } else if (orderDateFrom) {
              const fromDate = new Date(orderDateFrom);
              return deliveryDate >= fromDate;
            } else if (orderDateTo) {
              const toDate = new Date(orderDateTo);
              toDate.setHours(23, 59, 59, 999);
              return deliveryDate <= toDate;
            }
            return true;
          default:
            return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (orderSortBy) {
        case "order_number":
          aVal = a.order_number || "";
          bVal = b.order_number || "";
          break;
        case "customer_name":
          aVal = a.customer_name || "";
          bVal = b.customer_name || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "total_amount":
          aVal = a.total_amount || 0;
          bVal = b.total_amount || 0;
          break;
        case "delivery_date":
          aVal = new Date(a.delivery_date || 0);
          bVal = new Date(b.delivery_date || 0);
          break;
        case "created_at":
        default:
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          break;
      }

      if (
        orderSortBy === "total_amount" ||
        orderSortBy === "delivery_date" ||
        orderSortBy === "created_at"
      ) {
        return orderSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else {
        const aStr = (aVal ?? "").toString().toLowerCase().trim();
        const bStr = (bVal ?? "").toString().toLowerCase().trim();
        const comparison = aStr.localeCompare(bStr, undefined, {
          sensitivity: "base",
        });
        return orderSortOrder === "asc" ? comparison : -comparison;
      }
    });

    return filtered;
  };

  const toggleStatusFilter = (status) => {
    setOrderStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
    setCurrentPage(1);
  };

  const toggleDeliveryStatusFilter = (status) => {
    setOrderDeliveryStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
    setCurrentPage(1);
  };

  const clearAllOrderFilters = () => {
    setOrderSearchQuery("");
    setOrderStatusFilter([]);
    setOrderDeliveryStatusFilter([]);
    setOrderDateFilter("all");
    setOrderDateFrom("");
    setOrderDateTo("");
    setOrderTypeFilter("all");
    setOrderSortBy("created_at");
    setOrderSortOrder("desc");
    setCurrentPage(1);
  };

  // Pagination helpers
  const getPaginatedOrders = () => {
    const filtered = getFilteredAndSortedOrders();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filtered = getFilteredAndSortedOrders();
    return Math.ceil(filtered.length / itemsPerPage);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // User Pagination helpers
  const getPaginatedUsers = () => {
    const filtered = getFilteredAndSortedUsers();
    const startIndex = (userCurrentPage - 1) * userItemsPerPage;
    const endIndex = startIndex + userItemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getUserTotalPages = () => {
    const filtered = getFilteredAndSortedUsers();
    return Math.ceil(filtered.length / userItemsPerPage);
  };

  const handleUserPageChange = (newPage) => {
    setUserCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Users Filter & Sort Functions
  const getFilteredAndSortedUsers = () => {
    let filtered = [...users];

    // Search filter
    if (userSearchQuery.trim()) {
      const query = userSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.full_name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query)
      );
    }

    // Role filter
    if (userRoleFilter.length > 0) {
      filtered = filtered.filter((user) => userRoleFilter.includes(user.role));
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (userSortBy) {
        case "full_name":
          aVal = a.full_name || "";
          bVal = b.full_name || "";
          break;
        case "email":
          aVal = a.email || "";
          bVal = b.email || "";
          break;
        case "role":
          aVal = a.role || "";
          bVal = b.role || "";
          break;
        case "created_at":
        default:
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          break;
      }

      if (userSortBy === "created_at") {
        return userSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else {
        const comparison = aVal.toString().localeCompare(bVal.toString());
        return userSortOrder === "asc" ? comparison : -comparison;
      }
    });

    return filtered;
  };

  const toggleUserRoleFilter = (role) => {
    setUserRoleFilter((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
    setUserCurrentPage(1);
  };

  const clearUserFilters = () => {
    setUserSearchQuery("");
    setUserRoleFilter([]);
    setUserSortBy("created_at");
    setUserSortOrder("desc");
    setUserCurrentPage(1);
  };

  // SKUs Filter & Sort Functions
  const getFilteredAndSortedSkus = () => {
    let filtered = [...skus];

    // Search filter
    if (skuSearchQuery.trim()) {
      const query = skuSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (sku) =>
          sku.name?.toLowerCase().includes(query) ||
          sku.description?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (skuCategoryFilter.length > 0) {
      filtered = filtered.filter((sku) =>
        skuCategoryFilter.includes(sku.category)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (skuSortBy) {
        case "name":
          aVal = a.name || "";
          bVal = b.name || "";
          break;
        case "category":
          aVal = a.category || "";
          bVal = b.category || "";
          break;
        case "price":
          aVal = a.price || 0;
          bVal = b.price || 0;
          break;
        default:
          aVal = a.name || "";
          bVal = b.name || "";
          break;
      }

      if (skuSortBy === "price") {
        return skuSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else {
        const comparison = aVal.toString().localeCompare(bVal.toString());
        return skuSortOrder === "asc" ? comparison : -comparison;
      }
    });

    return filtered;
  };

  const toggleSkuCategoryFilter = (category) => {
    setSkuCategoryFilter((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleApproveInvoiceWithEmail = async (invoiceId) => {
    try {
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        toast.error("Invoice not found");
        return;
      }

      const template = emailTemplates.find(t => t.id === 'invoice_approved');
      const GST_RATE = 0.10;
      let credited_amount = invoice.credited_amount || 0;
      if (credited_amount) {
        // caluclate gst to the credited amount
        const gst = credited_amount * GST_RATE;
        credited_amount = credited_amount / (1 + GST_RATE);
      }
      const totalSubtotal = invoice.subtotal || 0;
      const totalGST = ((totalSubtotal - (credited_amount || 0)) * GST_RATE);
      const totalAUD = ((totalSubtotal + totalGST) - (credited_amount || 0)).toFixed(2);
      // Create template data with fallbacks
      const templateData = {
        invoice_number: invoice.invoice_number || `INV-${invoice.order_number}`,
        customer_name: invoice.customer_name || '',
        total_amount: totalAUD,
        due_date: invoice.due_date
          ? new Date(invoice.due_date).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
          : 'ASAP'
      };

      // Replace placeholders in template
      let message = template?.message || '';
      let subject = template?.subject || '';

      Object.keys(templateData).forEach(key => {
        const placeholder = `{{${key}}}`;
        const value = templateData[key];
        message = message.replace(new RegExp(placeholder, 'g'), value);
        subject = subject.replace(new RegExp(placeholder, 'g'), value);
      });

      // Show email dialog
      setSelectedInvoice(invoice);
      setEmailForm({
        emailType: 'finance',
        subject: subject,
        message: message,
        includePaymentInfo: true,
        isApprovalEmail: true,
        invoiceId: invoiceId,
        html_body: template?.html_body || ''
      });
      setShowInvoiceEmailDialog(true);
    } catch (error) {
      console.error("Failed to prepare approval email:", error);
      toast.error("Failed to prepare approval email");
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      const currentInvoice = invoices.find(inv => inv.id === invoiceId);

      if (!currentInvoice) {
        toast.error("Invoice not found");
        return;
      }

      // If approving and auto-send is enabled, use the approval flow
      if (newStatus === INVOICE_STATUS.APPROVED && autoSendOnApprove) {
        await handleApproveInvoiceWithEmail(invoiceId);
        return;
      }

      // For other status changes
      const updateData = { status: newStatus };

      switch (newStatus) {
        case INVOICE_STATUS.APPROVED:
          updateData.approved_at = new Date().toISOString();
          updateData.approved_by = user?.id || 'admin';
          toast.success("Invoice approved");
          break;

        case INVOICE_STATUS.PENDING_PAYMENT:
          toast.info("Invoice marked as pending payment");
          break;

        case INVOICE_STATUS.CLEARED:
          updateData.is_paid = true;
          updateData.paid_date = new Date().toISOString();
          updateData.paid_amount = currentInvoice.total_amount;
          toast.success("Invoice marked as cleared and paid");
          break;

        case INVOICE_STATUS.DISPUTE:
          setSelectedDisputeInvoice(currentInvoice);
          setDisputeReason("");
          setShowDisputeDialog(true);
          return;

        default:
          break;
      }

      updateData.updated_at = new Date().toISOString();
      await axios.put(`${API}/invoices/${invoiceId}`, updateData);
      fetchInvoices();

    } catch (error) {
      console.error("Failed to update invoice status", error);
      toast.error(error.response?.data?.detail || "Failed to update invoice status");
    }
  };
  // Add dispute submission handler
  const handleSubmitDispute = async () => {
    if (!selectedDisputeInvoice || !disputeReason.trim()) {
      toast.error("Please enter a dispute reason");
      return;
    }

    try {
      await axios.put(`${API}/invoices/${selectedDisputeInvoice.id}`, {
        status: INVOICE_STATUS.DISPUTE,
        dispute_reason: disputeReason,
        disputed_at: new Date().toISOString(),
        disputed_by: currentUser.id
      });

      toast.success("Invoice marked as disputed");
      setShowDisputeDialog(false);
      setSelectedDisputeInvoice(null);
      setDisputeReason("");
      fetchInvoices();

    } catch (error) {
      console.error("Failed to mark invoice as disputed", error);
      toast.error(error.response?.data?.detail || "Failed to mark as disputed");
    }
  };

  // Get Status Badge Function
  const getStatusBadge = (status) => {
    const statusConfig = {
      [INVOICE_STATUS.PENDING_REVIEW]: {
        color: 'bg-yellow-100 text-yellow-800',
        label: '⏳ Pending Review'
      },
      [INVOICE_STATUS.APPROVED]: {
        color: 'bg-blue-100 text-blue-800',
        label: '✅ Approved'
      },
      [INVOICE_STATUS.PENDING_PAYMENT]: {
        color: 'bg-orange-100 text-orange-800',
        label: '💰 Pending Payment'
      },
      [INVOICE_STATUS.CLEARED]: {
        color: 'bg-green-100 text-green-800',
        label: '✔️ Cleared (Paid)'
      },
      [INVOICE_STATUS.DISPUTE]: {
        color: 'bg-red-100 text-red-800',
        label: '⚠️ Dispute'
      },
      'fully_credited': { color: 'bg-purple-100 text-purple-800', label: '💎 Fully Credited' }
    };

    const config = statusConfig[status] || {
      color: 'bg-gray-100 text-gray-800',
      label: status || 'Unknown'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getDeliveryStatusBadge = (status) => {
    const config = {
      pending:          { color: 'bg-gray-100 text-gray-700',   label: 'Pending' },
      assigned:         { color: 'bg-blue-100 text-blue-800',   label: '🚗 Driver Assigned' },
      picked_up:        { color: 'bg-purple-100 text-purple-800', label: '📦 Picked Up' },
      out_for_delivery: { color: 'bg-orange-100 text-orange-800', label: '🚚 Out for Delivery' },
      delivered:        { color: 'bg-green-100 text-green-800', label: '✅ Delivered' },
    }[status];
    if (!config) return null;
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const clearSkuFilters = () => {
    setSkuSearchQuery("");
    setSkuCategoryFilter([]);
    setSkuSortBy("name");
    setSkuSortOrder("asc");
  };

  // Delivery Tracking Filter Functions
  const getFilteredDeliveryOrders = () => {
    let filtered = orders.filter((o) => o.driver_id);

    // Delivery Status filter
    if (deliveryTrackingStatusFilter.length > 0) {
      filtered = filtered.filter((order) => {
        const status = order.delivery_status || "assigned";
        return deliveryTrackingStatusFilter.includes(status);
      });
    }

    // Driver filter
    if (deliveryTrackingDriverFilter.trim()) {
      const query = deliveryTrackingDriverFilter.toLowerCase();
      filtered = filtered.filter((order) =>
        order.driver_name?.toLowerCase().includes(query)
      );
    }

    // Date range filter
    if (deliveryTrackingDateFrom || deliveryTrackingDateTo) {
      filtered = filtered.filter((order) => {
        const deliveryDate = new Date(order.delivery_date);

        if (deliveryTrackingDateFrom && deliveryTrackingDateTo) {
          const fromDate = new Date(deliveryTrackingDateFrom);
          const toDate = new Date(deliveryTrackingDateTo);
          toDate.setHours(23, 59, 59, 999);
          return deliveryDate >= fromDate && deliveryDate <= toDate;
        } else if (deliveryTrackingDateFrom) {
          const fromDate = new Date(deliveryTrackingDateFrom);
          return deliveryDate >= fromDate;
        } else if (deliveryTrackingDateTo) {
          const toDate = new Date(deliveryTrackingDateTo);
          toDate.setHours(23, 59, 59, 999);
          return deliveryDate <= toDate;
        }
        return true;
      });
    }

    return filtered;
  };

  const toggleDeliveryTrackingStatusFilter = (status) => {
    setDeliveryTrackingStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const clearDeliveryTrackingFilters = () => {
    setDeliveryTrackingStatusFilter([]);
    setDeliveryTrackingDriverFilter("");
    setDeliveryTrackingDateFrom("");
    setDeliveryTrackingDateTo("");
  };

  // Case Filter & Sort Functions
  const getFilteredAndSortedCases = () => {
    let filtered = [...cases];

    // Search filter
    if (caseSearchQuery.trim()) {
      const query = caseSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.customer_name?.toLowerCase().includes(query) ||
          c.customer_email?.toLowerCase().includes(query) ||
          c.subject?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (caseStatusFilter.length > 0) {
      filtered = filtered.filter((c) => caseStatusFilter.includes(c.status));
    }

    // Priority filter
    if (casePriorityFilter.length > 0) {
      filtered = filtered.filter((c) =>
        casePriorityFilter.includes(c.priority)
      );
    }

    // Date range filter
    if (caseDateFrom || caseDateTo) {
      filtered = filtered.filter((c) => {
        const caseDate = new Date(c.created_at);
        const fromDate = caseDateFrom ? new Date(caseDateFrom) : null;
        const toDate = caseDateTo
          ? new Date(caseDateTo + "T23:59:59.999")
          : null;

        if (fromDate && caseDate < fromDate) return false;
        if (toDate && caseDate > toDate) return false;
        return true;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (caseSortBy) {
        case "case_number":
          aVal = a.case_number || "";
          bVal = b.case_number || "";
          break;
        case "customer_name":
          aVal = a.customer_name || "";
          bVal = b.customer_name || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "priority":
          const priorityOrder = { low: 1, medium: 2, high: 3, urgent: 4 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          break;
        case "created_at":
        default:
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
      }

      if (aVal < bVal) return caseSortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return caseSortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const toggleCaseStatusFilter = (status) => {
    setCaseStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const toggleCasePriorityFilter = (priority) => {
    setCasePriorityFilter((prev) =>
      prev.includes(priority)
        ? prev.filter((p) => p !== priority)
        : [...prev, priority]
    );
  };

  const clearCaseFilters = () => {
    setCaseSearchQuery("");
    setCaseSortBy("created_at");
    setCaseSortOrder("desc");
    setCaseStatusFilter([]);
    setCasePriorityFilter([]);
    setCaseDateFrom("");
    setCaseDateTo("");
  };

  // Add this function near your other filter functions  1365
  const getFilteredInvoices = () => {
    let filtered = [...invoices];

    // Filter out cleared invoices unless explicitly showing them
    if (!showClearedInvoices) {
      filtered = filtered.filter(invoice => invoice.status !== INVOICE_STATUS.CLEARED);
    }

    return filtered;
  };

  const getUniqueCategories = () => {
    const categories = [
      ...new Set(skus.map((sku) => sku.category).filter(Boolean)),
    ];
    return categories.sort();
  };

  const fetchDrivers = async () => {
    try {
      const res = await axios.get(`${API}/drivers`);
      setDrivers(res.data);
    } catch (error) {
      console.error("Failed to fetch drivers", error);
    }
  };

  const handleAssignDriver = async (orderId) => {
    const driverId = selectedDriver[orderId];
    if (!driverId) {
      alert("Please select a driver");
      return;
    }
    try {
      await axios.put(`${API}/orders/${orderId}/assign-driver`, {
        driver_id: driverId,
      });
      alert("Driver assigned successfully");
      fetchOrders();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to assign driver");
    }
  };

  const handleUnassignDriver = async (orderId) => {
    if (
      !window.confirm(
        "Are you sure you want to unassign the driver from this order?"
      )
    ) {
      return;
    }
    try {
      await axios.put(`${API}/orders/${orderId}/unassign-driver`);
      toast.success("Driver unassigned successfully");
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign driver");
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    setCreatingOrder(true);
    try {
      const selectedCustomer = customers.find(
        (c) => c.id === orderForm.customer_id
      );
      if (!selectedCustomer) {
        alert("Please select a customer");
        setCreatingOrder(false);
        return;
      }

      const skuList = customerSkus.length > 0 ? customerSkus : skus;

      const validItems = orderItems
        .filter((item) => item.sku_id && item.quantity > 0)
        .map((item) => {
          const sku = skuList.find((s) => s.id === item.sku_id);
          if (!sku) return null;

          const price =
            sku.customer_price !== undefined ? sku.customer_price : sku.price;

          return {
            sku_id: item.sku_id,
            sku_name: sku.name,
            quantity: parseInt(item.quantity),
            price: parseFloat(price),
          };
        })
        .filter((item) => item !== null);

      if (validItems.length === 0) {
        alert("Please add at least one valid item");
        setCreatingOrder(false);
        return;
      }

      const orderData = {
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.full_name,
        customer_email: selectedCustomer.email,
        items: validItems,
        delivery_date: orderForm.delivery_date,
        pickup_address: orderForm.pickup_address,
        delivery_address: orderForm.delivery_address,
        special_instructions: orderForm.special_instructions || null,
        is_recurring: orderForm.is_recurring || false,
        delivery_rate: parseFloat(orderForm.delivery_rate || 0),
      };

      if (orderData.is_recurring && orderForm.frequency_template_id) {
        const template = frequencyTemplates.find(
          (t) => t.id === orderForm.frequency_template_id
        );
        if (template) {
          orderData.recurrence_pattern = {
            frequency_type: template.frequency_type,
            frequency_value: template.frequency_value,
          };
        }
      }

      await axios.post(`${API}/orders`, orderData);
      alert("Order created successfully");
      setShowOrderDialog(false);
      setOrderForm({
        customer_id: "",
        customer_name: "",
        customer_email: "",
        items: [],
        delivery_date: "",
        pickup_address: PERMANENT_PICKUP_ADDRESS,
        delivery_address: "",
        special_instructions: "",
        is_recurring: false,
        frequency_template_id: "",
      });
      setOrderItems([{ sku_id: "", quantity: 1 }]);
      setCustomerSkus([]);
      fetchOrders();
    } catch (error) {
      console.error("Order creation error:", error.response?.data);
      alert(
        error.response?.data?.detail ||
        JSON.stringify(error.response?.data) ||
        "Failed to create order"
      );
    } finally {
      setCreatingOrder(false);
    }
  };

  const calculateGST = (amount) => {
    const basePrice = amount;
    const gst = amount * 0.1;
    const total = amount + gst;
    return { basePrice, gst, total };
  };

  const generateInvoice = (order, returnHtml = false) => {
    const GST_RATE = 0.10;

    /* ---------------- BASIC CALCULATIONS ---------------- */
    const invoiceNumber = order.invoice_number || `INV-${order.order_number.replace(/\D/g, "").padStart(4, "0")}`;

    const deliveryDate = new Date(order.delivery_date || order.created_at);
    const formatDate = (d) =>
      `${d.getDate()} ${d.toLocaleString("en-AU", { month: "short" })} ${d.getFullYear()}`;

    // Calculate Due Date (Assuming 14 days or use specific field)
    const dueDate = order.due_date ? formatDate(new Date(order.due_date)) : null;

    let subtotalExGST = 0;
    const itemRows = order.items
      .map((item) => {
        const lineExGST = item.price * item.quantity;
        return `
      <tr>
        <td class="text-left">${item.sku_name}</td>
        <td class="text-right">${item.quantity.toFixed(2)}</td>
        <td class="text-right">${item.price.toFixed(2)}</td>
        <td class="text-right">10%</td>
        <td class="text-right">${lineExGST.toFixed(2)}</td>
      </tr>`;
      })
      .join("");
    /* ---------------- 2. CREDIT NOTE LOGIC (FOR TOTALS SECTION) ---------------- */
    let totalCreditIncGST = 0;
    let creditRowsHTML = "";

    if (order.applied_credit_notes && Array.isArray(order.applied_credit_notes) && order.applied_credit_notes.length > 0) {
      order.applied_credit_notes.forEach(cn => {
        totalCreditIncGST += (cn.amount || 0);

        // A: Credit Note has specific items -> List them individually with SKU Name
        if (cn.items && Array.isArray(cn.items) && cn.items.length > 0) {
          cn.items.forEach(item => {
            // Calculate item portion inclusive of GST for the display deduction
            // (Assuming item.price in CN is the credit amount per unit)
            const itemTotal = (item.price * item.quantity);

            creditRowsHTML += `
          <div class="total-row" >
            <span>Credit note: ${item.sku_name} (${item.quantity} x ${item.price}) </span>
            <span>- ${itemTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>`;
          });
        }
        // B: Credit Note is just a lump sum -> Show generic label
        else {
          creditRowsHTML += `
        <div class="total-row" >
          <span>Credit note (${cn.credit_note_number})</span>
          <span>- ${cn.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        </div>`;
        }
      });
    }
    // Legacy Fallback
    else if (order.credited_amount) {
      totalCreditIncGST = order.credited_amount;
      creditRowsHTML = `
      <div class="total-row" >
        <span>Credit note</span>
        <span>- ${totalCreditIncGST.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
      </div>`;
    }

    /* ---------------- FINAL TAX CALCULATIONS ---------------- */
    const deliveryExGST = order.delivery_rate ?? 0.00;
    const totalSubtotal = order.subtotal || 0;
    let credited_amount = order.credited_amount || 0;
    if (credited_amount) {
      // caluclate gst to the credited amount
      const gst = credited_amount * GST_RATE;
      credited_amount = credited_amount / (1 + GST_RATE);
    }

    const totalGST = ((totalSubtotal - ((credited_amount) || 0)) * GST_RATE);
    const totalAUD = ((totalSubtotal + totalGST) - (credited_amount || 0)).toFixed(2);

    /* ---------------- HTML + PRINT ---------------- */
    const invoiceHTML = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<style>
  @page { size: A4; margin: 15mm; }
  body { 
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; 
    font-size: 11px; 
    color: #333; 
    line-height: 1.4;
    margin: 0; 
    zoom: 95%; 
  }
  .text-right { text-align: right; }
  .text-left { text-align: left; }
  .bold { font-weight: bold; }
  
  /* Header section */
  .header-container { display: flex; justify-content: space-between; margin-bottom: 40px; }
  .brand-title { font-size: 28px; font-weight: bold; color: #000; margin: 0; }
  .customer-info { margin-top: 10px; font-size: 12px; }
  
  .company-details { text-align: right; }
  .logo { width: 80px; margin-bottom: 10px; }
  
  /* Meta data (Invoice Date/No etc) */
  .meta-table { margin-left: auto; border-spacing: 0 4px; }
  .meta-table td { padding-left: 20px; vertical-align: top; }
  .meta-label { color: #000; font-weight: bold; width: 100px; }

  /* Main Items Table */
  .main-table { width: 100%; border-collapse: collapse; margin-top: 50px; }
  .main-table th { border-bottom: 2px solid #000; padding: 8px 0; text-align: right; font-weight: bold; }
  .main-table th:first-child { text-align: left; }
  .main-table td { padding: 8px 0; border-bottom: 1px solid #eee; }
  
  /* Totals Section */
  .totals-container { width: 250px; margin-left: auto; margin-top: 10px; }
  .total-row { display: flex; justify-content: space-between; padding: 4px 0; }
  .grand-total-row { border-top: 2px solid #000; margin-top: 10px; padding-top: 10px; font-weight: bold; font-size: 13px; }

  /* Payment Details */
  .payment-details { margin-top: 40px; }
  
  /* Payment Advice Slip */
  .advice-slip { margin-top: 80px; border-top: 1px dashed #000; padding-top: 20px; position: relative; }
  .scissors { position: absolute; top: -10px; left: 20px; background: white; padding: 0 5px; font-size: 16px; }
  .advice-header { font-size: 24px; font-weight: bold; margin-bottom: 20px; }
  .advice-container { display: flex; justify-content: space-between; }
  
  .advice-meta-table { border-collapse: collapse; width: 350px; background: #f9f9f9; }
  .advice-meta-table td { padding: 6px; border: 1px solid #ddd; }
  .enclosed-box { border: 1px solid #000; height: 30px; margin-top: 5px; background: white; }
  .invoice-wrapper {
    background: #fff;
    padding: 20px;
    max-width: 210mm;
    margin: auto;
  }
@media print {
  body {
    padding: 0;
    background: #fff;
  }

  .invoice-wrapper {
    padding: 0;
    margin: 0;
  }
}
</style>
</head>
<body onload="window.print()">
  <div class="invoice-wrapper">
  <div class="header-container">
    <div>
      <h1 class="brand-title">TAX INVOICE</h1>
      <div class="customer-info">
        <p>${order.customer_name}<br/>${order.delivery_address || ''}</p>
      </div>
    </div>
    <div class="company-details">
       <img src="data:image/png;base64,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" class="logo" alt="Logo"/>
      <p class="bold">Infinite Laundry Solutions</p>
      <p>76c Mica St<br/>CAROLE PARK QLD 4300<br/>AUSTRALIA</p>
    </div>
  </div>

  <table class="meta-table">
    <tr>
      <td class="meta-label">Delivery date</td>
      <td>${formatDate(deliveryDate)}</td>
    </tr>
    <tr>
      <td class="meta-label">Invoice Number</td>
      <td>${invoiceNumber}</td>
    </tr>
    <tr>
      <td class="meta-label">Reference</td>
      <td>Delivery Order-${order.order_number.replace(/\D/g, "")}</td>
    </tr>
    <tr>
      <td class="meta-label">ABN</td>
      <td>20 671 606 901</td>
    </tr>
  </table>

  <table class="main-table">
    <thead>
      <tr>
        <th class="text-left">Description</th>
        <th>Quantity</th>
        <th>Unit Price</th>
        <th>GST</th>
        <th>Amount AUD</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
    </tbody>
  </table>

  <div class="totals-container">
    <div class="total-row">
      <span>Subtotal</span>
      <span>${totalSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    </div>
     ${creditRowsHTML}
    <div class="total-row" style="border-top: 1px solid #ddd; margin-top:5px; padding-top:5px;">
      <span class="bold">TOTAL GST 10%</span>
      <span class="bold">${totalGST.toFixed(2)}</span>
    </div>
    <div class="total-row grand-total-row">
      <span>TOTAL AUD</span>
      <span>${totalAUD}</span>
    </div>
  </div>

  <div class="payment-details">
    <p class="bold" style="font-size: 13px; margin-bottom: 5px;">${dueDate ? `Due Date: ${dueDate}` : 'Due upon receipt'}</p>
    <p>Pay by Direct Deposit-</p>
    <p>Account Name: <span class="bold">Infinite Asset Solutions</span><br/>
       BSB: <span class="bold">034-069</span><br/>
       Account Number: <span class="bold">671254</span>
    </p>
  </div>

  <!-- Bottom Advice Section -->
  <div class="advice-slip">
    <div class="scissors">✂--------------------------------------------------------------------------------------------------</div>
    <h2 class="advice-header">PAYMENT ADVICE</h2>
    <div class="advice-container">
      <div style="width: 250px;">
        <p class="bold">To:</p>
        <p>Infinite Laundry Solutions<br/>76c Mica St<br/>CAROLE PARK QLD 4300<br/>AUSTRALIA</p>
      </div>
      
      <table class="advice-meta-table">
        <tr>
          <td class="bold">Customer</td>
          <td>${order.customer_name}</td>
        </tr>
        <tr>
          <td class="bold">Invoice Number</td>
          <td>${invoiceNumber}</td>
        </tr>
        <tr>
          <td class="bold">Amount Due</td>
          <td class="bold">${totalAUD}</td>
        </tr>
        <tr>
          <td class="bold">Due Date</td>
          <td>${dueDate}</td>
        </tr>
        <tr>
          <td class="bold">Amount Enclosed</td>
          <td>
            <div class="enclosed-box"></div>
            <p style="font-size: 9px; margin-top: 2px;">Enter the amount you are paying above</p>
          </td>
        </tr>
      </table>
    </div>
  </div>
</div>
</body>
</html>
`;
    if (returnHtml) {
      return invoiceHTML;
    }
    const printWindow = window.open("", "_blank");
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
  };

  const generateDeliveryReceipt = (order) => {
    const invoiceDate = new Date(order.delivery_date).toLocaleDateString(
      "en-AU"
    );
    // Calculate total from actual items to ensure consistency
    const itemsTotal = order.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    const { basePrice, gst, total } = calculateGST(itemsTotal);

    let invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Invoice - Order ${order.order_number}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid ##a0d5c1; padding-bottom: 20px; }
          .company-name { font-size: 28px; font-weight: bold; color: #a0d5c1; margin-bottom: 10px; }
          .abn { font-size: 14px; color: #666; margin-top: 5px; }
          .customer-section { margin: 30px 0; padding: 15px; background-color: #f8f9fa; border-radius: 5px; }
          .customer-section h3 { margin-top: 0; color: #a0d5c1; font-size: 16px; }
          .customer-info { line-height: 1.8; }
          .order-details { margin: 30px 0; }
          .order-details h3 { color: #a0d5c1; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px; margin-bottom: 15px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background-color: #a0d5c1; color: white; padding: 12px; text-align: left; font-weight: 600; }
          td { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; }
          tr:nth-child(even) { background-color: #f8f9fa; }
          .totals { margin-top: 30px; text-align: right; }
          .total-row { display: flex; justify-content: flex-end; padding: 8px 0; font-size: 16px; }
          .total-row.grand-total { font-size: 20px; font-weight: bold; color: #a0d5c1; border-top: 2px solid #a0d5c1; padding-top: 15px; margin-top: 10px; }
          .total-label { width: 150px; text-align: right; padding-right: 20px; }
          .total-value { width: 120px; text-align: center; font-weight: 600; }
          .notice { margin-top: 40px; padding: 15px; background-color: #fef3c7; border-left: 4px solid #f59e0b; border-radius: 4px; }
          .notice strong { color: #d97706; }
          .hire-section { margin-top: 30px; padding: 20px; background-color: #e5faf3; border-radius: 5px; text-align: center; font-size: 18px; font-weight: bold; color: #a0d5c1; }
          .footer { margin-top: 50px; text-align: center; color: #666; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="/assets/logo.png" alt="Infinite Laundry Solutions" style="width: 120px; margin-bottom: 15px;">
          <div class="company-name">INFINITE LAUNDRY SOLUTIONS</div>
          <div class="abn">ABN: 20 671 606 901</div>
          <div class="abn">Address: 3/76 Mica Street, Carole Park, QLD, 4300</div>
        </div>
        <div class="customer-section">
          <h3>Customer Information</h3>
          <div class="customer-info">
            <strong>Customer Name:</strong> ${order.customer_name}<br>
            <strong>Delivery Address:</strong> ${order.delivery_address}<br>
            <strong>Date of Delivery:</strong> ${invoiceDate}
          </div>
        </div>
        <div class="order-details">
          <h3>Order Details - ${order.order_number}</h3>
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th style="text-align: center;">Quantity</th>
              </tr>
            </thead>
            <tbody>`;

    order.items.forEach((item) => {
      const itemTotal = (item.price * item.quantity).toFixed(2);
      invoiceHTML += `
              <tr>
                <td>${item.sku_name}</td>
                <td style="text-align: center;">${item.quantity}</td>
              </tr>`;
    });

    invoiceHTML += `
               <tr>
                <td><strong>Total Quantity </strong></td>
                <td style="text-align: center; font-weight: bold;">${order.items.reduce((total, item) => total + item.quantity, 0)}</td>
            </tr>
            </tbody>
          </table>
          <!-- <div class="totals">
            <div class="total-row grand-total">
              <div class="total-label">Total Quantity:</div>
              <div class="total-value">${order.items.reduce((total, item) => total + item.quantity, 0)}</div>
            </div>
          </div> -->
          
        </div>
        <div class="notice">
          <strong>IMPORTANT:</strong> Any discrepancies must be notified within 24 hours of delivery.
        </div>
        <div class="hire-section">HIRE</div>
        <div class="footer">
          Thank you for your business!<br>
          Infinite Laundry Solutions
        </div>
      </body>
      </html>`;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    printWindow.onload = function () {
      printWindow.print();
    };
  };


  const handleEditOrder = (order) => {
    setEditingOrder(order);

    let templateId = order.frequency_template_id || "";
    if (order.is_recurring && order.recurrence_pattern && !templateId) {
      const matchingTemplate = frequencyTemplates.find(
        (t) =>
          t.frequency_type === order.recurrence_pattern.frequency_type &&
          t.frequency_value === order.recurrence_pattern.frequency_value
      );
      if (matchingTemplate) {
        templateId = matchingTemplate.id;
      }
    }

    setOrderForm({
      customer_id: order.customer_id,
      customer_name: order.customer_name,
      customer_email: order.customer_email,
      delivery_date: order.delivery_date,
      pickup_address: order.pickup_address,
      delivery_address: order.delivery_address,
      special_instructions: order.special_instructions || "",
      is_recurring: order.is_recurring || false,
      frequency_template_id: templateId,
    });
    setOrderItems(
      order.items.map((item) => ({
        sku_id: item.sku_id,
        quantity: item.quantity,
      }))
    );
    setShowOrderDialog(true);
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    setCreatingOrder(true);
    try {
      const skuList = customerSkus.length > 0 ? customerSkus : skus;

      const validItems = orderItems
        .filter((item) => item.sku_id && item.quantity > 0)
        .map((item) => {
          const sku = skuList.find((s) => s.id === item.sku_id);
          if (!sku) return null;

          const price =
            sku.customer_price !== undefined ? sku.customer_price : sku.price;

          return {
            sku_id: item.sku_id,
            sku_name: sku.name,
            quantity: parseInt(item.quantity),
            price: parseFloat(price),
          };
        })
        .filter((item) => item !== null);

      if (validItems.length === 0) {
        alert("Please add at least one valid item");
        setCreatingOrder(false);
        return;
      }

      const updateData = {
        delivery_date: orderForm.delivery_date,
        pickup_address: orderForm.pickup_address,
        delivery_address: orderForm.delivery_address,
        special_instructions: orderForm.special_instructions || null,
        items: validItems,
      };

      // Update the order
      let response;
      if (editingOrder.is_recurring) {
        response = await axios.put(`${API}/orders/${editingOrder.id}/propose-modification`, updateData);
      } else {
        response = await axios.put(`${API}/orders/${editingOrder.id}`, updateData);
      }

      // Check if there's an existing invoice for this order
      const existingInvoice = invoices.find(inv =>
        inv.order_id === editingOrder.id ||
        inv.order_number === editingOrder.order_number
      );

      if (existingInvoice) {
        toast.success("Order updated successfully. Invoice has been updated and marked for review.");
      } else {
        toast.success("Order updated successfully");
      }

      // Close dialog and reset form
      setShowOrderDialog(false);
      setEditingOrder(null);
      setOrderForm({
        customer_id: "",
        customer_name: "",
        customer_email: "",
        items: [],
        delivery_date: "",
        pickup_address: PERMANENT_PICKUP_ADDRESS,
        delivery_address: "",
        special_instructions: "",
        is_recurring: false,
        frequency_template_id: "",
      });
      setOrderItems([{ sku_id: "", quantity: 1 }]);
      setCustomerSkus([]);

      // Refresh data immediately
      await fetchOrders();
      if (activeTab === "invoices") {
        await fetchInvoices();
      } else {
        // Still refresh invoices in background
        setTimeout(() => {
          fetchInvoices();
        }, 1000);
      }
    } catch (error) {
      console.error("Order update error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to update order");
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;

    setDeletingOrderId(orderId);
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      alert("Order deleted successfully");
      fetchOrders();
    } catch (error) {
      console.error("Order delete error:", error.response?.data);
      alert(error.response?.data?.detail || "Failed to delete order");
    } finally {
      setDeletingOrderId(null);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    if (updatingStatusOrderId) return;

    try {
      setUpdatingStatusOrderId(orderId);
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      toast.success("Order status updated successfully");
      fetchOrders();
    } catch (error) {
      console.error("Failed to update order status:", error);
      toast.error(
        error.response?.data?.detail || "Failed to update order status"
      );
    } finally {
      setUpdatingStatusOrderId(null);
    }
  };

  const handleLockOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/lock`);
      toast.success("Order locked successfully");
      fetchOrders();
    } catch (error) {
      console.error("Failed to lock order:", error);
      toast.error(error.response?.data?.detail || "Failed to lock order");
    }
  };

  const handleUnlockOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/unlock`);
      toast.success("Order unlocked successfully");
      fetchOrders();
    } catch (error) {
      console.error("Failed to unlock order:", error);
      toast.error(error.response?.data?.detail || "Failed to unlock order");
    }
  };

  // Generate and download invoice as PDF
  const handleDownloadInvoicePDF = async (order) => {
    try {
      const customer = customers.find((c) => c.id === order.customer_id);
      generateInvoice(order);
      toast.success("Invoice PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
      toast.error("Failed to download invoice PDF");
    }
  };

  const handleSendInvoiceEmail = async () => {
    if (!selectedInvoice) return;

    setSendingEmail(true);
    try {
      let toEmail;
      switch (emailForm.emailType) {
        case 'operations':
          toEmail = selectedInvoice.operations_email || selectedInvoice.customer_email;
          break;
        case 'finance':
          toEmail = selectedInvoice.secondary_email;
          break;
        default:
          toEmail = selectedInvoice.customer_email;
      }


      const html_body = generateInvoice(selectedInvoice, true);
      const pdfBlob = await htmlToPdfBytes(html_body);

      const formData = new FormData();
      formData.append("file", pdfBlob, `invoice-${selectedInvoice.invoice_number}.pdf`);
      formData.append("subject", emailForm.subject);
      formData.append("message", emailForm.message);
      formData.append("email_type", emailForm.emailType);
      formData.append("invoice_id", selectedInvoice.id);
      formData.append("include_payment_info", emailForm.includePaymentInfo);
      // const payload = {
      //   to_email: toEmail,
      //   subject: emailForm.subject,
      //   message: emailForm.message,
      //   include_payment_info: emailForm.includePaymentInfo,
      //   email_type: emailForm.emailType,
      //   html_body: html_body
      // };

      await axios.post(`${API}/invoices/${selectedInvoice.id}/send-email`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });

      // If this is an approval email, update status to pending payment
      if (emailForm.isApprovalEmail && emailForm.invoiceId) {
        await axios.put(`${API}/invoices/${emailForm.invoiceId}`, {
          status: INVOICE_STATUS.PENDING_PAYMENT,
          sent_at: new Date().toISOString()
        });

        toast.success(`Invoice approved and email sent to ${toEmail}`);
      } else {
        toast.success(`Email sent to ${toEmail}`);
      }

      // Close dialog and refresh
      setShowInvoiceEmailDialog(false);
      setSelectedInvoice(null);
      setEmailForm({
        subject: "",
        message: "",
        includePaymentInfo: true,
        emailType: "finance",
        html_body: ""
      });

      fetchInvoices();

    } catch (error) {
      console.error("Failed to send invoice email", error);
      toast.error(error.response?.data?.detail || "Failed to send invoice email");
    } finally {
      setSendingEmail(false);
    }
  };

  const loadEmailHistory = async (invoiceId) => {
    try {
      setLoadingEmailHistory(true);
      const response = await axios.get(`${API}/invoices/${invoiceId}/email-history`);
      setEmailHistory(response.data);
      setSelectedInvoiceForHistory(invoices.find(inv => inv.id === invoiceId));
      setShowEmailHistoryDialog(true);
    } catch (error) {
      console.error("Failed to load email history", error);
      toast.error("Failed to load email history");
    } finally {
      setLoadingEmailHistory(false);
    }
  };

  // Send invoice email (from dialog)
  const handleSendEmail = async () => {
    if (!selectedInvoice) return;

    setSendingEmail(true);
    try {
      await axios.post(`${API}/invoices/${selectedInvoice.id}/send`);
      toast.success(`Invoice email sent to ${selectedInvoice.customer_email}`);
      setShowInvoiceEmailDialog(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      console.error("Failed to send invoice email", error);
      toast.error(error.response?.data?.detail || "Failed to send invoice email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Get Customer Finance email
  const getCustomerFinanceEmail = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return "";

    // Check if customer has secondary_email field
    return customer.secondary_email || customer.email;
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Failed to download PDF", error);
      if (error.response?.status === 404) {
        toast.error("PDF not found. The invoice may need to be regenerated.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to download PDF");
      }
    }
  };
  // Delete invoice (owner only)
  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice? This action cannot be undone.")) return;

    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Invoice deleted successfully");
      fetchInvoices();
    } catch (error) {
      console.error("Failed to delete invoice", error);
      toast.error(error.response?.data?.detail || "Failed to delete invoice");
    }
  };


  const handleApproveInvoice = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/approve`);

      toast.success("Invoice approved");
      fetchInvoices();
    } catch (error) {
      console.error("Failed to approve invoice", error);
      toast.error(error.response?.data?.detail || "Failed to approve invoice");
    }
  };

  const addOrderItem = () => {
    setOrderItems([...orderItems, { sku_id: "", quantity: 1 }]);
  };

  const removeOrderItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const updateOrderItem = (index, field, value) => {
    const updated = [...orderItems];
    updated[index][field] = field === "quantity" ? parseInt(value) : value;
    setOrderItems(updated);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceEditForm({
      customer_name: invoice.customer_name,
      customer_email: invoice.customer_email,
      items: invoice.items || [],
      delivery_rate: invoice.delivery_rate || 0,
      subtotal: invoice.subtotal || 0,
      gst_amount: invoice.gst_amount || 0,
      total_amount: invoice.total_amount || 0,
      notes: invoice.notes || ""
    });
    setShowEditInvoiceDialog(true);
  };

  const handleSaveInvoiceEdit = async () => {
    if (!editingInvoice) return;

    try {
      // Update invoice in backend
      await axios.put(`${API}/invoices/${editingInvoice.id}`, {
        ...invoiceEditForm,
        status: editingInvoice.status === INVOICE_STATUS.DISPUTE ?
          INVOICE_STATUS.DISPUTE : // Stay in dispute if editing disputed invoice
          INVOICE_STATUS.PENDING_REVIEW, // Return to pending review for other statuses
        updated_at: new Date().toISOString(),
        updated_by: user?.id || 'owner'
      });

      toast.success("Invoice updated successfully.");
      setShowEditInvoiceDialog(false);
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error) {
      console.error("Failed to update invoice", error);
      toast.error(error.response?.data?.detail || "Failed to update invoice");
    }
  };

  // Bulk actions handler
  const handleBulkAction = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("Please select invoices first");
      return;
    }

    try {
      switch (bulkAction) {
        case 'send_email':
          // Send emails to all selected invoices
          await Promise.all(
            selectedInvoices.map(invoiceId =>
              axios.post(`${API}/invoices/${invoiceId}/send-email`, {
                subject: "Invoice Reminder",
                message: "Please find your invoice attached."
              })
            )
          );
          toast.success(`Emails sent to ${selectedInvoices.length} invoice(s)`);
          break;

        case 'status_pending_review':
        case 'status_approved':
        case 'status_pending_payment':
        case 'status_cleared':
        case 'status_dispute': {
          const statusMap = {
            status_pending_review: INVOICE_STATUS.PENDING_REVIEW,
            status_approved: INVOICE_STATUS.APPROVED,
            status_pending_payment: INVOICE_STATUS.PENDING_PAYMENT,
            status_cleared: INVOICE_STATUS.CLEARED,
            status_dispute: INVOICE_STATUS.DISPUTE,
          };
          const newStatus = statusMap[bulkAction];
          const isCleared = newStatus === INVOICE_STATUS.CLEARED;
          await Promise.all(
            selectedInvoices.map(invoiceId =>
              axios.put(`${API}/invoices/${invoiceId}`, {
                status: newStatus,
                ...(isCleared ? { is_paid: true, paid_date: new Date().toISOString() } : {}),
              })
            )
          );
          const labelMap = {
            status_pending_review: 'Pending Review',
            status_approved: 'Approved',
            status_pending_payment: 'Pending Payment',
            status_cleared: 'Cleared (Paid)',
            status_dispute: 'Dispute',
          };
          toast.success(`${selectedInvoices.length} invoice(s) set to ${labelMap[bulkAction]}`);
          break;
        }

        case 'download_pdf':
          for (const invoiceId of selectedInvoices) {
            const invoice = invoices.find(i => i.id === invoiceId);
            if (!invoice) continue;
            const html = generateInvoice(invoice, true);
            const blob = await htmlToPdfBytes(html);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${invoice.invoice_number || invoice.order_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
          toast.success(`${selectedInvoices.length} PDF(s) downloaded`);
          break;

        default:
          break;
      }

      // Clear selection
      setSelectedInvoices([]);
      setShowBulkActionDialog(false);
      fetchInvoices();
    } catch (error) {
      console.error("Bulk action failed", error);
      toast.error("Failed to perform bulk action");
    }
  };

  // Invoice sorting and filtering functions
  const getSortedAndFilteredInvoices = () => {
    let filtered = [...invoices];

    // Apply credit note filter
    if (invoiceFilters.isCreditNote) {
      filtered = filtered.filter(inv => inv.is_credit_note === true);
    }

    // Apply status filter
    if (invoiceFilters.status.length > 0) {
      filtered = filtered.filter(inv => invoiceFilters.status.includes(inv.status));
    }

    // Apply customer filter
    if (invoiceFilters.customer.length > 0) {
      filtered = filtered.filter(inv => invoiceFilters.customer.includes(inv.customer_id));
    }

    if (invoiceFilters.dateFrom) {
      const fromDate = new Date(invoiceFilters.dateFrom);
      filtered = filtered.filter(inv => new Date(inv.invoice_date) >= fromDate);
    }

    if (invoiceFilters.dateTo) {
      const toDate = new Date(invoiceFilters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(inv => new Date(inv.invoice_date) <= toDate);
    }

    // Apply amount range filter
    if (invoiceFilters.amountFrom) {
      const minAmount = parseFloat(invoiceFilters.amountFrom);
      filtered = filtered.filter(inv => inv.total_amount >= minAmount);
    }

    if (invoiceFilters.amountTo) {
      const maxAmount = parseFloat(invoiceFilters.amountTo);
      filtered = filtered.filter(inv => inv.total_amount <= maxAmount);
    }

    // History toggle:
    // - when OFF: show everything except cleared
    // - when ON: show only cleared (paid) invoices
    if (showClearedInvoices) {
      filtered = filtered.filter(invoice => invoice.status === INVOICE_STATUS.CLEARED);
    } else {
      filtered = filtered.filter(invoice => invoice.status !== INVOICE_STATUS.CLEARED);
    }

    // Apply sorting 2418 for proper sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (invoiceSortBy) {
        case "invoice_number":
          aVal = a.invoice_number || "";
          bVal = b.invoice_number || "";
          break;
        case "order_number":
          aVal = a.order_number || "";
          bVal = b.order_number || "";
          break;
        case "customer_name":
          aVal = a.customer_name || "";
          bVal = b.customer_name || "";
          break;
        case "amount":
          aVal = a.total_amount || 0;
          bVal = b.total_amount || 0;
          break;
        case "due_date":
          aVal = new Date(a.due_date || 0);
          bVal = new Date(b.due_date || 0);
          break;
        case "created_at":
        default:
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          break;
      }

      if (typeof aVal === 'number') {
        return invoiceSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else if (aVal instanceof Date) {
        return invoiceSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else {
        const comparison = aVal.toString().localeCompare(bVal.toString());
        return invoiceSortOrder === "asc" ? comparison : -comparison;
      }
    });

    return filtered;
  };

  // Chart data preparation functions
  const getRevenueChartData = () => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      last7Days.push({
        date: date.toISOString().split("T")[0],
        revenue: 0,
        orders: 0,
      });
    }

    orders.forEach((order) => {
      if (order.created_at && order.total_price) {
        try {
          const createdDate = new Date(order.created_at);
          const orderDate = createdDate.toISOString().split("T")[0];
          const dayData = last7Days.find((d) => d.date === orderDate);
          if (dayData) {
            dayData.revenue += parseFloat(order.total_price) || 0;
            dayData.orders += 1;
          }
        } catch (e) {
          console.error("Error parsing date for revenue:", order.created_at, e);
        }
      }
    });

    return last7Days.map((day) => ({
      name: new Date(day.date).toLocaleDateString("en-US", {
        weekday: "short",
      }),
      fullDate: new Date(day.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      revenue: parseFloat(day.revenue.toFixed(2)),
      orders: day.orders,
    }));
  };

  const getOrdersByStatusData = () => {
    const statusCounts = {
      pending: 0,
      confirmed: 0,
      processing: 0,
      ready: 0,
      "out-for-delivery": 0,
      delivered: 0,
      cancelled: 0,
    };

    orders.forEach((order) => {
      if (order.status && statusCounts.hasOwnProperty(order.status)) {
        statusCounts[order.status]++;
      }
    });

    const statusLabels = {
      pending: "Pending",
      confirmed: "Confirmed",
      processing: "Processing",
      ready: "Ready",
      "out-for-delivery": "Out for Delivery",
      delivered: "Delivered",
      cancelled: "Cancelled",
    };

    const result = Object.entries(statusCounts)
      .filter(([_, count]) => count > 0)
      .map(([status, count]) => ({
        name: statusLabels[status] || status,
        value: count,
        status: status,
      }));

    return result;
  };

  const getTopCustomersData = () => {
    const customerRevenue = {};

    orders.forEach((order) => {
      if (
        order.customer_name &&
        order.total_price &&
        order.status !== "cancelled"
      ) {
        const name = order.customer_name;
        if (!customerRevenue[name]) {
          customerRevenue[name] = 0;
        }
        customerRevenue[name] += parseFloat(order.total_price) || 0;
      }
    });

    return Object.entries(customerRevenue)
      .map(([name, revenue]) => ({
        name: name.length > 15 ? name.substring(0, 15) + "..." : name,
        fullName: name,
        revenue: parseFloat(revenue.toFixed(2)),
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  const getOrdersTrendData = () => {
    const ordersWithDates = orders.filter((o) => o.created_at);

    if (ordersWithDates.length === 0) {
      return [];
    }

    const last30Days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last30Days.push({
        date: date.toISOString().split("T")[0],
        orders: 0,
      });
    }

    ordersWithDates.forEach((order) => {
      try {
        const createdDate = new Date(order.created_at);
        const orderDate = createdDate.toISOString().split("T")[0];

        const dayData = last30Days.find((d) => d.date === orderDate);
        if (dayData) {
          dayData.orders += 1;
        }
      } catch (e) {
        console.error("Error parsing date:", order.created_at, e);
      }
    });

    const weeklyData = [];
    for (let i = 0; i < 30; i += 7) {
      const endIdx = Math.min(i + 7, 30);
      const weekOrders = last30Days
        .slice(i, endIdx)
        .reduce((sum, day) => sum + day.orders, 0);
      const weekStart = new Date(last30Days[i].date);
      weeklyData.push({
        name: `Week ${Math.floor(i / 7) + 1}`,
        orders: weekOrders,
        date: weekStart.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      });
    }

    return weeklyData;
  };

  const getCasesData = () => {
    let openCases = 0;
    let inProgressCases = 0;
    let resolvedCases = 0;
    let closedCases = 0;

    cases.forEach((caseItem) => {
      const status = caseItem.status?.toLowerCase();

      if (status === "open") {
        openCases++;
      } else if (status === "in_progress") {
        inProgressCases++;
      } else if (status === "resolved") {
        resolvedCases++;
      } else if (status === "closed") {
        closedCases++;
      }
    });

    const casesData = [
      { name: "Open", value: openCases, color: "#f59e0b", status: "open" },
      {
        name: "In Progress",
        value: inProgressCases,
        color: "#3b82f6",
        status: "in_progress",
      },
      {
        name: "Resolved",
        value: resolvedCases,
        color: "#10b981",
        status: "resolved",
      },
      {
        name: "Closed",
        value: closedCases,
        color: "#6b7280",
        status: "closed",
      },
    ].filter((item) => item.value > 0);

    return casesData;
  };

  const CHART_COLORS = {
    primary: "#14b8a6",
    secondary: "#0d9488",
    accent: "#2dd4bf",
    pending: "#f59e0b",
    confirmed: "#3b82f6",
    processing: "#8b5cf6",
    ready: "#10b981",
    "out-for-delivery": "#06b6d4",
    delivered: "#22c55e",
    cancelled: "#ef4444",
  };

  const getOrderForInvoice = (invoice) => {
    // First try to find the order in the orders state
    const order = orders.find(o =>
      o.id === invoice.order_id ||
      o.order_number === invoice.order_number
    );

    if (order) {
      return order;
    }

    // If order not found in state, use invoice data (which should be up-to-date)
    return {
      order_number: invoice.order_number,
      customer_name: invoice.customer_name,
      customer_email: invoice.customer_email,
      delivery_date: invoice.delivery_date,
      delivery_address: invoice.delivery_address,
      items: invoice.items || [],
      special_instructions: invoice.special_instructions || '',
      ...invoice
    };
  };
  const handleCreateCreditNote = async () => {
    if (!selectedInvoiceForCredit) return;

    const maxAllowed = selectedInvoiceForCredit.outstanding_amount ?? selectedInvoiceForCredit.total_amount;
    const amount = parseFloat(creditNoteForm.amount);

    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid credit amount");
      return;
    }

    if (amount > maxAllowed) {
      toast.error(`Credit cannot exceed the balance ($${maxAllowed.toFixed(2)})`);
      return;
    }

    try {
      const creditNoteData = {
        original_invoice_id: selectedInvoiceForCredit.id,
        reason: creditNoteForm.reason,
        amount: amount,
        notes: creditNoteForm.notes || ''
      };

      const token = localStorage.getItem('token');
      // Ensure NO trailing slash in the URL
      await axios.post(`${API}/credit-notes`, creditNoteData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success("Credit note created. Awaiting approval.");
      setShowCreditNoteDialog(false);
      setSelectedInvoiceForCredit(null);
      fetchInvoices();
      fetchAllCreditNotes()
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create credit note");
    }
  };

  const handleApproveCreditNote = async (creditNoteId) => {
    if (!window.confirm("Approve this credit note and apply it to the invoice balance?")) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/credit-notes/${creditNoteId}/approve`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success("Credit Note Approved! Invoice balance updated.");
      fetchInvoices(); // Refresh list
      if (fetchAllCreditNotes) fetchAllCreditNotes(); // If you have a separate list
    } catch (error) {
      console.error("Approval failed", error);
      toast.error(error.response?.data?.detail || "Failed to approve credit note");
    }
  };
  // Helper functions for Credit Note Items
  const addCreditNoteItem = () => {
    setCreditNoteForm({
      ...creditNoteForm,
      items: [...creditNoteForm.items, { sku_id: "", quantity: 1, price: 0 }]
    });
  };

  const removeCreditNoteItem = (index) => {
    const updatedItems = creditNoteForm.items.filter((_, i) => i !== index);
    recalculateCreditNoteTotals(updatedItems);
  };

  const updateCreditNoteItem = (index, field, value) => {
    const updatedItems = [...creditNoteForm.items];
    const item = { ...updatedItems[index], [field]: value };

    // Auto-fill price if SKU changes
    if (field === 'sku_id') {
      const skuList = customerSkus.length > 0 ? customerSkus : skus;
      const sku = skuList.find(s => s.id === value);
      if (sku) {
        item.price = sku.customer_price !== undefined ? sku.customer_price : sku.price;
        item.sku_name = sku.name;
      }
    } else if (field === 'quantity') {
      item.quantity = parseInt(value) || 0;
    } else if (field === 'price') {
      item.price = parseFloat(value) || 0;
    }

    updatedItems[index] = item;
    recalculateCreditNoteTotals(updatedItems);
  };

  const recalculateCreditNoteTotals = (items) => {
    const subtotal = items.reduce((acc, i) => acc + (parseFloat(i.price || 0) * parseInt(i.quantity || 0)), 0);
    const gst = subtotal * 0.1;
    const total = subtotal + gst;

    setCreditNoteForm(prev => ({
      ...prev,
      items: items,
      subtotal,
      gst,
      amount: total,
      total_with_gst: total
    }));
  };



  // Populate form when editing
  useEffect(() => {
    if (editingCreditNote) {
      setCreditNoteForm({
        customer_id: editingCreditNote.customer_id,
        reason: editingCreditNote.reason || "returned_goods",
        amount: editingCreditNote.amount || 0,
        notes: editingCreditNote.notes || "",
        items: editingCreditNote.items || [],
        subtotal: editingCreditNote.subtotal || 0,
        gst: editingCreditNote.gst || 0,
        total_with_gst: editingCreditNote.total_with_gst || (editingCreditNote.amount || 0)
      });

      // Fetch SKUs for this customer so the dropdowns work
      if (editingCreditNote.customer_id) {
        fetchCustomerSkus(editingCreditNote.customer_id);
      }
    } else {
      // Reset form when not editing
      setCreditNoteForm({
        customer_id: "",
        reason: "returned_goods",
        amount: 0,
        notes: "",
        items: [],
        subtotal: 0,
        gst: 0,
        total_with_gst: 0
      });
      setCustomerSkus([]);
    }
  }, [editingCreditNote]);

  const handleSaveCreditNote = async () => {

    // Validate
    if (creditNoteForm.items.length === 0 && creditNoteForm.amount <= 0) {
      toast.error("Please add items or enter a valid amount");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const payload = {
        customer_id: creditNoteForm.customer_id,
        reason: creditNoteForm.reason,
        amount: creditNoteForm.amount,
        items: creditNoteForm.items,
        subtotal: creditNoteForm.subtotal,
        gst: creditNoteForm.gst,
        total_with_gst: creditNoteForm.total_with_gst,
        notes: creditNoteForm.notes || ''
      };

      if (editingCreditNote) {
        // EDIT MODE
        await axios.put(`${API}/credit-notes/${editingCreditNote.id}`, payload, { headers });
        toast.success("Credit note updated successfully");
      } else {
        // CREATE MODE
        await axios.post(`${API}/credit-notes`, payload, { headers });
        toast.success("Credit note created. Awaiting approval.");
      }

      // Reset and Refresh
      setShowCreditNoteDialog(false);
      setEditingCreditNote(null);
      setSelectedInvoiceForCredit(null);
      setCreditNoteForm({
        customer_id: "",
        reason: "returned_goods",
        amount: 0,
        notes: "",
        items: [],
        subtotal: 0,
        gst: 0,
        total_with_gst: 0
      });

      fetchInvoices();
      if (showCreditNoteList) fetchAllCreditNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Infinite Laundry Solutions
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("overview")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "overview"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            {user.role === 'admin' ? "Pending Edit Requests" : "Overview"}
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "users"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab("skus")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "skus"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            SKU Management
          </button>
          <button
            onClick={() => setActiveTab("customer-pricing")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "customer-pricing"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Customer Pricing
          </button>
          <button
            onClick={() => setActiveTab("frequency-templates")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "frequency-templates"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Frequency Templates
          </button>
          <button
            onClick={() => setActiveTab("cases")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "cases"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Case Management
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "orders"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Orders & Drivers
          </button>
          <button
            onClick={() => setActiveTab("delivery-tracking")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "delivery-tracking"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Delivery Tracking
          </button>
          <button
            onClick={() => setActiveTab("live-tracking")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "live-tracking"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            🛰️ Live Tracking
          </button>
          <button
            onClick={() => setActiveTab("calendar")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "calendar"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            📅 Calendar
          </button>
          <button
            onClick={() => { setActiveTab("invoices"); }}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "invoices"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Invoices
          </button>
        </div>
        {activeTab === "live-tracking" && (
          <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Driver Live Tracking</h2>
                <p className="text-gray-600">Real-time location of drivers currently active on the platform.</p>
              </div>
              <div className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full font-bold text-sm">
                {liveDrivers.length} Drivers Online
              </div>
            </div>

            <Card className="h-[70vh] overflow-hidden border-2 border-teal-100">
              <MapContainer
                center={[-27.4698, 153.0251]} // Default to Brisbane area or center of your customers
                zoom={12}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {liveDrivers.map((cust) => (
                  <Marker
                    key={cust.id}
                    position={[cust.last_lat, cust.last_lng]}
                    icon={L.divIcon({
                      className: 'custom-div-icon',
                      html: `<div style="background-color: #14b8a6; color: white; padding: 5px 10px; border-radius: 20px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); white-space: nowrap;">${cust.full_name}</div>`,
                      iconSize: [100, 40],
                      iconAnchor: [50, 20]
                    })}
                  >
                    <Popup>
                      <div className="p-2">
                        <p className="font-bold text-teal-700">{cust.full_name}</p>
                        <p className="text-xs text-gray-600">{cust.email}</p>
                        {cust.phone && <p className="text-xs text-gray-600">{cust.phone}</p>}
                        <div className="mt-2 text-[10px] text-gray-400">
                          Last updated: {new Date().toLocaleTimeString()}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </Card>
          </div>
        )}
        {/* Pending Edit Requests - Show on Overview Tab */}
        {activeTab === "overview" && pendingEditRequests.length > 0 && (
          <div className="mt-6">
            <Card className="border-yellow-300 bg-yellow-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-yellow-900">
                  <Clock className="w-5 h-5" />
                  Pending Customer Edit Requests ({pendingEditRequests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingEditRequests.map((order) => (
                    <div
                      key={order.id}
                      className="bg-white p-4 rounded-lg border border-yellow-200"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-gray-900">
                              {order.order_number}
                            </span>
                            <span className="text-sm text-gray-600">
                              {order.customer_name}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            Requested:{" "}
                            {new Date(
                              order.modification_requested_at
                            ).toLocaleString()}
                          </div>
                          {order.pending_modifications?.reason && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                              <span className="font-medium">Reason:</span>{" "}
                              {order.pending_modifications.reason}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleViewEditRequest(order)}
                          className="bg-yellow-500 hover:bg-yellow-600"
                        >
                          Review Request
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Overview Tab */}
        {(activeTab === "overview") && (
          user.role !== 'admin' && (
            <div className="space-y-6 animate-in fade-in duration-500">

              {/* --- DATE FILTER BAR (Keep your existing one) --- */}
              <Card className="bg-white border border-gray-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> Start Date
                        </Label>
                        <Input
                          type="date"
                          value={overviewDateFrom}
                          onChange={(e) => setOverviewDateFrom(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> End Date
                        </Label>
                        <Input
                          type="date"
                          value={overviewDateTo}
                          onChange={(e) => setOverviewDateTo(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={applyOverviewFilters}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                      >
                        <Filter className="w-4 h-4 mr-2" />
                        Apply
                      </Button>
                      {(overviewDateFrom || overviewDateTo) && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            setOverviewDateFrom("");
                            setOverviewDateTo("");
                            setTimeout(() => {
                              axios.get(`${API}/analytics/dashboard`)
                                .then(res => setStats(res.data));
                            }, 100);
                          }}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* --- ROW 1: FINANCIAL HEALTH (High Value Insights) --- */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Cleared Revenue */}
                <Card className="border-l-4 border-l-teal-500 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Cleared Revenue</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">
                          ${stats?.counters?.cleared_revenue?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
                        </h3>
                      </div>
                      <div className="h-10 w-10 bg-teal-50 rounded-full flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-teal-600" />
                      </div>
                    </div>
                    <p className="text-xs text-teal-600 mt-2 flex items-center">
                      <TrendingUp className="h-3 w-3 mr-1" /> Actual Cash in Bank
                    </p>
                  </CardContent>
                </Card>

                {/* Average Order Value (New) */}
                <Card className="border-l-4 border-l-blue-500 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Avg. Order Value</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">
                          ${stats?.counters?.avg_order_value?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
                        </h3>
                      </div>
                      <div className="h-10 w-10 bg-blue-50 rounded-full flex items-center justify-center">
                        <CreditCard className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Per transaction average
                    </p>
                  </CardContent>
                </Card>

                {/* total_customers */}
                <Card className="border-l-4 border-l-orange-500 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Total Customers</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">
                          {stats?.counters?.total_customers || "0"}
                        </h3>
                      </div>
                      <div className="h-10 w-10 bg-orange-50 rounded-full flex items-center justify-center">
                        <Users className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                    <p className="text-xs text-orange-600 mt-2 font-medium">
                      Total Customers
                    </p>
                  </CardContent>
                </Card>

                {/* total_orders */}
                <Card className="border-l-4 border-l-purple-500 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Total Orders</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">
                          {stats?.counters?.total_orders || "0"}
                        </h3>
                      </div>
                      <div className="h-10 w-10 bg-purple-50 rounded-full flex items-center justify-center">
                        <ShoppingCart className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Total Orders
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* --- ROW 2: GROWTH & VIP CUSTOMERS --- */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Revenue Trend Area Chart */}
                <Card className="lg:col-span-2 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-800">
                      Cleared Revenue Trend
                    </CardTitle>
                    <p className="text-sm text-gray-500">Cash flow over time based on date filters</p>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px] w-full">
                      {stats?.revenue_trend && stats.revenue_trend.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={stats.revenue_trend}>
                            <defs>
                              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis
                              dataKey="_id"
                              tick={{ fontSize: 12, fill: '#6b7280' }}
                              minTickGap={30}
                            />
                            <YAxis
                              tickFormatter={(val) => `$${val}`}
                              tick={{ fontSize: 12, fill: '#6b7280' }}
                            />
                            <Tooltip
                              formatter={(val) => [`$${val.toLocaleString()}`, "Revenue"]}
                              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                            />
                            <Area
                              type="monotone"
                              dataKey="revenue"
                              stroke="#0d9488"
                              fillOpacity={1}
                              fill="url(#colorRevenue)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-400">
                          No revenue data for selected period
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Top 5 Customers (New) */}
                <Card className="shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-teal-600" /> Top 5 Customers
                    </CardTitle>
                    <p className="text-sm text-gray-500">By total sales volume</p>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px] w-full">
                      {stats?.top_customers && stats.top_customers.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            layout="vertical"
                            data={stats.top_customers}
                            margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                          >
                            <XAxis type="number" hide />
                            <YAxis
                              dataKey="_id"
                              type="category"
                              width={100}
                              tick={{ fontSize: 11, fill: '#374151' }}
                              tickFormatter={(val) => val && val.length > 12 ? val.substring(0, 12) + '...' : val}
                            />
                            <Tooltip
                              cursor={{ fill: '#f3f4f6' }}
                              formatter={(val) => [`$${val.toLocaleString()}`, "Spent"]}
                              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                            />
                            <Bar dataKey="total_spent" radius={[0, 4, 4, 0]} barSize={24}>
                              {stats.top_customers.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={index === 0 ? "#0d9488" : "#94a3b8"} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-400">
                          No customer data
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* --- REJECT LINEN RATE --- */}
              {rejectLinenStats && (
                <Card className="border-l-4 border-l-red-500 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="h-9 w-9 bg-red-50 rounded-full flex items-center justify-center">
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Reject Linen Rate</p>
                          <p className="text-xs text-gray-400">
                            Rejected items ÷ Total delivered × 100
                            {rejectLinenCustomerId === "all"
                              ? " (all customers)"
                              : ` (${customers.find(c => c.id === rejectLinenCustomerId)?.full_name || "selected customer"})`}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={rejectLinenCustomerId} onValueChange={changeRejectLinenCustomer}>
                          <SelectTrigger className="h-8 w-[180px] text-xs">
                            <SelectValue placeholder="All Customers" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Customers</SelectItem>
                            {customers.filter(c => c.role === "customer").map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex flex-wrap gap-1">
                          {["weekly", "monthly", "quarterly", "yearly"].map((p) => (
                            <button
                              key={p}
                              onClick={() => selectRejectLinenPeriod(p)}
                              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                                rejectLinenPeriod === p
                                  ? "bg-red-500 text-white"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              }`}
                            >
                              {p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                          ))}
                          {rejectLinenPeriod === "custom" && (
                            <span className="px-3 py-1 text-xs rounded-full font-medium bg-red-500 text-white">
                              Custom
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Custom date range */}
                    <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                      <div>
                        <Label className="text-xs text-gray-500">From</Label>
                        <Input
                          type="date"
                          value={rejectLinenCustom.start_date}
                          onChange={(e) => setRejectLinenCustom({ ...rejectLinenCustom, start_date: e.target.value })}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">To</Label>
                        <Input
                          type="date"
                          value={rejectLinenCustom.end_date}
                          onChange={(e) => setRejectLinenCustom({ ...rejectLinenCustom, end_date: e.target.value })}
                          className="h-8 text-xs"
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={applyRejectLinenCustom}
                        disabled={rejectLinenLoading}
                        className="h-8 bg-red-500 hover:bg-red-600 text-xs"
                      >
                        {rejectLinenLoading ? "Loading..." : "Apply Range"}
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Metric tiles */}
                      <div className="lg:col-span-1 grid grid-cols-3 gap-4 text-center self-center">
                        <div>
                          <p className="text-2xl font-bold text-red-600">
                            {rejectLinenStats[rejectLinenPeriod]?.reject_rate_pct ?? 0}%
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Reject Rate</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-800">
                            {rejectLinenStats[rejectLinenPeriod]?.rejected_qty ?? 0}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Rejected</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-800">
                            {rejectLinenStats[rejectLinenPeriod]?.delivered_qty ?? 0}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Delivered</p>
                        </div>
                      </div>

                      {/* Trend over time */}
                      <div className="lg:col-span-2">
                        <p className="text-xs font-semibold text-gray-600 mb-2">
                          Reject Rate Trend
                          {rejectLinenStats.trend_granularity === "day" && " (daily)"}
                          {rejectLinenStats.trend_granularity === "week" && " (weekly)"}
                          {rejectLinenStats.trend_granularity === "month" && " (monthly)"}
                        </p>
                        {Array.isArray(rejectLinenStats.trend) && rejectLinenStats.trend.length > 0 ? (
                          <div style={{ width: "100%", height: 200 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={rejectLinenStats.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
                                <YAxis tick={{ fontSize: 10 }} unit="%" />
                                <Tooltip
                                  formatter={(value) => [`${value}%`, "Reject Rate"]}
                                  labelStyle={{ fontSize: 12 }}
                                  contentStyle={{ fontSize: 12, borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
                                />
                                <Line type="monotone" dataKey="reject_rate_pct" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
                            No trend data available
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* --- ROW 3: BUSINESS STABILITY & PRODUCT MIX --- */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                {/* Recurring vs One-off (New Donut) */}
                <Card className="shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <Repeat className="w-5 h-5 text-purple-600" /> Revenue Source
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px] w-full flex items-center justify-center">
                      {stats?.recurring_split && stats.recurring_split.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={stats.recurring_split}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={2}
                            >
                              {stats.recurring_split.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.name === 'Recurring' ? '#8b5cf6' : '#9ca3af'} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(val) => `$${val.toLocaleString()}`} />
                            <Legend verticalAlign="bottom" height={36} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-gray-400 text-sm">No data available</div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Order Status Distribution */}
                <Card className="shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-800">Order Health</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px] w-full flex items-center justify-center">
                      {stats?.order_status_dist && stats.order_status_dist.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={stats.order_status_dist}
                              dataKey="count"
                              nameKey="_id"
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={2}
                            >
                              {stats.order_status_dist.map((entry, index) => {
                                // Color mapping for statuses
                                let color = '#94a3b8';
                                if (entry._id === 'delivered') color = '#10b981';
                                if (entry._id === 'pending') color = '#f59e0b';
                                if (entry._id === 'processing') color = '#3b82f6';
                                if (entry._id === 'cancelled') color = '#ef4444';
                                return <Cell key={`cell-${index}`} fill={color} />;
                              })}
                            </Pie>
                            <Tooltip />
                            <Legend
                              layout="horizontal"
                              verticalAlign="bottom"
                              height={36}
                              formatter={(val) => val ? val.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown'}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-gray-400 text-sm">No data available</div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Products List */}
                <Card className="shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-gray-800">Top Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {stats?.top_products && stats.top_products.length > 0 ? (
                        stats.top_products.map((product, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded bg-teal-50 flex items-center justify-center text-teal-700 font-bold text-xs">
                                #{idx + 1}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 truncate w-32">{product._id}</p>
                                <p className="text-xs text-gray-500">{product.total_quantity} sold</p>
                              </div>
                            </div>
                            <span className="text-sm font-bold text-gray-700">
                              ${product.total_revenue.toLocaleString()}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-gray-400 py-8">No product sales yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* --- ROW 4: ACTIVITY FEED --- */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-800">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[300px] overflow-y-auto">
                    {stats?.recent_activity && stats.recent_activity.length > 0 ? (
                      stats.recent_activity.map((activity, idx) => (
                        <div key={idx} className="flex items-start pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                          <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center mt-1 mr-3">
                            <PackageCheck className="h-4 w-4 text-teal-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                            <div className="flex items-center mt-1 text-xs text-gray-500 gap-2">
                              <span>{new Date(activity.date).toLocaleDateString()}</span>
                              {activity.amount && (
                                <>
                                  <span>•</span>
                                  <span className="font-semibold text-teal-600">${activity.amount?.toFixed(2)}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-10">No recent activity found.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>)
        )}


        {/* Calendar Tab */}
        {activeTab === "calendar" && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Orders Calendar
              </h2>
              <p className="text-gray-600 mt-1">
                View all orders in calendar view
              </p>
            </div>
            <OrderCalendar orders={orders} />
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div >
            <Dialog
              open={showOrderDialog}
              onOpenChange={(open) => {
                setShowOrderDialog(open);
                if (!open) {
                  setEditingOrder(null);
                  setOrderForm({
                    customer_id: "",
                    customer_name: "",
                    customer_email: "",
                    items: [],
                    delivery_date: "",
                    pickup_address: PERMANENT_PICKUP_ADDRESS,
                    delivery_address: "",
                    special_instructions: "",
                    is_recurring: false,
                    frequency_template_id: "",
                  });
                  setOrderItems([{ sku_id: "", quantity: 1 }]);
                  setCustomerSkus([]);
                }
              }}
            >
              <DialogTrigger asChild>
                {/* <Button className="bg-teal-500 hover:bg-teal-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Order
                </Button> */}
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingOrder ? "Edit Invoice" : "Create New Invoice"}
                  </DialogTitle>
                </DialogHeader>
                <form
                  onSubmit={
                    editingOrder ? handleUpdateOrder : handleCreateOrder
                  }
                  className="space-y-4"
                >
                  {/* <div>
                    <Label>Select Customer</Label>
                    <Select
                      value={orderForm.customer_id}
                      onValueChange={(value) => {
                        const selectedCustomer = customers.find(
                          (c) => c.id === value
                        );
                        setOrderForm({
                          ...orderForm,
                          customer_id: value,
                          pickup_address: businessPickupAddress,
                          delivery_address: selectedCustomer?.address || "",
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.full_name} - {customer.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div> */}

                  <div>
                    <Label>Order Items</Label>
                    {orderItems.map((item, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <Select
                          value={item.sku_id}
                          onValueChange={(value) =>
                            updateOrderItem(index, "sku_id", value)
                          }
                          disabled={!orderForm.customer_id}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue
                              placeholder={
                                orderForm.customer_id
                                  ? customerSkus.length > 0
                                    ? "Select item"
                                    : "No custom pricing set for this customer"
                                  : "Select customer first"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {customerSkus.map((sku) => {
                              const price =
                                sku.customer_price !== undefined
                                  ? sku.customer_price
                                  : sku.price;
                              const gst = price * 0.1;
                              const priceIncGST = price + gst;
                              return (
                                <SelectItem key={sku.id} value={sku.id}>
                                  {sku.name} - ${price.toFixed(2)} + GST $
                                  {gst.toFixed(2)} = ${priceIncGST.toFixed(2)}
                                  {sku.customer_price !== undefined &&
                                    " (Custom)"}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            updateOrderItem(index, "quantity", e.target.value)
                          }
                          placeholder="Qty"
                          className="w-20"
                        />
                        {orderItems.length > 1 && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removeOrderItem(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addOrderItem}
                      className="mt-2"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>

                  <div>
                    <Label>Delivery Date</Label>
                    <Input
                      type="date"
                      value={orderForm.delivery_date}
                      disabled
                      onChange={(e) =>
                        setOrderForm({
                          ...orderForm,
                          delivery_date: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div>
                    <Label>Pickup Address</Label>
                    <Input
                      value={orderForm.pickup_address}
                      disabled
                      className="bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      📍 Permanent business pickup address
                    </p>
                  </div>

                  <div>
                    <Label>Delivery Address</Label>
                    <Input
                      value={orderForm.delivery_address}
                      disabled
                      onChange={(e) =>
                        setOrderForm({
                          ...orderForm,
                          delivery_address: e.target.value,
                        })
                      }
                      placeholder="Enter delivery address"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      🏠 Customer's saved address (auto-filled when customer
                      selected, can be changed)
                    </p>
                  </div>

                  <div>
                    <Label>Special Instructions (Optional)</Label>
                    <Input
                      value={orderForm.special_instructions}
                      onChange={(e) =>
                        setOrderForm({
                          ...orderForm,
                          special_instructions: e.target.value,
                        })
                      }
                      placeholder="Any special requirements"
                    />
                  </div>

                  {/* <div>
                      <Label>Delivery Rate ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={orderForm.delivery_rate || ""}
                        onChange={(e) =>
                          setOrderForm({
                            ...orderForm,
                            delivery_rate: e.target.value,
                          })
                        }
                        placeholder="0.00"
                      />
                    </div> */}

                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center gap-3 mb-4">
                      <Switch
                        checked={orderForm.is_recurring}
                        onCheckedChange={(checked) =>
                          setOrderForm({
                            ...orderForm,
                            is_recurring: checked,
                          })
                        }
                      />
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <Repeat className="w-4 h-4 text-teal-600" />
                        Make this a recurring order
                      </Label>
                    </div>

                    {orderForm.is_recurring && (
                      <div>
                        <Label>Select Frequency Template</Label>
                        <Select
                          value={orderForm.frequency_template_id}
                          onValueChange={(value) =>
                            setOrderForm({
                              ...orderForm,
                              frequency_template_id: value,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose recurring frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            {frequencyTemplates.map((template) => (
                              <SelectItem
                                key={template.id}
                                value={template.id}
                              >
                                {template.name} - Every{" "}
                                {template.frequency_value}{" "}
                                {template.frequency_type}
                              </SelectItem>
                            ))}
                            {frequencyTemplates.length === 0 && (
                              <div className="p-2 text-sm text-gray-500 text-center">
                                No templates available. Create one in
                                Frequency Templates tab.
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/*  auto-invoiceotal with GST Breakdown */}
                  {orderItems.some(
                    (item) => item.sku_id && item.quantity
                  ) && (
                      <div className="border-t pt-4 mt-4 bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-3">
                          Order Summary
                        </h3>
                        <div className="space-y-2">
                          {orderItems.map((item, index) => {
                            const sku = customerSkus.find(
                              (s) => s.id === item.sku_id
                            );
                            if (!sku || !item.quantity) return null;
                            const price =
                              sku.customer_price !== undefined
                                ? sku.customer_price
                                : sku.price;
                            const lineTotal =
                              price * parseInt(item.quantity || 0);
                            return (
                              <div
                                key={index}
                                className="flex justify-between text-sm"
                              >
                                <span className="text-gray-600">
                                  {sku.name} x {item.quantity}
                                </span>
                                <span className="text-gray-900 font-medium">
                                  ${lineTotal.toFixed(2)}
                                </span>
                              </div>
                            );
                          })}

                          {(() => {
                            const subtotalExGST = orderItems.reduce(
                              (total, item) => {
                                const sku = customerSkus.find(
                                  (s) => s.id === item.sku_id
                                );
                                if (!sku || !item.quantity) return total;
                                const price =
                                  sku.customer_price !== undefined
                                    ? sku.customer_price
                                    : sku.price;
                                return (
                                  total + price * parseInt(item.quantity || 0)
                                );
                              },
                              0
                            );
                            const deliveryRate = parseFloat(orderForm.delivery_rate || 0);
                            const combinedTotal = subtotalExGST + deliveryRate;
                            const gstAmount = combinedTotal * 0.1;
                            const totalIncGST = combinedTotal + gstAmount;

                            return (
                              <div className="border-t pt-3 mt-3 space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    Base Amount:
                                  </span>
                                  <span className="text-gray-900 font-medium">
                                    ${subtotalExGST.toFixed(2)}
                                  </span>
                                </div>
                                {/* <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Delivery Rate:
                                    </span>
                                    <span className="text-gray-900 font-medium">
                                      ${deliveryRate.toFixed(2)}
                                    </span>
                                  </div> */}
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    GST (10%):
                                  </span>
                                  <span className="text-gray-900 font-medium">
                                    ${gstAmount.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t">
                                  <span className="text-lg font-bold text-gray-900">
                                    Total Amount
                                  </span>
                                  <span className="text-2xl font-bold text-teal-600">
                                    ${totalIncGST.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                  {editingOrder && (
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full mb-3"
                      onClick={() => generateInvoice(editingOrder)}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Download Invoice
                    </Button>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-teal-500 hover:bg-teal-600"
                    disabled={creatingOrder}
                  >
                    {creatingOrder ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        {editingOrder ? "Updating..." : "Creating..."}
                      </>
                    ) : editingOrder ? (
                      "Update Invoice"
                    ) : (
                      "Create Order"
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Dialog to Apply Multiple Credits to an Invoice */}
            <Dialog open={showApplyCreditsDialog} onOpenChange={setShowApplyCreditsDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Apply Credits to {selectedInvoice?.invoice_number}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
                    Select credits from <strong>{selectedInvoice?.customer_name}'s</strong> pool to apply to this bill.
                  </div>

                  <div className="max-h-60 overflow-y-auto border rounded-md p-2 space-y-2">
                    {availableCreditsPool.length === 0 ? (
                      <p className="text-center py-4 text-gray-500">No available credits found for this customer.</p>
                    ) : availableCreditsPool.map((cn) => (
                      <div key={cn.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded border-b last:border-0">
                        <Checkbox
                          id={cn.id}
                          checked={selectedPoolCreditIds.includes(cn.id)}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedPoolCreditIds([...selectedPoolCreditIds, cn.id]);
                            else setSelectedPoolCreditIds(selectedPoolCreditIds.filter(id => id !== cn.id));
                          }}
                        />
                        <div className="flex-1">
                          <Label htmlFor={cn.id} className="flex justify-between cursor-pointer">
                            <span className="font-medium">{cn.credit_note_number}</span>
                            {/* Ensure amount exists before calling toFixed */}
                            <span className="text-red-600 font-bold">${(cn.amount || 0).toFixed(2)}</span>
                          </Label>
                          <p className="text-xs text-gray-500">{(cn.reason || '').replace('_', ' ')}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedPoolCreditIds.length > 0 && (
                    <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg">
                      <div className="flex justify-between text-sm mt-1">
                        <span>Order Balance:</span>
                        {/* Add fallback || 0 */}
                        <span>${(selectedInvoice?.total_with_gst || 0).toFixed(2)}</span>
                      </div>
                      {selectedInvoice?.credited_amount && <div className="flex justify-between text-sm mt-1">
                        <span>Applied Credits:</span>
                        {/* Add fallback || 0 */}
                        <span>-${(selectedInvoice?.credited_amount || 0).toFixed(2)}</span>
                      </div>}
                      <div className="flex justify-between text-sm">
                        <span>Selected Credits:</span>
                        <span className="font-bold">
                          -${((availableCreditsPool
                            .filter(c => selectedPoolCreditIds.includes(c.id))
                            // Add (c.amount || 0) to prevent undefined errors
                            .reduce((sum, c) => sum + (c.amount || 0), 0)).toFixed(2))}
                        </span>
                      </div>


                      <div className="flex justify-between text-sm mt-1">
                        <span>May Invoice Balance:</span>
                        {/* Add fallback || 0 */}
                        <span>${((selectedInvoice?.total_with_gst || 0) - ((selectedInvoice?.credited_amount || 0) +
                          (availableCreditsPool
                            .filter(c => selectedPoolCreditIds.includes(c.id))
                            .reduce((sum, c) => sum + (c.amount || 0), 0)))).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  {
                    (((selectedInvoice?.total_with_gst || 0) - ((selectedInvoice?.credited_amount || 0) +
                      (availableCreditsPool
                        .filter(c => selectedPoolCreditIds.includes(c.id))
                        .reduce((sum, c) => sum + (c.amount || 0), 0)))) < 0
                    ) && (
                      <>
                        <p className="text-red-600 text-sm mt-2">You cannot apply more credits than the invoice balance.</p>
                      </>
                    )
                  }
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowApplyCreditsDialog(false)}>Cancel</Button>
                    <Button
                      className="bg-teal-600 hover:bg-teal-700"
                      disabled={selectedPoolCreditIds.length === 0 || isApplying || (
                        ((selectedInvoice?.total_with_gst || 0) - ((selectedInvoice?.credited_amount || 0) +
                          (availableCreditsPool
                            .filter(c => selectedPoolCreditIds.includes(c.id))
                            .reduce((sum, c) => sum + (c.amount || 0), 0)))) < 0
                      )}
                      onClick={async () => {
                        setIsApplying(true);
                        try {
                          await axios.put(`${API}/invoices/${selectedInvoice.id}/apply-credits`, {
                            credit_note_ids: selectedPoolCreditIds
                          });
                          toast.success("Credits applied and invoice balance updated!");
                          setShowApplyCreditsDialog(false);
                          fetchInvoices(); // Refresh the table
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Failed to apply credits");
                        } finally {
                          setIsApplying(false);
                        }
                      }}
                    >
                      {isApplying ? "Applying..." : "Apply Selected Credits"}
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>
            {/* Invoice Management Header Section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Invoice Management</h2>
                  <p className="text-gray-600 mt-1">
                    Invoices are created only after orders are delivered
                  </p>
                </div>
                <div className="flex gap-2">
                  {/* Credit Note Button */}
                  <Button
                    variant={showCreditNoteList ? "default" : "outline"}
                    onClick={() => setShowCreditNoteList(prev => !prev)}
                    className="relative"
                  >
                    Credit Notes
                    {allCreditNotes.filter(cn => cn.status === 'available').length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                        {allCreditNotes.filter(cn => cn.status === 'available').length}
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreditNoteDialog(true)
                      // clear the selected cusotmer 
                      setSelectedInvoiceForCredit(null);
                      setEditingCreditNote(null);
                      setCreditNoteForm({
                        customer_id: "",
                        reason: "",
                        amount: 0,
                        notes: "",
                        items: [],
                        subtotal: 0,
                        gst: 0,
                        total_with_gst: 0
                      });

                    }}
                    className="flex items-center gap-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    Create Credit Note
                  </Button>

                  {/* Toggle Cleared Invoices / History */}
                  <Button
                    variant="outline"
                    onClick={() => setShowClearedInvoices(!showClearedInvoices)}
                    className="flex items-center gap-2"
                  >
                    {showClearedInvoices ? (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Hide Paid (History)
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Show Paid (History)
                      </>
                    )}
                  </Button>

                  {/* Filter Toggle */}
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (showCreditNoteList) {
                        setShowCreditNoteFilters(!showCreditNoteFilters);
                      } else {
                        setShowInvoiceFilters(!showInvoiceFilters);
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                  </Button>
                </div>
              </div>
              {showCreditNoteList ? (
                /* --- CREDIT NOTES MANAGEMENT TABLE --- */
                <>
                  {showCreditNoteFilters && (
                    <Card className="mb-4">
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {/* Status Filter */}
                          <div>
                            <Label>Status</Label>
                            <Select
                              value={creditNoteFilters.status[0] || "all"}
                              onValueChange={(value) => {
                                setCreditNoteFilters({
                                  ...creditNoteFilters,
                                  status: value === "all" ? [] : [value]
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Filter by status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value="available">Available</SelectItem>
                                <SelectItem value="used">Used</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Customer Filter */}
                          <div>
                            <Label>Customer</Label>
                            <Select
                              value={creditNoteFilters.customer[0] || "all"}
                              onValueChange={(value) => {
                                setCreditNoteFilters({
                                  ...creditNoteFilters,
                                  customer: value === "all" ? [] : [value]
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Filter by customer" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Customers</SelectItem>
                                {customers.filter(c => c.role === 'customer').map(customer => (
                                  <SelectItem key={customer.id} value={customer.id}>
                                    {customer.full_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Date Range */}
                          <div>
                            <Label>Date From</Label>
                            <Input
                              type="date"
                              value={creditNoteFilters.dateFrom}
                              onChange={(e) => setCreditNoteFilters({
                                ...creditNoteFilters,
                                dateFrom: e.target.value
                              })}
                            />
                          </div>

                          <div>
                            <Label>Date To</Label>
                            <Input
                              type="date"
                              value={creditNoteFilters.dateTo}
                              onChange={(e) => setCreditNoteFilters({
                                ...creditNoteFilters,
                                dateTo: e.target.value
                              })}
                            />
                          </div>

                          {/* Amount Range */}
                          <div>
                            <Label>Amount From</Label>
                            <Input
                              type="number"
                              placeholder="Min amount"
                              value={creditNoteFilters.amountFrom}
                              onChange={(e) => setCreditNoteFilters({
                                ...creditNoteFilters,
                                amountFrom: e.target.value
                              })}
                            />
                          </div>

                          <div>
                            <Label>Amount To</Label>
                            <Input
                              type="number"
                              placeholder="Max amount"
                              value={creditNoteFilters.amountTo}
                              onChange={(e) => setCreditNoteFilters({
                                ...creditNoteFilters,
                                amountTo: e.target.value
                              })}
                            />
                          </div>

                          {/* Clear Filters */}
                          <div className="flex items-end">
                            <Button
                              variant="outline"
                              onClick={() => setCreditNoteFilters({
                                customer: [],
                                status: [],
                                dateFrom: "",
                                dateTo: "",
                                amountFrom: "",
                                amountTo: ""
                              })}
                              className="w-full"
                            >
                              Clear Filters
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                              <th className="px-6 py-3 text-left">CN Number</th>
                              <th className="px-6 py-3 text-left">Customer</th>
                              <th className="px-6 py-3 text-left">Reason</th>
                              <th className="px-6 py-3 text-center">Amount</th>
                              <th className="px-6 py-3 text-center">Date</th>
                              <th className="px-6 py-3 text-center">Status</th>
                              <th className="px-6 py-3 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {allCreditNotes.map((cn) => (
                              <tr key={cn.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 font-medium text-teal-700">{cn.credit_note_number}</td>
                                <td className="px-6 py-4">{cn.customer_name}</td>
                                <td className="px-6 py-4 text-sm capitalize">{cn.reason.replace('_', ' ')}</td>
                                <td className="px-6 py-4 text-center font-bold text-red-600">-${cn.amount.toFixed(2)}</td>
                                <td className="px-6 py-4 text-center">
                                  {new Date(cn.created_at).toLocaleString("en-AU", {
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                    // hour: "2-digit",
                                    // minute: "2-digit",
                                  })}
                                </td>

                                <td className="px-6 py-4 text-center">
                                  <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${cn.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                    {cn.status.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <div className="flex justify-center gap-2">
                                    {cn.status === 'available' && (
                                      <>
                                        <Button variant="outline" size="sm" onClick={() => {
                                          setEditingCreditNote(cn);
                                          // setCreditNoteForm({ reason: cn.reason, amount: cn.amount, notes: cn.notes });
                                          setShowCreditNoteDialog(true);
                                        }}>
                                          <Edit className="w-3 h-3" />
                                        </Button>
                                        {/* <Button variant="outline" size="sm" className="text-green-600" onClick={() => handleApproveCreditNote(cn.id)}>
                                        <CheckCircle className="w-3 h-3" />
                                      </Button> */}
                                      </>
                                    )}
                                    <Button variant="outline" size="sm" className="text-red-600" onClick={async () => {
                                      if (window.confirm("Delete this credit note?")) {
                                        await axios.delete(`${API}/credit-notes/${cn.id}`);
                                        fetchAllCreditNotes();
                                        toast.success("Deleted");
                                      }
                                    }}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </>) : (
                <>
                  {/* Invoice Filters Panel */}
                  {showInvoiceFilters && (
                    <Card className="mb-4">
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {/* Status Filter */}
                          <div>
                            <Label>Status</Label>
                            <Select
                              value={invoiceFilters.status[0] || "all"}
                              onValueChange={(value) => {
                                setInvoiceFilters({
                                  ...invoiceFilters,
                                  status: value === "all" ? [] : [value]
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Filter by status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value={INVOICE_STATUS.PENDING_REVIEW}>⏳ Pending Review</SelectItem>
                                <SelectItem value={INVOICE_STATUS.APPROVED}>✅ Approved</SelectItem>
                                <SelectItem value={INVOICE_STATUS.PENDING_PAYMENT}>💰 Pending Payment</SelectItem>
                                <SelectItem value={INVOICE_STATUS.CLEARED}>✔️ Cleared (Paid)</SelectItem>
                                <SelectItem value={INVOICE_STATUS.DISPUTE}>⚠️ Dispute</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Customer Filter */}
                          <div>
                            <Label>Customer</Label>
                            <Select
                              value={invoiceFilters.customer[0] || "all"}
                              onValueChange={(value) => {
                                setInvoiceFilters({
                                  ...invoiceFilters,
                                  customer: value === "all" ? [] : [value]
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Filter by customer" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Customers</SelectItem>
                                {customers.filter(c => c.role === 'customer').map(customer => (
                                  <SelectItem key={customer.id} value={customer.id}>
                                    {customer.full_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Date Range */}
                          <div>
                            <Label>Date From</Label>
                            <Input
                              type="date"
                              value={invoiceFilters.dateFrom}
                              onChange={(e) => setInvoiceFilters({
                                ...invoiceFilters,
                                dateFrom: e.target.value
                              })}
                            />
                          </div>

                          <div>
                            <Label>Date To</Label>
                            <Input
                              type="date"
                              value={invoiceFilters.dateTo}
                              onChange={(e) => setInvoiceFilters({
                                ...invoiceFilters,
                                dateTo: e.target.value
                              })}
                            />
                          </div>

                          {/* Amount Range */}
                          <div>
                            <Label>Amount From</Label>
                            <Input
                              type="number"
                              placeholder="Min amount"
                              value={invoiceFilters.amountFrom}
                              onChange={(e) => setInvoiceFilters({
                                ...invoiceFilters,
                                amountFrom: e.target.value
                              })}
                            />
                          </div>

                          <div>
                            <Label>Amount To</Label>
                            <Input
                              type="number"
                              placeholder="Max amount"
                              value={invoiceFilters.amountTo}
                              onChange={(e) => setInvoiceFilters({
                                ...invoiceFilters,
                                amountTo: e.target.value
                              })}
                            />
                          </div>

                          {/* Credit Notes Filter */}
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="credit-notes"
                              checked={invoiceFilters.isCreditNote}
                              onCheckedChange={(checked) => setInvoiceFilters({
                                ...invoiceFilters,
                                isCreditNote: !!checked
                              })}
                            />
                            <Label htmlFor="credit-notes">Show Credit Notes Only</Label>
                          </div>

                          {/* Clear Filters */}
                          <div className="flex items-end">
                            <Button
                              variant="outline"
                              onClick={() => setInvoiceFilters({
                                status: [],
                                customer: [],
                                dateFrom: "",
                                dateTo: "",
                                amountFrom: "",
                                amountTo: "",
                                isCreditNote: false
                              })}
                              className="w-full"
                            >
                              Clear Filters
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Sorting Controls */}
                  <Card className="mb-4">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Label>Sort by:</Label>
                          <Select value={invoiceSortBy} onValueChange={setInvoiceSortBy}>
                            <SelectTrigger className="w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="created_at">Invoice Date</SelectItem>
                              <SelectItem value="due_date">Due Date</SelectItem>
                              <SelectItem value="invoice_number">Invoice Number</SelectItem>
                              <SelectItem value="order_number">Order Number</SelectItem>
                              <SelectItem value="customer_name">Customer Name</SelectItem>
                              <SelectItem value="amount">Amount</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <Button
                          variant="outline"
                          onClick={() => setInvoiceSortOrder(invoiceSortOrder === "asc" ? "desc" : "asc")}
                          className="flex items-center gap-2"
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${invoiceSortOrder === "asc" ? "rotate-180" : ""}`} />
                          {invoiceSortOrder === "asc" ? "Ascending" : "Descending"}
                        </Button>

                        <div className="ml-auto text-sm text-gray-600">
                          Showing {getSortedAndFilteredInvoices().length} of {invoices.length} invoices
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Invoice Status Summary Cards - Updated */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    {/* Pending for Review */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Pending Review</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {/* In each card, use this logic instead of filtering invoices directly */}
                              {invoices.filter(i =>
                                i.status === INVOICE_STATUS.PENDING_REVIEW &&
                                (!showClearedInvoices ? i.status !== INVOICE_STATUS.CLEARED : true)
                              ).length}
                            </p>
                          </div>
                          <Clock className="w-8 h-8 text-yellow-500" />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Approved */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Approved</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {invoices.filter(i => i.status === INVOICE_STATUS.APPROVED).length}
                            </p>
                          </div>
                          <CheckCircle className="w-8 h-8 text-blue-500" />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pending Payment */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Pending Payment</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {invoices.filter(i => i.status === INVOICE_STATUS.PENDING_PAYMENT).length}
                            </p>
                          </div>
                          <DollarSign className="w-8 h-8 text-orange-500" />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Cleared */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Cleared</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {invoices.filter(i => i.status === INVOICE_STATUS.CLEARED).length}
                            </p>
                          </div>
                          <CheckCircle2 className="w-8 h-8 text-green-500" />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Dispute */}
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Dispute</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {invoices.filter(i => i.status === INVOICE_STATUS.DISPUTE).length}
                            </p>
                          </div>
                          <AlertCircle className="w-8 h-8 text-red-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </div></>
              )}
            </div>

            {/* Invoice Table */}
            {!showCreditNoteList && <Card className="mb-6">
              <CardContent className="p-0">
                {/* Bulk Actions Bar */}
                {selectedInvoices.length > 0 && (
                  <div className="p-4 bg-blue-50 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedInvoices.length === getSortedAndFilteredInvoices().length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedInvoices(getSortedAndFilteredInvoices().map(inv => inv.id));
                          } else {
                            setSelectedInvoices([]);
                          }
                        }}
                      />
                      <div>
                        <span className="text-sm font-medium">
                          {selectedInvoices.length} invoice(s) selected
                        </span>
                        {selectedInvoices.length > 0 && (() => {
                          const runningTotal = invoices
                            .filter(inv => selectedInvoices.includes(inv.id))
                            .reduce((sum, inv) => sum + (inv.total_with_gst || inv.total_amount || 0), 0);
                          return (
                            <span className="ml-3 text-sm font-semibold text-teal-700">
                              Total: ${runningTotal.toFixed(2)}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Select value={bulkAction} onValueChange={setBulkAction}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Bulk actions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="status_pending_review">Set → Pending Review</SelectItem>
                          <SelectItem value="status_approved">Set → Approved</SelectItem>
                          <SelectItem value="status_pending_payment">Set → Pending Payment</SelectItem>
                          <SelectItem value="status_cleared">Set → Cleared (Paid)</SelectItem>
                          <SelectItem value="status_dispute">Set → Dispute</SelectItem>
                          <SelectItem value="download_pdf">Download PDFs</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={() => setShowBulkActionDialog(true)} size="sm">
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedInvoices([])}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                )}

                <div >
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        {/* Checkbox Column */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          <Checkbox
                            checked={selectedInvoices.length === getSortedAndFilteredInvoices().length && getSortedAndFilteredInvoices().length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedInvoices(getSortedAndFilteredInvoices().map(inv => inv.id));
                              } else {
                                setSelectedInvoices([]);
                              }
                            }}
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Delivery Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Credit Note
                        </th>
                        {/* Status Column */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Updated
                        </th>
                        {/* Actions Column */}
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getSortedAndFilteredInvoices().length === 0 ? (
                        <tr>
                          <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                            {showClearedInvoices
                              ? "No invoices found"
                              : "No invoices found. Mark orders as delivered to create invoices."}
                          </td>
                        </tr>
                      ) : (
                        getSortedAndFilteredInvoices().map((invoice) => {
                          const formattedDate = invoice.delivery_date
                            ? new Date(invoice.delivery_date).toLocaleDateString("en-AU")
                            : (invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString("en-AU") : "-");
                          const formattedDueDate = invoice.due_date ? new Date(invoice.due_date).toLocaleDateString("en-AU") : "-";
                          const totalAmount = invoice.total_amount || 0;
                          const isSelected = selectedInvoices.includes(invoice.id);
                          const isCreditNote = invoice.is_credit_note && invoice.status === 'pending_review';

                          return (
                            <tr key={invoice.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${isCreditNote ? 'bg-red-50' : ''}`}>
                              {/* Checkbox Cell */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedInvoices([...selectedInvoices, invoice.id]);
                                    } else {
                                      setSelectedInvoices(selectedInvoices.filter(id => id !== invoice.id));
                                    }
                                  }}
                                />
                              </td>

                              {/* Invoice Details Cells */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {invoice.invoice_number || `INV-${invoice.order_number}`}
                                  {isCreditNote && (
                                    <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                                      Credit Note
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {invoice.order_number}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {invoice.customer_name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {invoice.customer_email}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {formattedDate}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {formattedDueDate}
                                </div>
                              </td>
                              {/* Amount Cell */}
                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <div className="text-sm font-medium text-gray-900">
                                  Total: ${(invoice.total_with_gst || invoice.total_amount || 0).toFixed(2)}
                                </div>
                                {invoice.credited_amount > 0 && (
                                  <div className="text-xs text-red-600 font-bold">
                                    {/* Add || 0 here */}
                                    Credited: -${(invoice.credited_amount || 0).toFixed(2)}
                                  </div>
                                )}
                                {/* Rest of the balance logic... */}
                              </td>

                              {/* Credit Note Quick Action Button (Add this to the Actions Cell) */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex justify-center gap-2">
                                  {/* Only allow applying credits to unpaid/approved invoices */}
                                  {/* {invoice.status === INVOICE_STATUS.PENDING_REVIEW && ( */}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleOpenApplyCredits(invoice)}
                                    className="text-orange-600 border-orange-100 hover:bg-orange-50"
                                    title="Apply Credits from Pool"
                                  >
                                    <CreditCard className="w-4 h-4 mr-1" />
                                    Apply Credit
                                  </Button>
                                </div>
                              </td>

                              {/* Status Cell with Dropdown */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Select
                                  value={invoice.status}
                                  onValueChange={(value) => handleUpdateInvoiceStatus(invoice.id, value)}
                                  disabled={invoice.is_paid}
                                >
                                  <SelectTrigger className="w-full min-w-[180px]">
                                    <SelectValue>
                                      {getStatusBadge(invoice.status)}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {/* Show only allowed transitions based on current status */}
                                    {invoice.status === INVOICE_STATUS.PENDING_REVIEW && (
                                      <>
                                        <SelectItem value={INVOICE_STATUS.APPROVED}>
                                          ✅ Approve & Send to Customer
                                        </SelectItem>
                                        <SelectItem value={INVOICE_STATUS.DISPUTE}>
                                          ⚠️ Dispute
                                        </SelectItem>
                                      </>
                                    )}

                                    {invoice.status === INVOICE_STATUS.APPROVED && (
                                      <>
                                        <SelectItem value={INVOICE_STATUS.PENDING_PAYMENT}>
                                          💰 Pending Payment
                                        </SelectItem>
                                        <SelectItem value={INVOICE_STATUS.DISPUTE}>
                                          ⚠️  Dispute
                                        </SelectItem>
                                      </>
                                    )}

                                    {invoice.status === INVOICE_STATUS.PENDING_PAYMENT && (
                                      <>
                                        <SelectItem value={INVOICE_STATUS.CLEARED}>
                                          ✔️ Cleared (Paid)
                                        </SelectItem>
                                        <SelectItem value={INVOICE_STATUS.DISPUTE}>
                                          ⚠️ Dispute
                                        </SelectItem>
                                      </>
                                    )}

                                    {invoice.status === INVOICE_STATUS.CLEARED && (
                                      <SelectItem value={INVOICE_STATUS.DISPUTE}>
                                        ⚠️ Reopen as Disputed
                                      </SelectItem>
                                    )}

                                    {invoice.status === INVOICE_STATUS.DISPUTE && (
                                      <>
                                        <SelectItem value={INVOICE_STATUS.APPROVED}>
                                          ✅ Re-approve after Dispute
                                        </SelectItem>
                                        <SelectItem value={INVOICE_STATUS.CLEARED}>
                                          ✔️ Cleared (Paid)
                                        </SelectItem>
                                      </>
                                    )}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-xs text-gray-500">
                                  {invoice.updated_at ?
                                    new Date(invoice.updated_at).toLocaleDateString() :
                                    new Date(invoice.created_at).toLocaleDateString()
                                  }
                                  {invoice.status === INVOICE_STATUS.PENDING_REVIEW && (
                                    <div className="text-xs text-yellow-600 font-medium mt-1">
                                      ⚠️ Updated, needs review
                                    </div>
                                  )}
                                </div>
                              </td>

                              {/* Actions Cell */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex justify-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      generateInvoice(invoice);
                                    }}
                                    className="flex items-center gap-1"
                                    title="View Invoice"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  {/* Send Email Button */}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const GST_RATE = 0.10;
                                      let credited_amount = invoice.credited_amount || 0;
                                      if (credited_amount) {
                                        // caluclate gst to the credited amount
                                        const gst = credited_amount * GST_RATE;
                                        credited_amount = credited_amount / (1 + GST_RATE);
                                      }
                                      const totalSubtotal = invoice.subtotal || 0;
                                      const totalGST = ((totalSubtotal - (credited_amount || 0)) * GST_RATE);
                                      const totalAUD = ((totalSubtotal + totalGST) - (credited_amount || 0)).toFixed(2);
                                      setSelectedInvoice(invoice);
                                      setEmailForm({
                                        emailType: 'finance',
                                        subject: `Invoice ${invoice.invoice_number} - ${invoice.customer_name}`,
                                        message: `Dear ${invoice.customer_name},\n\nPlease find your invoice ${invoice.invoice_number} attached.\n\nTotal Amount: $${totalAUD}\n\nThank you for your business!`,
                                        includePaymentInfo: true,
                                        html_body: generateInvoice(invoice, true),
                                      });
                                      setShowInvoiceEmailDialog(true);
                                    }}
                                    className="flex items-center gap-1"
                                    title="Send Email"
                                  >
                                    <Mail className="w-4 h-4" />
                                  </Button>

                                  {/* Edit Button */}
                                  {(invoice.status === INVOICE_STATUS.PENDING_REVIEW || invoice.status === INVOICE_STATUS.DISPUTE) && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={async () => {
                                        if (!orders.length) {
                                          // toast.error("Original order details not found. Refreshing orders...");
                                          await fetchOrders();
                                        }

                                        // 1. Find the full order object from the orders state using the order_id in the invoice
                                        const associatedOrder = orders.find(o => o.id === invoice.order_id);

                                        if (associatedOrder) {
                                          // 2. Open the standard Edit Order dialog logic you already have
                                          handleEditOrder(associatedOrder);
                                        } else {
                                          // toast.error("Original order details not found. Refreshing orders...");
                                          await fetchOrders();
                                        }
                                      }}
                                      className="flex items-center gap-1 text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                                      title="Edit Order Details"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {/* {invoice.status === INVOICE_STATUS.PENDING_REVIEW && !isCreditNote && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleUpdateInvoiceStatus(invoice.id, INVOICE_STATUS.APPROVED)}
                                      className="flex items-center gap-1 bg-green-50 text-green-700 hover:bg-green-100"
                                      title="Approve Invoice"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                  )} */}

                                  {/* Clear/Pay Button */}
                                  {/* {(invoice.status === INVOICE_STATUS.APPROVED || invoice.status === INVOICE_STATUS.PENDING_PAYMENT) && !isCreditNote && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleUpdateInvoiceStatus(invoice.id, INVOICE_STATUS.CLEARED)}
                                      className="flex items-center gap-1 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                      title="Mark as Paid/Cleared"
                                    >
                                      <DollarSign className="w-4 h-4" />
                                    </Button>
                                  )} */}

                                  {/* Dispute Button */}
                                  {/* {invoice.status !== INVOICE_STATUS.DISPUTE && invoice.status !== INVOICE_STATUS.CLEARED && !isCreditNote && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedDisputeInvoice(invoice);
                                        setDisputeReason("");
                                        setShowDisputeDialog(true);
                                      }}
                                      className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      title="Mark as Dispute"
                                    >
                                      <AlertCircle className="w-4 h-4" />
                                    </Button>
                                  )} */}

                                  {/* Delete Button - Only for cleared/paid invoices */}
                                  {/* {invoice.is_paid && !isCreditNote && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteInvoice(invoice.id)}
                                      disabled={deletingInvoice === invoice.id}
                                      className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      title="Delete Invoice"
                                    >
                                      {deletingInvoice === invoice.id ? (
                                        <Clock className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="w-4 h-4" />
                                      )}
                                    </Button>
                                  )} */}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            }

            {/* Bulk Action Confirmation Dialog */}
            <Dialog open={showBulkActionDialog} onOpenChange={setShowBulkActionDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm Bulk Action</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p>
                    Are you sure you want to <strong>{bulkAction.replace('_', ' ')}</strong> on {selectedInvoices.length} invoice(s)?
                  </p>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setShowBulkActionDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleBulkAction}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>


            {/* Dispute Dialog */}
            <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-5 h-5" />
                    Mark Invoice as Disputed
                  </DialogTitle>
                </DialogHeader>

                {selectedDisputeInvoice && (
                  <div className="space-y-4">
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Invoice:</span>
                          <span className="font-medium">{selectedDisputeInvoice.invoice_number}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Customer:</span>
                          <span className="font-medium">{selectedDisputeInvoice.customer_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Amount:</span>
                          <span className="font-medium">${(selectedDisputeInvoice.total_amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="dispute-reason" className="text-red-700">
                        Dispute Reason *
                      </Label>
                      <Textarea
                        id="dispute-reason"
                        value={disputeReason}
                        onChange={(e) => setDisputeReason(e.target.value)}
                        placeholder="Enter reason for dispute (e.g., wrong items, incorrect pricing, KA mismatch, etc.)"
                        rows={4}
                        className="mt-1 border-red-300 focus:border-red-500 focus:ring-red-500"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Common reasons: Wrong item, wrong quantity, incorrect pricing, missing items, KA mismatch
                      </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDisputeDialog(false);
                          setSelectedDisputeInvoice(null);
                          setDisputeReason("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSubmitDispute}
                        className="bg-red-600 hover:bg-red-700"
                        disabled={!disputeReason.trim()}
                      >
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Mark as Disputed
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Credit Note Dialog */}
            <Dialog open={showCreditNoteDialog} onOpenChange={(open) => {
              setShowCreditNoteDialog(open);
              if (!open) {
                setEditingCreditNote(null); // Clear editing state on close
                setSelectedInvoiceForCredit(null);

              }
            }}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    {editingCreditNote ? `Edit Credit Note: ${editingCreditNote.credit_note_number}` : "Create Credit Note"}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  {/* If creating, show Invoice Selector. If editing, show read-only info */}
                  {!editingCreditNote ? (
                    <div>
                      <Label>Select Customer</Label>
                      <Select
                        value={creditNoteForm.customer_id}
                        disabled={creditNoteForm.items.length > 0}
                        onValueChange={(val) => {
                          setCreditNoteForm({ ...creditNoteForm, customer_id: val });
                          fetchCustomerSkus(val);
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Select Customer" /></SelectTrigger>
                        <SelectContent>
                          {customers.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg text-sm border">
                      <p><strong>Customer:</strong> {editingCreditNote.customer_name}</p>
                    </div>
                  )}

                  {/* Common Form Fields */}
                  <div>
                    <Label>Credit Reason *</Label>
                    <Select
                      value={creditNoteForm.reason}
                      onValueChange={(value) => setCreditNoteForm({ ...creditNoteForm, reason: value })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="returned_goods">Returned Goods</SelectItem>
                        <SelectItem value="overpayment">Overpayment</SelectItem>
                        <SelectItem value="price_adjustment">Price Adjustment</SelectItem>
                        <SelectItem value="damaged_goods">Damaged Goods</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Credit Items</Label>
                      <Button type="button" size="sm" variant="outline" onClick={addCreditNoteItem}>
                        <Plus className="w-4 h-4 mr-1" /> Add Item
                      </Button>
                    </div>

                    <div className="space-y-2 border rounded-md p-2 max-h-60 overflow-y-auto mb-4">
                      {creditNoteForm.items.map((item, index) => (
                        <div key={index} className="flex gap-2 items-end border-b pb-2 last:border-0">
                          <div className="flex-1">
                            <Label className="text-xs">Item</Label>
                            <Select
                              value={item.sku_id}
                              onValueChange={(val) => updateCreditNoteItem(index, 'sku_id', val)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Select Item" />
                              </SelectTrigger>
                              <SelectContent>
                                {(customerSkus || []).map((sku) => {
                                  const price =
                                    sku.customer_price !== undefined
                                      ? sku.customer_price
                                      : sku.price;
                                  const gst = price * 0.1;
                                  const priceIncGST = price + gst;
                                  return (
                                    <SelectItem key={sku.id} value={sku.id}>
                                      {sku.name} - ${price.toFixed(2)} + GST $
                                      {gst.toFixed(2)} = ${priceIncGST.toFixed(2)}
                                      {sku.customer_price !== undefined &&
                                        " (Custom)"}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-20">
                            <Label className="text-xs">Qty</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateCreditNoteItem(index, 'quantity', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div className="w-24">
                            <Label className="text-xs">Price</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={(item.price * item.quantity)}
                              onChange={(e) => updateCreditNoteItem(index, 'price', e.target.value)}
                              disabled={true}
                              className="h-8"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            onClick={() => removeCreditNoteItem(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span>${(creditNoteForm.subtotal || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>GST (10%):</span>
                        <span>${(creditNoteForm.gst || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Total Credit:</span>
                        <span>${(creditNoteForm.total_with_gst || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>Internal Notes</Label>
                    <Textarea
                      value={creditNoteForm.notes}
                      onChange={(e) => setCreditNoteForm({ ...creditNoteForm, notes: e.target.value })}
                      placeholder="Why are you editing this?"
                    />
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreditNoteDialog(false)}>Cancel</Button>
                    <Button onClick={handleSaveCreditNote} className="bg-teal-600 hover:bg-teal-700">
                      {editingCreditNote ? "Update Changes" : "Create Credit Note"}
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>



            {/* Edit Invoice Dialog */}
            <Dialog open={showEditInvoiceDialog} onOpenChange={setShowEditInvoiceDialog}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Invoice {editingInvoice?.invoice_number}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Customer Name</Label>
                      <Input value={invoiceEditForm.customer_name} disabled />
                    </div>
                    {/* <div>
                      <Label>Delivery Rate ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={invoiceEditForm.delivery_rate}
                        onChange={(e) => {
                          const rate = parseFloat(e.target.value) || 0;
                          const subtotal = invoiceEditForm.items.reduce((acc, item) => acc + (item.price * item.quantity), 0);
                          const gst = (subtotal + rate) * 0.1;
                          const total = subtotal + rate + gst;
                          setInvoiceEditForm({
                            ...invoiceEditForm,
                            delivery_rate: rate,
                            gst_amount: gst,
                            total_amount: total
                          });
                        }}
                      />
                    </div> */}
                  </div>

                  <div>
                    <Label className="mb-2 block">Line Items</Label>
                    <div className="space-y-2 border rounded-md p-2 max-h-60 overflow-y-auto">
                      {invoiceEditForm.items.map((item, index) => (
                        <div key={index} className="flex gap-2 items-end border-b pb-2 last:border-0">
                          <div className="flex-1">
                            <Label className="text-xs">Item</Label>
                            <Input value={item.sku_name} disabled className="h-8" />
                          </div>
                          <div className="w-20">
                            <Label className="text-xs">Qty</Label>
                            <Input
                              type="number"
                              min="0"
                              value={item.quantity}
                              onChange={(e) => {
                                const newItems = [...invoiceEditForm.items];
                                newItems[index].quantity = parseInt(e.target.value) || 0;
                                const subtotal = newItems.reduce((acc, i) => acc + (i.price * i.quantity), 0);
                                const gst = (subtotal + invoiceEditForm.delivery_rate) * 0.1;
                                const total = subtotal + invoiceEditForm.delivery_rate + gst;
                                setInvoiceEditForm({
                                  ...invoiceEditForm,
                                  items: newItems,
                                  subtotal,
                                  gst_amount: gst,
                                  total_amount: total
                                });
                              }}
                              className="h-8"
                            />
                          </div>
                          <div className="w-24">
                            <Label className="text-xs">Price</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.price}
                              onChange={(e) => {
                                const newItems = [...invoiceEditForm.items];
                                newItems[index].price = parseFloat(e.target.value) || 0;
                                const subtotal = newItems.reduce((acc, i) => acc + (i.price * i.quantity), 0);
                                const gst = (subtotal + invoiceEditForm.delivery_rate) * 0.1;
                                const total = subtotal + invoiceEditForm.delivery_rate + gst;
                                setInvoiceEditForm({
                                  ...invoiceEditForm,
                                  items: newItems,
                                  subtotal,
                                  gst_amount: gst,
                                  total_amount: total
                                });
                              }}
                              className="h-8"
                            />
                          </div>
                          <div className="w-24 text-right pb-1">
                            <p className="text-sm font-medium">
                              ${((item.price * item.quantity) || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal (Items):</span>
                      <span>${(invoiceEditForm.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Delivery:</span>
                      <span>${(invoiceEditForm.delivery_rate || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>GST (10%):</span>
                      <span>${(invoiceEditForm.gst_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span>${(invoiceEditForm.total_amount || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowEditInvoiceDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveInvoiceEdit} className="bg-teal-600 hover:bg-teal-700">
                      Save Changes
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>

            {/* Invoice Email Dialog - Updated */}
            <Dialog open={showInvoiceEmailDialog} onOpenChange={setShowInvoiceEmailDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Send Invoice Email
                  </DialogTitle>
                </DialogHeader>

                {selectedInvoice && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Invoice:</span>
                          <span className="font-medium">{selectedInvoice.invoice_number}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Customer:</span>
                          <span className="font-medium">{selectedInvoice.customer_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium">
                            {getCustomerFinanceEmail(selectedInvoice.customer_id)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label>Email Type</Label>
                      <Select
                        value={emailForm.emailType}
                        onValueChange={(value) => setEmailForm({ ...emailForm, emailType: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="operations">Operations Email</SelectItem>
                          <SelectItem value="finance">Finance/Accounts Email</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        Finance email is recommended for invoicing
                      </p>
                    </div>

                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={emailForm.subject}
                        onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label>Message</Label>
                      <Textarea
                        value={emailForm.message}
                        onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                        rows={4}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="include-payment"
                        checked={emailForm.includePaymentInfo}
                        onCheckedChange={(checked) => setEmailForm({
                          ...emailForm,
                          includePaymentInfo: !!checked
                        })}
                      />
                      <Label htmlFor="include-payment">Include Payment Information</Label>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowInvoiceEmailDialog(false);
                          setSelectedInvoice(null);
                        }}
                        disabled={sendingEmail}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSendInvoiceEmail}
                        disabled={sendingEmail}
                        className="bg-teal-500 hover:bg-teal-600"
                      >
                        {sendingEmail ? (
                          <>
                            <Clock className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Email
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Email History Dialog */}
            <Dialog open={showEmailHistoryDialog} onOpenChange={setShowEmailHistoryDialog}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email History - {selectedInvoiceForHistory?.invoice_number}
                  </DialogTitle>
                </DialogHeader>

                {loadingEmailHistory ? (
                  <div className="flex justify-center py-8">
                    <Clock className="w-8 h-8 animate-spin text-teal-500" />
                  </div>
                ) : emailHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No email history found for this invoice</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                    {emailHistory.map((email, index) => (
                      <Card key={index} className="border border-gray-200">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-gray-900">{email.subject}</p>
                                <p className="text-sm text-gray-600">
                                  To: {email.to_email}
                                </p>
                              </div>
                              <span className="text-xs text-gray-500">
                                {new Date(email.sent_at).toLocaleString()}
                              </span>
                            </div>

                            <div className="text-sm text-gray-700 whitespace-pre-line bg-gray-50 p-3 rounded">
                              {email.message}
                            </div>

                            <div className="text-xs text-gray-500">
                              <div className="flex items-center gap-2">
                                <span>Type: {email.email_type}</span>
                                {email.include_payment_info && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded">
                                    Payment Info Included
                                  </span>
                                )}
                              </div>
                              {email.status === 'sent' && (
                                <div className="mt-1 text-green-600 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Sent successfully
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                User Management
              </h2>
              <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={userForm.full_name}
                        onChange={(e) =>
                          setUserForm({
                            ...userForm,
                            full_name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Operations Email</Label>
                      <Input
                        type="email"
                        value={userForm.email}
                        onChange={(e) =>
                          setUserForm({ ...userForm, email: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={userForm.password}
                        onChange={(e) =>
                          setUserForm({ ...userForm, password: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Select
                        value={userForm.role}
                        onValueChange={(value) =>
                          setUserForm({ ...userForm, role: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="owner">Owner</SelectItem>
                          <SelectItem value="driver">Driver</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {userForm.role === 'customer' && (
                      <div>
                        <Label>Finance Team Emaill</Label>
                        <Input
                          type="email"
                          value={userForm.secondary_email}
                          onChange={(e) =>
                            setUserForm({
                              ...userForm,
                              secondary_email: e.target.value,
                            })
                          }
                          placeholder="Finance Team Emaill (optional)"
                        />
                      </div>
                    )}
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={userForm.phone}
                        onChange={(e) =>
                          setUserForm({ ...userForm, phone: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={userForm.address}
                        onChange={(e) =>
                          setUserForm({ ...userForm, address: e.target.value })
                        }
                      />
                    </div>
                    {userForm.role === 'customer' && (
                      <div>
                        <Label>Billing Terms</Label>
                        <Input
                          type="number"
                          value={userForm.billing_terms}
                          onChange={(e) =>
                            setUserForm({
                              ...userForm,
                              billing_terms: e.target.value,
                            })
                          }
                          placeholder="Billing Terms (optional)"
                        />
                      </div>
                    )}
                    <Button
                      type="submit"
                      className="w-full bg-teal-500 hover:bg-teal-600"
                    >
                      Create User
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Filter & Sort Controls */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by name or email..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <Select value={userSortBy} onValueChange={setUserSortBy}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sort by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_name">Name</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="role">Role</SelectItem>
                        <SelectItem value="created_at">Created Date</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      onClick={() =>
                        setUserSortOrder(
                          userSortOrder === "asc" ? "desc" : "asc"
                        )
                      }
                      className="w-full"
                    >
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      {userSortOrder === "asc" ? "↑ Ascending" : "↓ Descending"}
                    </Button>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Filter by Role
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {["owner", "admin", "customer", "driver"].map((role) => (
                        <Button
                          key={role}
                          variant={
                            userRoleFilter.includes(role)
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => toggleUserRoleFilter(role)}
                          className="capitalize"
                        >
                          {role}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t">
                    <p className="text-sm text-gray-600">
                      Showing{" "}
                      <span className="font-semibold">
                        {getFilteredAndSortedUsers().length}
                      </span>{" "}
                      of <span className="font-semibold">{users.length}</span>{" "}
                      users
                    </p>
                    {(userSearchQuery ||
                      userRoleFilter.length > 0 ||
                      userSortBy !== "created_at" ||
                      userSortOrder !== "desc") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearUserFilters}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear Filters
                        </Button>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Phone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getPaginatedUsers().map((user) => (
                        <tr
                          key={user.id}
                          className={`hover:bg-gray-50 ${!user.is_active ? "opacity-50 bg-gray-100" : ""
                            }`}
                        >
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {user.full_name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {user.email}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.role === "owner"
                                ? "bg-purple-100 text-purple-800"
                                : user.role === "admin"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-green-100 text-green-800"
                                }`}
                            >
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${user.is_active !== false
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                                }`}
                            >
                              {user.is_active !== false ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3" /> Active
                                </>
                              ) : (
                                <>
                                  <Ban className="w-3 h-3" /> Disabled
                                </>
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {user.phone || "-"}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center gap-2">
                              {(user.role === "customer" ||
                                user.role === "driver") && (
                                  <button
                                    onClick={() => openUserEditDialog(user)}
                                    className="text-teal-600 hover:text-teal-800"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                )}
                              <button
                                onClick={() => openPasswordResetDialog(user)}
                                className="text-blue-600 hover:text-blue-800"
                                disabled={resettingPasswordUserId === user.id}
                              >
                                {resettingPasswordUserId === user.id ? (
                                  <Clock className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Lock className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleToggleUserStatus(user)}
                                className={`${user.is_active !== false
                                  ? "text-red-600 hover:text-red-800"
                                  : "text-green-600 hover:text-green-800"
                                  } ${togglingUserId === user.id
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                  }`}
                                disabled={togglingUserId === user.id}
                              >
                                {togglingUserId === user.id ? (
                                  <Clock className="w-4 h-4 animate-spin" />
                                ) : user.is_active !== false ? (
                                  <Ban className="w-4 h-4" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Pagination Controls */}
            {getUserTotalPages() > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Showing {(userCurrentPage - 1) * userItemsPerPage + 1} to{" "}
                  {Math.min(
                    userCurrentPage * userItemsPerPage,
                    getFilteredAndSortedUsers().length
                  )}{" "}
                  of {getFilteredAndSortedUsers().length} users
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUserPageChange(userCurrentPage - 1)}
                    disabled={userCurrentPage === 1}
                  >
                    Previous
                  </Button>

                  <div className="flex gap-1">
                    {Array.from(
                      { length: Math.min(5, getUserTotalPages()) },
                      (_, i) => {
                        let pageNum;
                        const totalPages = getUserTotalPages();

                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (userCurrentPage <= 3) {
                          pageNum = i + 1;
                        } else if (userCurrentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = userCurrentPage - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={
                              userCurrentPage === pageNum
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => handleUserPageChange(pageNum)}
                            className={
                              userCurrentPage === pageNum
                                ? "bg-teal-500 hover:bg-teal-600"
                                : ""
                            }
                          >
                            {pageNum}
                          </Button>
                        );
                      }
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUserPageChange(userCurrentPage + 1)}
                    disabled={userCurrentPage === getUserTotalPages()}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {getFilteredAndSortedUsers().length === 0 && users.length > 0 && (
              <Card className="mt-4">
                <CardContent className="p-12 text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    No users found matching your filters
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Password Reset Dialog */}
            <Dialog
              open={showPasswordDialog}
              onOpenChange={setShowPasswordDialog}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset Password</DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div>
                    <Label className="text-gray-700">User</Label>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedUser?.full_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedUser?.email}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      required
                      minLength={6}
                      autoComplete="off"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Password must be at least 6 characters long
                    </p>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> This will immediately change the
                      user's password. The user will need to use this new
                      password to log in.
                    </p>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowPasswordDialog(false)}
                      disabled={resettingPasswordUserId}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={resettingPasswordUserId}
                    >
                      {resettingPasswordUserId ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* User Edit Dialog */}
            <Dialog
              open={showUserEditDialog}
              onOpenChange={setShowUserEditDialog}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit User Details</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveUserEdit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={userEditForm.full_name}
                        onChange={(e) =>
                          setUserEditForm({
                            ...userEditForm,
                            full_name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Operations Email</Label>
                      <Input
                        type="email"
                        value={userEditForm.email}
                        onChange={(e) =>
                          setUserEditForm({
                            ...userEditForm,
                            email: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={userEditForm.phone}
                        onChange={(e) =>
                          setUserEditForm({
                            ...userEditForm,
                            phone: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={userEditForm.address}
                        onChange={(e) =>
                          setUserEditForm({
                            ...userEditForm,
                            address: e.target.value,
                          })
                        }
                      />
                    </div>
                    {userEditForm.role === 'customer' && (
                      <div>
                        <Label>Billing Terms</Label>
                        <Input
                          type="number"
                          value={userEditForm.billing_terms}
                          onChange={(e) =>
                            setUserEditForm({
                              ...userEditForm,
                              billing_terms: e.target.value,
                            })
                          }
                          placeholder="Billing Terms (optional)"
                        />

                      </div>
                    )}
                    <div>
                      <Label>Role</Label>
                      <Select
                        value={userEditForm.role}
                        onValueChange={(v) =>
                          setUserEditForm({ ...userEditForm, role: v })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="driver">Driver</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {userEditForm.role === 'customer' && (
                      <div>
                        <Label>Finance Team Emaill</Label>
                        <Input
                          type="email"
                          value={userEditForm.secondary_email}
                          onChange={(e) =>
                            setUserEditForm({
                              ...userEditForm,
                              secondary_email: e.target.value,
                            })
                          }
                          placeholder="Finance Team Emaill (optional)"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowUserEditDialog(false)}
                      disabled={savingUserEdit}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-teal-600 hover:bg-teal-700"
                      disabled={savingUserEdit}
                    >
                      {savingUserEdit ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* SKUs Tab */}
        {activeTab === "skus" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                SKU & Pricing Management
              </h2>
              <Dialog
                open={showSkuDialog}
                onOpenChange={(open) => {
                  setShowSkuDialog(open);
                  if (!open) {
                    setEditingSku(null);
                    setSkuForm({
                      name: "",
                      category: "",
                      price: "",
                      unit: "",
                      description: "",
                    });
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Add SKU
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingSku ? "Edit SKU" : "Create New SKU"}
                    </DialogTitle>
                  </DialogHeader>
                  <form
                    onSubmit={handleCreateOrUpdateSku}
                    className="space-y-4"
                  >
                    <div>
                      <Label>Item Name</Label>
                      <Input
                        value={skuForm.name}
                        onChange={(e) =>
                          setSkuForm({ ...skuForm, name: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Input
                        value={skuForm.category}
                        onChange={(e) =>
                          setSkuForm({ ...skuForm, category: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={skuForm.price}
                        onChange={(e) =>
                          setSkuForm({ ...skuForm, price: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Input
                        placeholder="e.g., per item, per kg"
                        value={skuForm.unit}
                        onChange={(e) =>
                          setSkuForm({ ...skuForm, unit: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={skuForm.description}
                        onChange={(e) =>
                          setSkuForm({
                            ...skuForm,
                            description: e.target.value,
                          })
                        }
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-teal-500 hover:bg-teal-600"
                    >
                      {editingSku ? "Update SKU" : "Create SKU"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Filter & Sort Controls */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by name or description..."
                        value={skuSearchQuery}
                        onChange={(e) => setSkuSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <Select value={skuSortBy} onValueChange={setSkuSortBy}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sort by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="price">Price</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      onClick={() =>
                        setSkuSortOrder(skuSortOrder === "asc" ? "desc" : "asc")
                      }
                      className="w-full"
                    >
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      {skuSortOrder === "asc" ? "↑ Ascending" : "↓ Descending"}
                    </Button>
                  </div>

                  {getUniqueCategories().length > 0 && (
                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Filter by Category
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {getUniqueCategories().map((category) => (
                          <Button
                            key={category}
                            variant={
                              skuCategoryFilter.includes(category)
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => toggleSkuCategoryFilter(category)}
                          >
                            {category}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-2 border-t">
                    <p className="text-sm text-gray-600">
                      Showing{" "}
                      <span className="font-semibold">
                        {getFilteredAndSortedSkus().length}
                      </span>{" "}
                      of <span className="font-semibold">{skus.length}</span>{" "}
                      SKUs
                    </p>
                    {(skuSearchQuery ||
                      skuCategoryFilter.length > 0 ||
                      skuSortBy !== "name" ||
                      skuSortOrder !== "asc") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearSkuFilters}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear Filters
                        </Button>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getFilteredAndSortedSkus().map((sku) => (
                <Card key={sku.id} className="card-hover">
                  <CardHeader>
                    <CardTitle className="flex justify-between items-start">
                      <span>{sku.name}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditSku(sku)}
                          className="text-teal-600 hover:text-teal-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSku(sku.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        Category: {sku.category}
                      </p>
                      <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Base Price:</span>
                            <span className="font-medium text-gray-900">
                              ${sku.price.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">GST (10%):</span>
                            <span className="font-medium text-gray-900">
                              ${(sku.price * 0.1).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t">
                            <span className="text-sm font-semibold text-gray-900">
                              Total (Inc. GST):
                            </span>
                            <span className="text-2xl font-bold text-teal-600">
                              ${(sku.price * 1.1).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">{sku.unit}</p>
                      {sku.description && (
                        <p className="text-sm text-gray-600 mt-2">
                          {sku.description}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {getFilteredAndSortedSkus().length === 0 && skus.length > 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    No SKUs found matching your filters
                  </p>
                </CardContent>
              </Card>
            )}

            {skus.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No SKUs found</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Customer Pricing Tab */}
        {activeTab === "customer-pricing" && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Customer-Specific Pricing
              </h2>
              <div className="mb-4">
                <Label>Select Customer</Label>
                <Select
                  value={selectedCustomer || ""}
                  onValueChange={setSelectedCustomer}
                >
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Choose a customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.full_name} ({customer.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedCustomer && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Custom Pricing for Selected Customer
                  </h3>
                  <Dialog
                    open={showPricingDialog}
                    onOpenChange={setShowPricingDialog}
                  >
                    <DialogTrigger asChild>
                      <Button className="bg-teal-500 hover:bg-teal-600">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Custom Price
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Set Custom Price for Customer</DialogTitle>
                      </DialogHeader>
                      <form
                        onSubmit={handleCreateOrUpdatePricing}
                        className="space-y-4"
                      >
                        <div>
                          <Label>Select SKU</Label>
                          <Select
                            value={pricingForm.sku_id}
                            onValueChange={(value) =>
                              setPricingForm({ ...pricingForm, sku_id: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choose a SKU" />
                            </SelectTrigger>
                            <SelectContent>
                              {skus.map((sku) => (
                                <SelectItem key={sku.id} value={sku.id}>
                                  {sku.name} (Base: ${sku.price})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Custom Price ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={pricingForm.custom_price}
                            onChange={(e) =>
                              setPricingForm({
                                ...pricingForm,
                                custom_price: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full bg-teal-500 hover:bg-teal-600"
                        >
                          Set Custom Price
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {skusWithPricing
                    .filter((sku) => sku.has_custom_pricing)
                    .map((sku) => (
                      <Card key={sku.id} className="card-hover">
                        <CardHeader>
                          <CardTitle className="flex justify-between items-start">
                            <span>{sku.name}</span>
                            {sku.has_custom_pricing && (
                              <button
                                onClick={() => {
                                  const pricing = customerPricing.find(
                                    (p) => p.sku_id === sku.id
                                  );
                                  if (pricing) handleDeletePricing(pricing.id);
                                }}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600">
                              Category: {sku.category}
                            </p>
                            {sku.has_custom_pricing ? (
                              <>
                                <div className="flex items-center gap-2 mb-2">
                                  <p className="text-sm text-gray-400 line-through">
                                    Base: ${(sku.price * 1.1).toFixed(2)}
                                  </p>
                                  <Tag className="w-4 h-4 text-teal-600" />
                                  <span className="text-xs font-semibold text-teal-600">
                                    CUSTOM PRICING
                                  </span>
                                </div>
                                <div className="p-3 bg-teal-50 rounded border border-teal-200 space-y-2">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">
                                      Custom Price:
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      ${sku.customer_price.toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">
                                      GST (10%):
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      ${(sku.customer_price * 0.1).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center pt-2 border-t border-teal-300">
                                    <span className="text-sm font-semibold text-gray-900">
                                      Total (Inc. GST):
                                    </span>
                                    <span className="text-2xl font-bold text-teal-600">
                                      ${(sku.customer_price * 1.1).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="p-3 bg-gray-50 rounded border border-gray-200 space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-gray-600">
                                    Base Price:
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    ${sku.price.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-gray-600">
                                    GST (10%):
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    ${(sku.price * 0.1).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t">
                                  <span className="text-sm font-semibold text-gray-900">
                                    Total (Inc. GST):
                                  </span>
                                  <span className="text-2xl font-bold text-gray-900">
                                    ${(sku.price * 1.1).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            )}
                            <p className="text-sm text-gray-500">{sku.unit}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>

                {selectedCustomer &&
                  skusWithPricing.filter((sku) => sku.has_custom_pricing)
                    .length === 0 && (
                    <Card className="mt-6">
                      <CardContent className="p-12 text-center">
                        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2">
                          No custom pricing set for this customer
                        </p>
                        <p className="text-sm text-gray-500">
                          Click "Set Custom Price" above to add custom pricing
                          for this customer
                        </p>
                      </CardContent>
                    </Card>
                  )}
              </div>
            )}
          </div>
        )}

        {/* Frequency Templates Tab */}
        {activeTab === "frequency-templates" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Frequency Templates
              </h2>
              <Dialog
                open={showTemplateDialog}
                onOpenChange={(open) => {
                  setShowTemplateDialog(open);
                  if (!open) {
                    setEditingTemplate(null);
                    setTemplateForm({
                      name: "",
                      frequency_type: "daily",
                      frequency_value: "1",
                      description: "",
                    });
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingTemplate
                        ? "Edit Template"
                        : "Create Frequency Template"}
                    </DialogTitle>
                  </DialogHeader>
                  <form
                    onSubmit={handleCreateOrUpdateTemplate}
                    className="space-y-4"
                  >
                    <div>
                      <Label>Template Name</Label>
                      <Input
                        value={templateForm.name}
                        onChange={(e) =>
                          setTemplateForm({
                            ...templateForm,
                            name: e.target.value,
                          })
                        }
                        placeholder="e.g., Weekly Pickup"
                        required
                      />
                    </div>
                    <div>
                      <Label>Frequency Type</Label>
                      <Select
                        value={templateForm.frequency_type}
                        onValueChange={(value) =>
                          setTemplateForm({
                            ...templateForm,
                            frequency_type: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Frequency Value</Label>
                      <Input
                        type="number"
                        min="1"
                        value={templateForm.frequency_value}
                        onChange={(e) =>
                          setTemplateForm({
                            ...templateForm,
                            frequency_value: e.target.value,
                          })
                        }
                        placeholder="e.g., 1 for every day, 2 for every 2 days"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {templateForm.frequency_type === "daily" &&
                          `Every ${templateForm.frequency_value} day(s)`}
                        {templateForm.frequency_type === "weekly" &&
                          `Every ${templateForm.frequency_value} week(s)`}
                        {templateForm.frequency_type === "monthly" &&
                          `Every ${templateForm.frequency_value} month(s)`}
                      </p>
                    </div>
                    <div>
                      <Label>Description (Optional)</Label>
                      <Input
                        value={templateForm.description}
                        onChange={(e) =>
                          setTemplateForm({
                            ...templateForm,
                            description: e.target.value,
                          })
                        }
                        placeholder="Additional details"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-teal-500 hover:bg-teal-600"
                    >
                      {editingTemplate ? "Update Template" : "Create Template"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {frequencyTemplates.map((template) => (
                <Card key={template.id} className="card-hover">
                  <CardHeader>
                    <CardTitle className="flex justify-between items-start">
                      <span>{template.name}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="text-teal-600 hover:text-teal-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-teal-600" />
                        <p className="text-lg font-semibold text-gray-900">
                          Every {template.frequency_value}{" "}
                          {template.frequency_type === "daily"
                            ? "Day(s)"
                            : template.frequency_type === "weekly"
                              ? "Week(s)"
                              : "Month(s)"}
                        </p>
                      </div>
                      <p className="text-sm text-gray-600 capitalize">
                        Type: {template.frequency_type}
                      </p>
                      {template.description && (
                        <p className="text-sm text-gray-500 mt-2">
                          {template.description}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Case Management Tab */}
        {activeTab === "cases" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Case Management
              </h2>
              <Button
                onClick={() => setShowRejectLinenDialog(true)}
                className="bg-red-500 hover:bg-red-600 text-white flex items-center gap-2"
              >
                <AlertCircle className="w-4 h-4" />
                Raise Reject Linen Case
              </Button>
            </div>

            {/* ─── Reject Linen Dialog ─── */}
            <Dialog open={showRejectLinenDialog} onOpenChange={setShowRejectLinenDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-5 h-5" />
                    Raise Reject Linen Case
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitRejectLinenCase} className="space-y-4">
                  {/* Customer selector */}
                  <div>
                    <Label>Customer *</Label>
                    <Select
                      value={rejectLinenForm.customer_id}
                      onValueChange={(val) => {
                        const cust = customers.find(c => c.id === val);
                        setRejectLinenForm({
                          ...rejectLinenForm,
                          customer_id: val,
                          customer_name: cust?.full_name || "",
                          customer_email: cust?.email || "",
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.filter(c => c.role === "customer").map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rejected quantity */}
                  <div>
                    <Label>Number of Rejected Items *</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="e.g. 8"
                      value={rejectLinenForm.reject_linen_qty}
                      onChange={(e) => setRejectLinenForm({ ...rejectLinenForm, reject_linen_qty: e.target.value })}
                      required
                    />
                  </div>

                  {/* Associated Order (optional) */}
                  <div>
                    <Label>Associated Order (optional)</Label>
                    <Select
                      value={rejectLinenForm.order_id || "none"}
                      onValueChange={(val) => setRejectLinenForm({ ...rejectLinenForm, order_id: val === "none" ? "" : val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Link to an order" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No specific order —</SelectItem>
                        {orders
                          .filter(o => !rejectLinenForm.customer_id || o.customer_id === rejectLinenForm.customer_id)
                          .map(o => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.order_number} — {o.delivery_date?.slice(0, 10) || "no date"}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Priority */}
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={rejectLinenForm.priority}
                      onValueChange={(val) => setRejectLinenForm({ ...rejectLinenForm, priority: val })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div>
                    <Label>Description / Notes</Label>
                    <Textarea
                      rows={3}
                      placeholder="e.g. 8 towels rejected due to heavy staining..."
                      value={rejectLinenForm.description}
                      onChange={(e) => setRejectLinenForm({ ...rejectLinenForm, description: e.target.value })}
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2 border-t">
                    <Button type="button" variant="outline" onClick={() => setShowRejectLinenDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-red-500 hover:bg-red-600" disabled={submittingRejectLinen}>
                      {submittingRejectLinen ? "Submitting..." : "Raise Case"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Search and Filter Controls */}
            <Card className="mb-6">

              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        Search Cases
                      </Label>
                      <Input
                        placeholder="Search by case number, customer, or subject..."
                        value={caseSearchQuery}
                        onChange={(e) => setCaseSearchQuery(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Sort By
                      </Label>
                      <div className="flex gap-2">
                        <Select
                          value={caseSortBy}
                          onValueChange={setCaseSortBy}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="created_at">
                              Created Date
                            </SelectItem>
                            <SelectItem value="case_number">
                              Case Number
                            </SelectItem>
                            <SelectItem value="customer_name">
                              Customer
                            </SelectItem>
                            <SelectItem value="status">Status</SelectItem>
                            <SelectItem value="priority">Priority</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCaseSortOrder(
                              caseSortOrder === "asc" ? "desc" : "asc"
                            )
                          }
                          className="px-3"
                        >
                          {caseSortOrder === "asc" ? "↑" : "↓"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filter by Status
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {["open", "in_progress", "resolved", "closed"].map(
                        (status) => (
                          <Button
                            key={status}
                            variant={
                              caseStatusFilter.includes(status)
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => toggleCaseStatusFilter(status)}
                            className={
                              caseStatusFilter.includes(status)
                                ? "bg-blue-500 hover:bg-blue-600"
                                : ""
                            }
                          >
                            {status.replace("_", " ")}
                          </Button>
                        )
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2">
                      Filter by Priority
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {["low", "medium", "high", "urgent"].map((priority) => (
                        <Button
                          key={priority}
                          variant={
                            casePriorityFilter.includes(priority)
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => toggleCasePriorityFilter(priority)}
                          className={
                            casePriorityFilter.includes(priority)
                              ? "bg-orange-500 hover:bg-orange-600"
                              : ""
                          }
                        >
                          {priority}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        From Date
                      </Label>
                      <Input
                        type="date"
                        value={caseDateFrom}
                        onChange={(e) => setCaseDateFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        To Date
                      </Label>
                      <Input
                        type="date"
                        value={caseDateTo}
                        onChange={(e) => setCaseDateTo(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t">
                    <p className="text-sm text-gray-600">
                      Showing {getFilteredAndSortedCases().length} of{" "}
                      {cases.length} cases
                    </p>
                    {(caseSearchQuery ||
                      caseStatusFilter.length > 0 ||
                      casePriorityFilter.length > 0 ||
                      caseDateFrom ||
                      caseDateTo ||
                      caseSortBy !== "created_at" ||
                      caseSortOrder !== "desc") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearCaseFilters}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Clear Filters
                        </Button>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4">
              {getFilteredAndSortedCases().map((caseItem) => (
                <Card key={caseItem.id} className="card-hover">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">
                            {caseItem.case_number}
                          </h3>
                          <span
                            className={`px-3 py-1 text-xs font-semibold rounded-full ${caseItem.status === "open"
                              ? "bg-yellow-100 text-yellow-800"
                              : caseItem.status === "in_progress"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                              }`}
                          >
                            {caseItem.status}
                          </span>
                          <span
                            className={`px-3 py-1 text-xs font-semibold rounded-full ${caseItem.priority === "high"
                              ? "bg-red-100 text-red-800"
                              : caseItem.priority === "medium"
                                ? "bg-orange-100 text-orange-800"
                                : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {caseItem.priority}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">
                          {caseItem.customer_name} - {caseItem.customer_email}
                        </p>
                        <p className={`font-semibold flex items-center gap-2 ${caseItem.subject === "Reject Linen" ? "text-red-600" : "text-gray-700"}`}>
                          {caseItem.subject === "Reject Linen" && <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                          {caseItem.subject}
                          {caseItem.subject === "Reject Linen" && caseItem.reject_linen_qty && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-medium">
                              {caseItem.reject_linen_qty} items rejected
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                          {caseItem.description}
                        </p>
                        {caseItem.resolution && (
                          <div className="mt-3 p-3 bg-green-50 rounded-lg">
                            <p className="text-sm font-semibold text-green-800">
                              Resolution:
                            </p>
                            <p className="text-sm text-green-700">
                              {caseItem.resolution}
                            </p>
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={() => {
                          setSelectedCase(caseItem);
                          setCaseUpdate({
                            status: caseItem.status,
                            resolution: caseItem.resolution || "",
                            priority: caseItem.priority,
                          });
                          setShowCaseDialog(true);
                        }}
                        className="bg-teal-500 hover:bg-teal-600"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Update
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {getFilteredAndSortedCases().length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {cases.length === 0
                        ? "No cases found"
                        : "No cases match your filters"}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Case Update Dialog */}
            <Dialog open={showCaseDialog} onOpenChange={setShowCaseDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Update Case</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateCase} className="space-y-4">
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={caseUpdate.status}
                      onValueChange={(value) =>
                        setCaseUpdate({ ...caseUpdate, status: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={caseUpdate.priority}
                      onValueChange={(value) =>
                        setCaseUpdate({ ...caseUpdate, priority: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Resolution</Label>
                    <Textarea
                      value={caseUpdate.resolution}
                      onChange={(e) =>
                        setCaseUpdate({
                          ...caseUpdate,
                          resolution: e.target.value,
                        })
                      }
                      rows={4}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-teal-500 hover:bg-teal-600"
                    disabled={updatingCaseId !== null}
                  >
                    {updatingCaseId ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Case"
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Orders & Driver Assignment
                </h2>
                <p className="text-gray-600 mt-1">
                  Create orders and assign drivers for delivery
                </p>
              </div>
              <Dialog
                open={showOrderDialog}
                onOpenChange={(open) => {
                  setShowOrderDialog(open);
                  if (!open) {
                    setEditingOrder(null);
                    setOrderForm({
                      customer_id: "",
                      customer_name: "",
                      customer_email: "",
                      items: [],
                      delivery_date: "",
                      pickup_address: PERMANENT_PICKUP_ADDRESS,
                      delivery_address: "",
                      special_instructions: "",
                      is_recurring: false,
                      frequency_template_id: "",
                    });
                    setOrderItems([{ sku_id: "", quantity: 1 }]);
                    setCustomerSkus([]);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingOrder ? "Edit Order" : "Create New Order"}
                    </DialogTitle>
                  </DialogHeader>
                  <form
                    onSubmit={
                      editingOrder ? handleUpdateOrder : handleCreateOrder
                    }
                    className="space-y-4"
                  >
                    <div>
                      <Label>Select Customer</Label>
                      <Select
                        value={orderForm.customer_id}
                        onValueChange={(value) => {
                          const selectedCustomer = customers.find(
                            (c) => c.id === value
                          );
                          setOrderForm({
                            ...orderForm,
                            customer_id: value,
                            pickup_address: businessPickupAddress,
                            delivery_address: selectedCustomer?.address || "",
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {customer.full_name} - {customer.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Order Items</Label>
                      {orderItems.map((item, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <Select
                            value={item.sku_id}
                            onValueChange={(value) =>
                              updateOrderItem(index, "sku_id", value)
                            }
                            disabled={!orderForm.customer_id}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue
                                placeholder={
                                  orderForm.customer_id
                                    ? customerSkus.length > 0
                                      ? "Select item"
                                      : "No custom pricing set for this customer"
                                    : "Select customer first"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {customerSkus.map((sku) => {
                                const price =
                                  sku.customer_price !== undefined
                                    ? sku.customer_price
                                    : sku.price;
                                const gst = price * 0.1;
                                const priceIncGST = price + gst;
                                return (
                                  <SelectItem key={sku.id} value={sku.id}>
                                    {sku.name} - ${price.toFixed(2)} + GST $
                                    {gst.toFixed(2)} = ${priceIncGST.toFixed(2)}
                                    {sku.customer_price !== undefined &&
                                      " (Custom)"}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              updateOrderItem(index, "quantity", e.target.value)
                            }
                            placeholder="Qty"
                            className="w-20"
                          />
                          {orderItems.length > 1 && (
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => removeOrderItem(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addOrderItem}
                        className="mt-2"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Item
                      </Button>
                    </div>

                    <div>
                      <Label>Delivery Date</Label>
                      <Input
                        type="date"
                        value={orderForm.delivery_date}
                        onChange={(e) =>
                          setOrderForm({
                            ...orderForm,
                            delivery_date: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div>
                      <Label>Pickup Address</Label>
                      <Input
                        value={orderForm.pickup_address}
                        disabled
                        className="bg-gray-100 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        📍 Permanent business pickup address
                      </p>
                    </div>

                    <div>
                      <Label>Delivery Address</Label>
                      <Input
                        value={orderForm.delivery_address}
                        onChange={(e) =>
                          setOrderForm({
                            ...orderForm,
                            delivery_address: e.target.value,
                          })
                        }
                        placeholder="Enter delivery address"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        🏠 Customer's saved address (auto-filled when customer
                        selected, can be changed)
                      </p>
                    </div>

                    <div>
                      <Label>Special Instructions (Optional)</Label>
                      <Input
                        value={orderForm.special_instructions}
                        onChange={(e) =>
                          setOrderForm({
                            ...orderForm,
                            special_instructions: e.target.value,
                          })
                        }
                        placeholder="Any special requirements"
                      />
                    </div>

                    {/* <div>
                      <Label>Delivery Rate ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={orderForm.delivery_rate || ""}
                        onChange={(e) =>
                          setOrderForm({
                            ...orderForm,
                            delivery_rate: e.target.value,
                          })
                        }
                        placeholder="0.00"
                      />
                    </div> */}

                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center gap-3 mb-4">
                        <Switch
                          checked={orderForm.is_recurring}
                          onCheckedChange={(checked) =>
                            setOrderForm({
                              ...orderForm,
                              is_recurring: checked,
                            })
                          }
                        />
                        <Label className="flex items-center gap-2 cursor-pointer">
                          <Repeat className="w-4 h-4 text-teal-600" />
                          Make this a recurring order
                        </Label>
                      </div>

                      {orderForm.is_recurring && (
                        <div>
                          <Label>Select Frequency Template</Label>
                          <Select
                            value={orderForm.frequency_template_id}
                            onValueChange={(value) =>
                              setOrderForm({
                                ...orderForm,
                                frequency_template_id: value,
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choose recurring frequency" />
                            </SelectTrigger>
                            <SelectContent>
                              {frequencyTemplates.map((template) => (
                                <SelectItem
                                  key={template.id}
                                  value={template.id}
                                >
                                  {template.name} - Every{" "}
                                  {template.frequency_value}{" "}
                                  {template.frequency_type}
                                </SelectItem>
                              ))}
                              {frequencyTemplates.length === 0 && (
                                <div className="p-2 text-sm text-gray-500 text-center">
                                  No templates available. Create one in
                                  Frequency Templates tab.
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/*  auto-invoiceotal with GST Breakdown */}
                    {orderItems.some(
                      (item) => item.sku_id && item.quantity
                    ) && (
                        <div className="border-t pt-4 mt-4 bg-gray-50 p-4 rounded-lg">
                          <h3 className="font-semibold text-gray-900 mb-3">
                            Order Summary
                          </h3>
                          <div className="space-y-2">
                            {orderItems.map((item, index) => {
                              const sku = customerSkus.find(
                                (s) => s.id === item.sku_id
                              );
                              if (!sku || !item.quantity) return null;
                              const price =
                                sku.customer_price !== undefined
                                  ? sku.customer_price
                                  : sku.price;
                              const lineTotal =
                                price * parseInt(item.quantity || 0);
                              return (
                                <div
                                  key={index}
                                  className="flex justify-between text-sm"
                                >
                                  <span className="text-gray-600">
                                    {sku.name} x {item.quantity}
                                  </span>
                                  <span className="text-gray-900 font-medium">
                                    ${lineTotal.toFixed(2)}
                                  </span>
                                </div>
                              );
                            })}

                            {(() => {
                              const subtotalExGST = orderItems.reduce(
                                (total, item) => {
                                  const sku = customerSkus.find(
                                    (s) => s.id === item.sku_id
                                  );
                                  if (!sku || !item.quantity) return total;
                                  const price =
                                    sku.customer_price !== undefined
                                      ? sku.customer_price
                                      : sku.price;
                                  return (
                                    total + price * parseInt(item.quantity || 0)
                                  );
                                },
                                0
                              );
                              const deliveryRate = parseFloat(orderForm.delivery_rate || 0);
                              const combinedTotal = subtotalExGST + deliveryRate;
                              const gstAmount = combinedTotal * 0.1;
                              const totalIncGST = combinedTotal + gstAmount;

                              return (
                                <div className="border-t pt-3 mt-3 space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Base Amount:
                                    </span>
                                    <span className="text-gray-900 font-medium">
                                      ${subtotalExGST.toFixed(2)}
                                    </span>
                                  </div>
                                  {/* <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Delivery Rate:
                                    </span>
                                    <span className="text-gray-900 font-medium">
                                      ${deliveryRate.toFixed(2)}
                                    </span>
                                  </div> */}
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      GST (10%):
                                    </span>
                                    <span className="text-gray-900 font-medium">
                                      ${gstAmount.toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center pt-2 border-t">
                                    <span className="text-lg font-bold text-gray-900">
                                      Total Amount
                                    </span>
                                    <span className="text-2xl font-bold text-teal-600">
                                      ${totalIncGST.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                    {editingOrder && (
                      <Button
                        type="button"
                        variant="secondary"
                        className="w-full mb-3"
                        onClick={() => generateDeliveryReceipt(editingOrder)}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Download Delivery Receipt
                      </Button>
                    )}

                    <Button
                      type="submit"
                      className="w-full bg-teal-500 hover:bg-teal-600"
                      disabled={creatingOrder}
                    >
                      {creatingOrder ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          {editingOrder ? "Updating..." : "Creating..."}
                        </>
                      ) : editingOrder ? (
                        "Update Order"
                      ) : (
                        "Create Order"
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>All Orders</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Filter & Sort Controls */}
                <div className="mb-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search by order #, customer name, or email..."
                        value={orderSearchQuery}
                        onChange={(e) => setOrderSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    {(orderSearchQuery ||
                      orderStatusFilter.length > 0 ||
                      orderDeliveryStatusFilter.length > 0 ||
                      orderDateFilter !== "all" ||
                      orderDateFrom ||
                      orderDateTo ||
                      orderTypeFilter !== "all") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearAllOrderFilters}
                          className="whitespace-nowrap"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear Filters
                        </Button>
                      )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium whitespace-nowrap">
                        Sort by:
                      </Label>
                      <Select
                        value={orderSortBy}
                        onValueChange={setOrderSortBy}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="created_at">Order Date</SelectItem>
                          <SelectItem value="delivery_date">
                            Delivery Date
                          </SelectItem>
                          <SelectItem value="order_number">
                            Order Number
                          </SelectItem>
                          <SelectItem value="customer_name">
                            Customer Name
                          </SelectItem>
                          <SelectItem value="status">Status</SelectItem>
                          <SelectItem value="total_amount">
                            Total Amount
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setOrderSortOrder(
                            orderSortOrder === "asc" ? "desc" : "asc"
                          )
                        }
                        title={
                          orderSortOrder === "asc" ? "Ascending" : "Descending"
                        }
                      >
                        <ArrowUpDown className="w-4 h-4" />
                        {orderSortOrder === "asc" ? "↑" : "↓"}
                      </Button>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <Select
                        value={orderDateFilter}
                        onValueChange={setOrderDateFilter}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Dates</SelectItem>
                          <SelectItem value="today">Today</SelectItem>
                          <SelectItem value="week">This Week</SelectItem>
                          <SelectItem value="month">This Month</SelectItem>
                          <SelectItem value="custom">Custom Range</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {orderDateFilter === "custom" && (
                      <div className="flex items-center gap-2">
                        <Label className="text-sm whitespace-nowrap">
                          From:
                        </Label>
                        <Input
                          type="date"
                          value={orderDateFrom}
                          onChange={(e) => setOrderDateFrom(e.target.value)}
                          className="w-[150px]"
                        />
                        <Label className="text-sm whitespace-nowrap">To:</Label>
                        <Input
                          type="date"
                          value={orderDateTo}
                          onChange={(e) => setOrderDateTo(e.target.value)}
                          className="w-[150px]"
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-gray-500" />
                      <Select
                        value={orderTypeFilter}
                        onValueChange={setOrderTypeFilter}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="regular">Regular Only</SelectItem>
                          <SelectItem value="recurring">
                            Recurring Only
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <Label className="text-sm font-medium">Status:</Label>
                    {[
                      "pending",
                      "scheduled",
                      "processing",
                      "ready_for_pickup",
                      "out_for_delivery",
                      "delivered",
                      "cancelled",
                    ].map((status) => (
                      <button
                        key={status}
                        onClick={() => toggleStatusFilter(status)}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${orderStatusFilter.includes(status)
                          ? "bg-teal-500 text-white border-teal-500"
                          : "bg-white text-gray-700 border-gray-300 hover:border-teal-500"
                          }`}
                      >
                        {getStatusDisplayName(status)}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Truck className="w-4 h-4 text-gray-500" />
                    <Label className="text-sm font-medium">
                      Delivery Status:
                    </Label>
                    {[
                      "assigned",
                      "picked_up",
                      "out_for_delivery",
                      "delivered",
                    ].map((deliveryStatus) => (
                      <button
                        key={deliveryStatus}
                        onClick={() =>
                          toggleDeliveryStatusFilter(deliveryStatus)
                        }
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${orderDeliveryStatusFilter.includes(deliveryStatus)
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white text-gray-700 border-gray-300 hover:border-blue-500"
                          }`}
                      >
                        {deliveryStatus.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>

                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold">
                      {getFilteredAndSortedOrders().length}
                    </span>{" "}
                    of <span className="font-semibold">{orders.length}</span>{" "}
                    orders
                  </div>
                </div>

                {getFilteredAndSortedOrders().length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>
                      {orderSearchQuery ||
                        orderStatusFilter.length > 0 ||
                        orderDeliveryStatusFilter.length > 0 ||
                        orderDateFilter !== "all" ||
                        orderDateFrom ||
                        orderDateTo ||
                        orderTypeFilter !== "all"
                        ? "No orders match your filters"
                        : "No orders available"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Order #
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Customer
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Delivery Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Delivery Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Assigned Driver
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Assign Driver
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {getPaginatedOrders().map((order) => (
                          <tr key={order.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {order.order_number}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {order.customer_name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3">
                              {order.is_recurring ? (
                                <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 flex items-center gap-1 w-fit">
                                  <Repeat className="w-3 h-3" />
                                  Recurring
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">
                                  Regular
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Select
                                value={order.status}
                                onValueChange={(value) =>
                                  handleUpdateOrderStatus(order.id, value)
                                }
                                disabled={updatingStatusOrderId === order.id}
                              >
                                <SelectTrigger className="w-full min-w-[140px]">
                                  {updatingStatusOrderId === order.id ? (
                                    <span className="flex items-center text-xs">
                                      <Clock className="w-3 h-3 mr-2 animate-spin" />
                                      Updating...
                                    </span>
                                  ) : (
                                    <SelectValue placeholder="Select Status">
                                      {order.status === "pending" ? "Pending" :
                                        order.status === "confirmed" ? "Confirmed" :
                                          order.status === "scheduled" ? "Scheduled" :
                                            order.status === "processing" ? "Processing" :
                                              order.status === "ready" ? "Ready for Pickup" :
                                                order.status === "ready_for_pickup" ? "Ready for Pickup" :
                                                  order.status === "out-for-delivery" ? "Out for Delivery" :
                                                    order.status === "out_for_delivery" ? "Out for Delivery" :
                                                      order.status === "delivered" ? "Delivered" :
                                                        order.status === "cancelled" ? "Cancelled" :
                                                          order.status || "Select Status"}
                                    </SelectValue>
                                  )}
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">
                                    Pending
                                  </SelectItem>
                                  <SelectItem value="confirmed">
                                    Confirmed
                                  </SelectItem>
                                  <SelectItem value="processing">
                                    Processing
                                  </SelectItem>
                                  <SelectItem value="ready">
                                    Ready for Pickup
                                  </SelectItem>
                                  <SelectItem value="out-for-delivery">
                                    Out for Delivery
                                  </SelectItem>
                                  <SelectItem value="delivered">
                                    Delivered
                                  </SelectItem>
                                  <SelectItem value="cancelled">
                                    Cancelled
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-4 py-3">
                              {order.delivery_status ? (
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${order.delivery_status === "delivered"
                                    ? "bg-green-100 text-green-800"
                                    : order.delivery_status ===
                                      "out_for_delivery"
                                      ? "bg-orange-100 text-orange-800"
                                      : order.delivery_status === "picked_up"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-blue-100 text-blue-800"
                                    }`}
                                >
                                  {order.delivery_status.replace(/_/g, " ")}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">
                                  Not assigned
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {order.driver_name || (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {order.driver_id ? (
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    ✓ Already Assigned
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleUnassignDriver(order.id)
                                    }
                                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    title="Unassign driver to reassign"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Select
                                    value={selectedDriver[order.id] || ""}
                                    onValueChange={(value) =>
                                      setSelectedDriver({
                                        ...selectedDriver,
                                        [order.id]: value,
                                      })
                                    }
                                  >
                                    <SelectTrigger className="w-40">
                                      <SelectValue placeholder="Select driver" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {drivers.map((driver) => (
                                        <SelectItem
                                          key={driver.id}
                                          value={driver.id}
                                        >
                                          {driver.full_name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    size="sm"
                                    onClick={() => handleAssignDriver(order.id)}
                                    className="bg-teal-500 hover:bg-teal-600"
                                  >
                                    Assign
                                  </Button>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditOrder(order)}
                                  disabled={creatingOrder}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  title="Edit order (Owner can always edit)"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>


                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteOrder(order.id)}
                                  disabled={deletingOrderId === order.id}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  {deletingOrderId === order.id ? (
                                    <span className="animate-spin">⏳</span>
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                                {order.is_locked ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUnlockOrder(order.id)}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    title="Unlock order to allow customer edits"
                                  >
                                    <Unlock className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleLockOrder(order.id)}
                                    className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                    title="Lock order to prevent customer edits"
                                  >
                                    <Lock className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {getTotalPages() > 1 && (
                      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                          {Math.min(
                            currentPage * itemsPerPage,
                            getFilteredAndSortedOrders().length
                          )}{" "}
                          of {getFilteredAndSortedOrders().length} orders
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                          <div className="flex gap-1">
                            {[...Array(getTotalPages())].map((_, idx) => {
                              const pageNum = idx + 1;
                              if (
                                pageNum === 1 ||
                                pageNum === getTotalPages() ||
                                (pageNum >= currentPage - 1 &&
                                  pageNum <= currentPage + 1)
                              ) {
                                return (
                                  <Button
                                    key={pageNum}
                                    variant={
                                      currentPage === pageNum
                                        ? "default"
                                        : "outline"
                                    }
                                    size="sm"
                                    onClick={() => handlePageChange(pageNum)}
                                    className={
                                      currentPage === pageNum
                                        ? "bg-teal-500 hover:bg-teal-600"
                                        : ""
                                    }
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              } else if (
                                pageNum === currentPage - 2 ||
                                pageNum === currentPage + 2
                              ) {
                                return (
                                  <span key={pageNum} className="px-2 py-1">
                                    ...
                                  </span>
                                );
                              }
                              return null;
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === getTotalPages()}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Driver List */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Available Drivers</CardTitle>
              </CardHeader>
              <CardContent>
                {drivers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>
                      No drivers available. Create driver accounts in User
                      Management.
                    </p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {drivers.map((driver) => (
                      <Card key={driver.id} className="border border-gray-200">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-teal-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {driver.full_name}
                              </p>
                              <p className="text-sm text-gray-600">
                                {driver.email}
                              </p>
                              {driver.phone && (
                                <p className="text-xs text-gray-500">
                                  {driver.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delivery Tracking Tab */}
        {activeTab === "delivery-tracking" && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Delivery Tracking
              </h2>
              <p className="text-gray-600 mt-1">
                Track delivery progress and view detailed timeline
              </p>
            </div>

            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search by driver name..."
                        value={deliveryTrackingDriverFilter}
                        onChange={(e) =>
                          setDeliveryTrackingDriverFilter(e.target.value)
                        }
                        className="pl-10"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">From:</Label>
                      <Input
                        type="date"
                        value={deliveryTrackingDateFrom}
                        onChange={(e) =>
                          setDeliveryTrackingDateFrom(e.target.value)
                        }
                        className="w-[150px]"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">To:</Label>
                      <Input
                        type="date"
                        value={deliveryTrackingDateTo}
                        onChange={(e) =>
                          setDeliveryTrackingDateTo(e.target.value)
                        }
                        className="w-[150px]"
                      />
                    </div>

                    {(deliveryTrackingStatusFilter.length > 0 ||
                      deliveryTrackingDriverFilter ||
                      deliveryTrackingDateFrom ||
                      deliveryTrackingDateTo) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearDeliveryTrackingFilters}
                          className="whitespace-nowrap"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear
                        </Button>
                      )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Truck className="w-4 h-4 text-gray-500" />
                    <Label className="text-sm font-medium">
                      Delivery Status:
                    </Label>
                    {[
                      "assigned",
                      "picked_up",
                      "out_for_delivery",
                      "delivered",
                    ].map((status) => (
                      <button
                        key={status}
                        onClick={() =>
                          toggleDeliveryTrackingStatusFilter(status)
                        }
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${deliveryTrackingStatusFilter.includes(status)
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white text-gray-700 border-gray-300 hover:border-blue-500"
                          }`}
                      >
                        {status.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>

                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold">
                      {getFilteredDeliveryOrders().length}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">
                      {orders.filter((o) => o.driver_id).length}
                    </span>{" "}
                    deliveries
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Orders List */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Orders with Delivery</CardTitle>
                </CardHeader>
                <CardContent className="max-h-[600px] overflow-y-auto">
                  {getFilteredDeliveryOrders().length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Truck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>
                        {orders.filter((o) => o.driver_id).length === 0
                          ? "No orders with assigned drivers"
                          : "No deliveries match your filters"}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {getFilteredDeliveryOrders().map((order) => (
                        <div
                          key={order.id}
                          onClick={() => setSelectedOrderForTracking(order)}
                          className={`p-3 border rounded-lg cursor-pointer transition-all ${selectedOrderForTracking?.id === order.id
                            ? "border-teal-500 bg-teal-50"
                            : "border-gray-200 hover:border-teal-300 hover:bg-gray-50"
                            }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-sm">
                                {order.order_number}
                              </p>
                              <p className="text-xs text-gray-600">
                                {order.customer_name}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${order.delivery_status === "delivered"
                                ? "bg-green-100 text-green-800"
                                : order.delivery_status === "out_for_delivery"
                                  ? "bg-orange-100 text-orange-800"
                                  : order.delivery_status === "picked_up"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                            >
                              {order.delivery_status?.replace(/_/g, " ") ||
                                "assigned"}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Driver: {order.driver_name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Delivery Details */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Delivery Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedOrderForTracking ? (
                    <div className="text-center py-16 text-gray-500">
                      <Package className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p>Select an order to view delivery details</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Order Info */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-600">
                              Order Number
                            </p>
                            <p className="font-semibold">
                              {selectedOrderForTracking.order_number}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Customer</p>
                            <p className="font-semibold">
                              {selectedOrderForTracking.customer_name}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Driver</p>
                            <p className="font-semibold">
                              {selectedOrderForTracking.driver_name}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">
                              Current Status
                            </p>
                            <span
                              className={`inline-block px-2 py-1 text-xs rounded-full ${selectedOrderForTracking.delivery_status ===
                                "delivered"
                                ? "bg-green-100 text-green-800"
                                : selectedOrderForTracking.delivery_status ===
                                  "out_for_delivery"
                                  ? "bg-orange-100 text-orange-800"
                                  : selectedOrderForTracking.delivery_status ===
                                    "picked_up"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                            >
                              {selectedOrderForTracking.delivery_status
                                ?.replace(/_/g, " ")
                                .toUpperCase() || "ASSIGNED"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Addresses */}
                      <div className="grid grid-cols-1 gap-4">
                        <div className="border border-gray-200 p-4 rounded-lg">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-5 h-5 text-green-600 mt-1" />
                            <div>
                              <p className="text-xs text-gray-600 mb-1">
                                Delivery Address
                              </p>
                              <p className="text-sm font-medium">
                                {selectedOrderForTracking.delivery_address}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Date:{" "}
                                {new Date(
                                  selectedOrderForTracking.delivery_date
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-4">
                          Delivery Progress
                        </h3>
                        <div className="space-y-4">
                          {/* Assigned */}
                          <div className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedOrderForTracking.assigned_at
                                  ? "bg-teal-500 text-white"
                                  : "bg-gray-200 text-gray-400"
                                  }`}
                              >
                                <Clock className="w-5 h-5" />
                              </div>
                              {selectedOrderForTracking.assigned_at && (
                                <div className="w-0.5 h-16 bg-teal-500 mt-2"></div>
                              )}
                            </div>
                            <div className="flex-1 pt-2">
                              <p className="font-semibold text-gray-900">
                                Assigned to Driver
                              </p>
                              {selectedOrderForTracking.assigned_at ? (
                                <p className="text-sm text-gray-600">
                                  {new Date(
                                    selectedOrderForTracking.assigned_at
                                  ).toLocaleString()}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-400">
                                  Not assigned yet
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Picked Up */}
                          <div className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedOrderForTracking.picked_up_at
                                  ? "bg-yellow-500 text-white"
                                  : "bg-gray-200 text-gray-400"
                                  }`}
                              >
                                <Package className="w-5 h-5" />
                              </div>
                              {selectedOrderForTracking.picked_up_at && (
                                <div className="w-0.5 h-16 bg-yellow-500 mt-2"></div>
                              )}
                            </div>
                            <div className="flex-1 pt-2">
                              <p className="font-semibold text-gray-900">
                                Picked Up
                              </p>
                              {selectedOrderForTracking.picked_up_at ? (
                                <p className="text-sm text-gray-600">
                                  {new Date(
                                    selectedOrderForTracking.picked_up_at
                                  ).toLocaleString()}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-400">
                                  Not picked up yet
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Out for Delivery */}
                          <div className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedOrderForTracking.delivery_status ===
                                  "out_for_delivery" ||
                                  selectedOrderForTracking.delivery_status ===
                                  "delivered"
                                  ? "bg-orange-500 text-white"
                                  : "bg-gray-200 text-gray-400"
                                  }`}
                              >
                                <Truck className="w-5 h-5" />
                              </div>
                              {(selectedOrderForTracking.delivery_status ===
                                "out_for_delivery" ||
                                selectedOrderForTracking.delivery_status ===
                                "delivered") && (
                                  <div className="w-0.5 h-16 bg-orange-500 mt-2"></div>
                                )}
                            </div>
                            <div className="flex-1 pt-2">
                              <p className="font-semibold text-gray-900">
                                Out for Delivery
                              </p>
                              <p className="text-sm text-gray-600">
                                {selectedOrderForTracking.delivery_status ===
                                  "out_for_delivery" ||
                                  selectedOrderForTracking.delivery_status ===
                                  "delivered"
                                  ? "In transit to delivery address"
                                  : "Waiting for dispatch"}
                              </p>
                            </div>
                          </div>

                          {/* Delivered */}
                          <div className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedOrderForTracking.delivered_at
                                  ? "bg-green-500 text-white"
                                  : "bg-gray-200 text-gray-400"
                                  }`}
                              >
                                <CheckCircle className="w-5 h-5" />
                              </div>
                            </div>
                            <div className="flex-1 pt-2">
                              <p className="font-semibold text-gray-900">
                                Delivered
                              </p>
                              {selectedOrderForTracking.delivered_at ? (
                                <>
                                  <p className="text-sm text-gray-600">
                                    {new Date(
                                      selectedOrderForTracking.delivered_at
                                    ).toLocaleString()}
                                  </p>
                                  {selectedOrderForTracking.delivery_notes && (
                                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                                      <p className="text-xs text-gray-600">
                                        Delivery Notes:
                                      </p>
                                      <p className="text-gray-800">
                                        {
                                          selectedOrderForTracking.delivery_notes
                                        }
                                      </p>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-gray-400">
                                  Not delivered yet
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Edit Request Review Dialog */}
        <Dialog
          open={showEditRequestDialog}
          onOpenChange={setShowEditRequestDialog}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review Customer Edit Request</DialogTitle>
            </DialogHeader>

            {selectedEditRequest && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Order Information
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Order Number:</span>
                      <span className="font-medium text-gray-900 ml-2">
                        {selectedEditRequest.order_number}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Customer:</span>
                      <span className="font-medium text-gray-900 ml-2">
                        {selectedEditRequest.customer_name}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-gray-900 ml-2">
                        {selectedEditRequest.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Request Date:</span>
                      <span className="font-medium text-gray-900 ml-2">
                        {new Date(
                          selectedEditRequest.modification_requested_at
                        ).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedEditRequest.pending_modifications?.reason && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-2">
                      Customer's Reason:
                    </h3>
                    <p className="text-blue-800">
                      {selectedEditRequest.pending_modifications.reason}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <h3 className="font-semibold text-red-900 mb-3">
                      Current Values
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-red-700 font-medium">Items:</span>
                        <div className="mt-1 space-y-1">
                          {selectedEditRequest.items.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between text-red-900 bg-red-100 px-2 py-1 rounded"
                            >
                              <span>{item.sku_name}</span>
                              <span className="font-semibold">
                                ×{item.quantity}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-red-700 font-medium">
                          Delivery Date:
                        </span>
                        <p className="text-red-900">
                          {new Date(
                            selectedEditRequest.delivery_date
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-red-700 font-medium">
                          Delivery Address:
                        </span>
                        <p className="text-red-900">
                          {selectedEditRequest.delivery_address}
                        </p>
                      </div>
                      <div>
                        <span className="text-red-700 font-medium">
                          Order Type:
                        </span>
                        <p className="text-red-900">
                          {selectedEditRequest.is_recurring ? "Recurring" : "One Time"}
                        </p>
                      </div>
                      {selectedEditRequest.special_instructions && (
                        <div>
                          <span className="text-red-700 font-medium">
                            Instructions:
                          </span>
                          <p className="text-red-900">
                            {selectedEditRequest.special_instructions}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-green-900 mb-3">
                      Requested Changes
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-green-700 font-medium">
                          Items:
                        </span>
                        {selectedEditRequest.pending_modifications?.items ? (
                          <div className="mt-1 space-y-1">
                            {selectedEditRequest.pending_modifications.items.map(
                              (item, idx) => (
                                <div
                                  key={idx}
                                  className="flex justify-between text-green-900 bg-green-100 px-2 py-1 rounded"
                                >
                                  <span>{item.sku_name}</span>
                                  <span className="font-semibold">
                                    ×{item.quantity}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="text-green-900 mt-1">No change</p>
                        )}
                      </div>
                      <div>
                        <span className="text-green-700 font-medium">
                          Delivery Date:
                        </span>
                        <p className="text-green-900">
                          {selectedEditRequest.pending_modifications
                            ?.delivery_date
                            ? new Date(
                              selectedEditRequest.pending_modifications.delivery_date
                            ).toLocaleString()
                            : "No change"}
                        </p>
                      </div>
                      <div>
                        <span className="text-green-700 font-medium">
                          Delivery Address:
                        </span>
                        <p className="text-green-900">
                          {selectedEditRequest.pending_modifications
                            ?.delivery_address || "No change"}
                        </p>
                      </div>
                      <div>
                        <span className="text-green-700 font-medium">
                          Order Type:
                        </span>
                        <p className="text-green-900">
                          {selectedEditRequest.pending_modifications
                            ?.is_recurring ? "Recurring" : "One Time"}
                        </p>
                      </div>
                      {selectedEditRequest.pending_modifications
                        ?.special_instructions && (
                          <div>
                            <span className="text-green-700 font-medium">
                              Instructions:
                            </span>
                            <p className="text-green-900">
                              {
                                selectedEditRequest.pending_modifications
                                  .special_instructions
                              }
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold mb-3 block">
                    Select Action:
                  </Label>
                  <div className="flex gap-4 mb-4">
                    <Button
                      variant={
                        editRequestAction === "approve" ? "default" : "outline"
                      }
                      onClick={() => setEditRequestAction("approve")}
                      className={
                        editRequestAction === "approve"
                          ? "bg-green-500 hover:bg-green-600"
                          : ""
                      }
                    >
                      ✓ Approve As-Is
                    </Button>
                    <Button
                      variant={
                        editRequestAction === "modify" ? "default" : "outline"
                      }
                      onClick={() => setEditRequestAction("modify")}
                      className={
                        editRequestAction === "modify"
                          ? "bg-blue-500 hover:bg-blue-600"
                          : ""
                      }
                    >
                      ✏️ Modify & Approve
                    </Button>
                    <Button
                      variant={
                        editRequestAction === "reject" ? "default" : "outline"
                      }
                      onClick={() => setEditRequestAction("reject")}
                      className={
                        editRequestAction === "reject"
                          ? "bg-red-500 hover:bg-red-600"
                          : ""
                      }
                    >
                      ✗ Reject
                    </Button>
                  </div>

                  {editRequestAction === "modify" && (
                    <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                      <Label>Modify Requested Changes:</Label>
                      <div>
                        <Label className="text-sm">Delivery Date</Label>
                        <Input
                          type="datetime-local"
                          value={
                            editRequestForm.delivery_date?.slice(0, 16) || ""
                          }
                          onChange={(e) =>
                            setEditRequestForm({
                              ...editRequestForm,
                              delivery_date: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Delivery Address</Label>
                        <Input
                          value={editRequestForm.delivery_address || ""}
                          onChange={(e) =>
                            setEditRequestForm({
                              ...editRequestForm,
                              delivery_address: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Special Instructions</Label>
                        <Textarea
                          value={editRequestForm.special_instructions || ""}
                          onChange={(e) =>
                            setEditRequestForm({
                              ...editRequestForm,
                              special_instructions: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}

                  {editRequestAction === "reject" && (
                    <div className="p-4 bg-red-50 rounded-lg">
                      <Label className="text-sm">
                        Rejection Reason (Required)
                      </Label>
                      <Textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Explain why this request is being rejected..."
                        required
                      />
                    </div>
                  )}

                  <div className="mt-4">
                    <Label className="text-sm">Owner Notes (Optional)</Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Any additional notes for the customer..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditRequestDialog(false)}
                    disabled={processingEditRequest}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProcessEditRequest}
                    disabled={processingEditRequest}
                    className={
                      editRequestAction === "approve"
                        ? "bg-green-500 hover:bg-green-600"
                        : editRequestAction === "modify"
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-red-500 hover:bg-red-600"
                    }
                  >
                    {processingEditRequest ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        {editRequestAction === "approve" && "✓ Approve Request"}
                        {editRequestAction === "modify" &&
                          "✏️ Modify & Approve"}
                        {editRequestAction === "reject" && "✗ Reject Request"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout >
  );
}

export default OwnerDashboard;